import React, { useCallback, useEffect, useMemo, useState } from "react";
import TikTokAnalyticsSuite from "../components/TikTokAnalyticsSuite";
import { useTheme } from "../context/ThemeContext";
import ScoutCenterPage from "./ScoutCenterPage";
import {
  Card,
  SectionHeader,
  ActionButton,
  RowActionButton,
  SearchBox,
  ApiNotice,
  EmptyState,
  FormInput,
  FormSelect,
  ModalShell,
  ModalActions,
  StatCard,
  PasswordModal,
  PublisherListContent,
  PublisherTrackingContent,
  PublisherReportsManagementContent,
  PublisherEyeExperienceContent,
} from "../components/AdminSharedComponents";

function useMobileOnlyResponsiveLayout(scopeClass, enabled = true) {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return undefined;

    const bodyClass = `${scopeClass}-active`;
    const styleId = `${scopeClass}-style`;
    document.body.classList.add(bodyClass);

    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      const scope = `body.${bodyClass}`;

      style.textContent = `
        @media (max-width: 1023px) {
          ${scope},
          ${scope} #root {
            width: 100%;
            max-width: 100%;
            overflow-x: hidden;
          }

          ${scope} main {
            width: 100%;
            min-width: 0;
            max-width: 100vw;
          }

          ${scope} table {
            display: block;
            width: 100%;
            max-width: 100%;
            overflow-x: auto;
            overscroll-behavior-inline: contain;
            -webkit-overflow-scrolling: touch;
          }

          ${scope} iframe,
          ${scope} video,
          ${scope} canvas {
            max-width: 100%;
          }

          ${scope} [class*="fixed"][class*="inset-0"] {
            overflow-y: auto;
            overscroll-behavior: contain;
          }
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      document.body.classList.remove(bodyClass);
      const existing = document.getElementById(styleId);
      if (existing) {
        existing.remove();
      }
    };
  }, [enabled, scopeClass]);
}

const ADMIN_THEME_STYLES = `
  .wm-admin-theme {
    --admin-gold: #d6a800;
    --admin-gold-dark: #9b7600;
    --admin-gold-soft: #fff8df;
    --admin-page: #f4f6f8;
    --admin-surface: #ffffff;
    --admin-surface-soft: #f8fafc;
    --admin-text: #182230;
    --admin-text-soft: #475467;
    --admin-muted: #667085;
    --admin-border: #d9dee7;
  }

  /* Tema anahtarı */
  .wm-admin-theme-switch {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px;
    border: 1px solid rgba(255,255,255,.10);
    border-radius: 12px;
    background: rgba(255,255,255,.035);
  }

  .wm-admin-theme-button {
    min-height: 32px;
    padding: 0 11px;
    border: 1px solid transparent;
    border-radius: 9px;
    font-size: 11px;
    font-weight: 700;
    transition: background-color .15s ease, color .15s ease, border-color .15s ease;
  }

  .wm-admin-dark .wm-admin-theme-button {
    color: rgba(255,255,255,.55);
  }

  .wm-admin-dark .wm-admin-theme-button.is-active {
    background: #eab308;
    border-color: rgba(250,204,21,.55);
    color: #080808;
  }

  /* ================================================================
     AÇIK TEMA
     Mevcut JSX, API, state ve işlem fonksiyonlarına dokunmaz.
     ================================================================ */
  .wm-admin-light {
    background: var(--admin-page) !important;
    color: var(--admin-text) !important;
    color-scheme: light;
  }

  /* Koyu temanın dekoratif sabit arka planlarını açık modda gizle.
     z-50 modal overlay'leri etkilenmez. */
  .wm-admin-light > .fixed.inset-0:not(.z-50) {
    display: none !important;
  }

  .wm-admin-light main {
    background: var(--admin-page) !important;
    color: var(--admin-text) !important;
  }

  .wm-admin-light aside {
    background: #ffffff !important;
    border-color: var(--admin-border) !important;
    box-shadow: 1px 0 0 rgba(16,24,40,.03);
  }

  /* Ana yüzeyler */
  .wm-admin-light [class*="bg-white/"] {
    background-color: var(--admin-surface) !important;
  }

  .wm-admin-light [class*="bg-black/"]:not([class*="fixed"]) {
    background-color: var(--admin-surface-soft) !important;
  }

  .wm-admin-light [class*="bg-[#070707]"],
  .wm-admin-light [class*="bg-[#080808]"],
  .wm-admin-light [class*="bg-[#090909]"],
  .wm-admin-light [class*="bg-[#0a0a0a]"],
  .wm-admin-light [class*="bg-[#0b0b0b]"],
  .wm-admin-light [class*="bg-[#101010]"] {
    background-color: #ffffff !important;
  }

  /* Modal overlay koyu kalır, modal kartı açık olur */
  .wm-admin-light [class*="fixed"][class*="inset-0"][class*="bg-black"] {
    background-color: rgba(15,23,42,.48) !important;
  }

  .wm-admin-light [class*="fixed"][class*="inset-0"] > [class*="bg-[#0b0b0b]"] {
    background-color: #ffffff !important;
  }

  /* Yazılar */
  .wm-admin-light [class~="text-white"] {
    color: var(--admin-text) !important;
  }

  .wm-admin-light [class*="text-white/"] {
    color: var(--admin-muted) !important;
  }

  .wm-admin-light [class*="text-yellow-"] {
    color: var(--admin-gold-dark) !important;
  }

  .wm-admin-light [class*="fill-white"] {
    fill: #667085 !important;
  }

  .wm-admin-light [class*="fill-yellow"] {
    fill: #9b7600 !important;
  }

  /* Border'lar */
  .wm-admin-light [class*="border-white"] {
    border-color: var(--admin-border) !important;
  }

  .wm-admin-light [class*="border-yellow-"] {
    border-color: rgba(183,137,0,.34) !important;
  }

  /* Gold soft alanlar */
  .wm-admin-light [class*="bg-yellow-500/"],
  .wm-admin-light [class*="bg-yellow-400/"] {
    background-color: var(--admin-gold-soft) !important;
  }

  /* Ana gold butonlar orijinal karakterini korur */
  .wm-admin-light [class~="bg-yellow-500"] {
    background-color: #eab308 !important;
    color: #111111 !important;
    border-color: #d6a800 !important;
  }

  .wm-admin-light [class~="hover:bg-yellow-500"]:hover,
  .wm-admin-light [class~="hover:bg-yellow-400"]:hover {
    background-color: #facc15 !important;
    color: #111111 !important;
  }

  /* Semantik renkler açık zeminde okunaklı */
  .wm-admin-light [class*="text-green-"],
  .wm-admin-light [class*="text-emerald-"] {
    color: #067647 !important;
  }

  .wm-admin-light [class*="bg-green-"],
  .wm-admin-light [class*="bg-emerald-"] {
    background-color: #ecfdf3 !important;
  }

  .wm-admin-light [class*="border-green-"],
  .wm-admin-light [class*="border-emerald-"] {
    border-color: #abefc6 !important;
  }

  .wm-admin-light [class*="text-red-"] {
    color: #b42318 !important;
  }

  .wm-admin-light [class*="bg-red-"] {
    background-color: #fef3f2 !important;
  }

  .wm-admin-light [class*="border-red-"] {
    border-color: #fecdca !important;
  }

  .wm-admin-light [class*="text-fuchsia-"] {
    color: #8b3fa4 !important;
  }

  .wm-admin-light [class*="bg-fuchsia-"] {
    background-color: #fdf4ff !important;
  }

  .wm-admin-light [class*="border-fuchsia-"] {
    border-color: #f0abfc !important;
  }

  .wm-admin-light [class*="text-cyan-"] {
    color: #0e7490 !important;
  }

  .wm-admin-light [class*="bg-cyan-"] {
    background-color: #ecfeff !important;
  }

  .wm-admin-light [class*="border-cyan-"] {
    border-color: #a5f3fc !important;
  }

  /* Form alanları */
  .wm-admin-light input,
  .wm-admin-light select,
  .wm-admin-light textarea,
  .wm-admin-light .form-input,
  .wm-admin-light .form-textarea {
    background: #ffffff !important;
    color: var(--admin-text) !important;
    border-color: #cfd6df !important;
    color-scheme: light !important;
  }

  .wm-admin-light input::placeholder,
  .wm-admin-light textarea::placeholder {
    color: #98a2b3 !important;
  }

  .wm-admin-light input:focus,
  .wm-admin-light select:focus,
  .wm-admin-light textarea:focus,
  .wm-admin-light .form-input:focus,
  .wm-admin-light .form-textarea:focus {
    border-color: #c99a12 !important;
    box-shadow: 0 0 0 3px rgba(201,154,18,.10) !important;
  }

  .wm-admin-light option {
    background: #ffffff !important;
    color: #182230 !important;
  }

  /* Kart ve tablo hiyerarşisi */
  .wm-admin-light [class*="shadow-[0_18px"],
  .wm-admin-light [class*="shadow-[0_20px"] {
    box-shadow:
      0 1px 2px rgba(16,24,40,.03),
      0 8px 24px rgba(16,24,40,.055) !important;
  }

  .wm-admin-light [class*="uppercase"][class*="bg-white/"] {
    background-color: #f7f8fa !important;
  }

  .wm-admin-light [class*="hover:bg-white"]:hover {
    background-color: #f8fafc !important;
  }

  .wm-admin-light [class*="hover:bg-yellow"]:hover {
    background-color: #fff9e8 !important;
  }

  /* Accordion / aktif menü */
  .wm-admin-light aside button {
    color: #344054;
  }

  .wm-admin-light aside button:hover {
    background-color: #f8fafc !important;
  }

  .wm-admin-light aside [class*="bg-yellow-500/"] {
    background-color: #fff8df !important;
    color: #8a6800 !important;
    border-color: rgba(183,137,0,.32) !important;
  }

  /* Grafik ve koyu özel yüzeyleri açık moda taşı */
  .wm-admin-light [class*="bg-[radial-gradient"] {
    background:
      radial-gradient(circle at top right, rgba(234,179,8,.10), transparent 30%),
      linear-gradient(180deg,#ffffff 0%,#f4f6f8 100%) !important;
  }

  .wm-admin-light svg > rect[fill="rgba(0,0,0,0.18)"] {
    fill: rgba(255,255,255,.72) !important;
  }

  /* Tema anahtarı açık mod */
  .wm-admin-light .wm-admin-theme-switch {
    background: #eef1f5;
    border-color: #d9dee7;
  }

  .wm-admin-light .wm-admin-theme-button {
    color: #667085;
  }

  .wm-admin-light .wm-admin-theme-button.is-active {
    background: #ffffff;
    border-color: #d7b64a;
    color: #9b7600;
    box-shadow: 0 1px 3px rgba(16,24,40,.08);
  }

  /* Scrollbar */
  .wm-admin-light * {
    scrollbar-color: rgba(183,137,0,.50) #eef1f4;
  }

  /* ================================================================
     AÇIK TEMA V3
     Sadece görsel katman:
     - Yazılar daha net ve koyu
     - Kart/kutu yüzeyleri yumuşak geçişli gri
     - Mevcut işlev, API, state ve JSX akışı değiştirilmez
     ================================================================ */

  .wm-admin-light {
    --admin-page: #eef2f6;
    --admin-surface: #f6f8fa;
    --admin-surface-soft: #e9eef3;
    --admin-text: #101828;
    --admin-text-soft: #1d2939;
    --admin-muted: #475467;
    --admin-border: #cbd5df;
  }

  /* Genel zemin */
  .wm-admin-light,
  .wm-admin-light main {
    background:
      linear-gradient(
        180deg,
        #f4f6f8 0%,
        #eef2f5 45%,
        #e8edf2 100%
      ) !important;
  }

  /* Başlıklar ve ana metinler daha net */
  .wm-admin-light h1,
  .wm-admin-light h2,
  .wm-admin-light h3,
  .wm-admin-light h4,
  .wm-admin-light [class~="text-white"] {
    color: #101828 !important;
  }

  .wm-admin-light [class*="text-white/82"],
  .wm-admin-light [class*="text-white/80"],
  .wm-admin-light [class*="text-white/78"],
  .wm-admin-light [class*="text-white/68"],
  .wm-admin-light [class*="text-white/65"] {
    color: #1d2939 !important;
  }

  .wm-admin-light [class*="text-white/55"],
  .wm-admin-light [class*="text-white/48"],
  .wm-admin-light [class*="text-white/46"],
  .wm-admin-light [class*="text-white/45"],
  .wm-admin-light [class*="text-white/40"],
  .wm-admin-light [class*="text-white/35"],
  .wm-admin-light [class*="text-white/30"],
  .wm-admin-light [class*="text-white/25"] {
    color: #475467 !important;
  }

  /* Ana kartlar ve çerçeveli kutular:
     küçük badge/buton/uyarı renklerini bozmamak için yalnızca div yüzeyleri */
  .wm-admin-light div[class*="rounded-"][class*="border"]
    :not(button) {
  }

  .wm-admin-light div[class*="rounded-"][class*="border"]
    {
    background:
      linear-gradient(
        180deg,
        #f7f9fb 0%,
        #edf1f5 48%,
        #e3e8ee 100%
      ) !important;
    border-color: #cbd4de !important;
    box-shadow:
      0 1px 2px rgba(16,24,40,.035),
      0 8px 24px rgba(16,24,40,.06) !important;
  }

  /* Semantik / durum kutuları kendi renklerini korusun */
  .wm-admin-light div[class*="rounded-"][class*="border"][class*="bg-red-"] {
    background: #fef3f2 !important;
    border-color: #fecdca !important;
  }

  .wm-admin-light div[class*="rounded-"][class*="border"][class*="bg-green-"],
  .wm-admin-light div[class*="rounded-"][class*="border"][class*="bg-emerald-"] {
    background: #ecfdf3 !important;
    border-color: #abefc6 !important;
  }

  .wm-admin-light div[class*="rounded-"][class*="border"][class*="bg-yellow-"] {
    background:
      linear-gradient(
        180deg,
        #fff9e9 0%,
        #f7edc8 100%
      ) !important;
    border-color: rgba(183,137,0,.32) !important;
  }

  /* Header ve büyük ana paneller biraz daha belirgin */
  .wm-admin-light header[class*="rounded-"],
  .wm-admin-light main > div > div[class*="rounded-"][class*="border"] {
    background:
      linear-gradient(
        180deg,
        #f8fafc 0%,
        #edf1f5 50%,
        #e1e7ed 100%
      ) !important;
    border-color: #c7d0da !important;
  }

  /* Stat kartlar */
  .wm-admin-light .grid > div[class*="rounded-"][class*="border"] {
    background:
      linear-gradient(
        180deg,
        #f7f9fb 0%,
        #edf1f5 52%,
        #e2e7ed 100%
      ) !important;
  }

  /* Form alanları daha okunaklı */
  .wm-admin-light input,
  .wm-admin-light select,
  .wm-admin-light textarea,
  .wm-admin-light .form-input,
  .wm-admin-light .form-textarea {
    background:
      linear-gradient(
        180deg,
        #ffffff 0%,
        #f4f7fa 100%
      ) !important;
    color: #101828 !important;
    border-color: #c7d0da !important;
  }

  .wm-admin-light input::placeholder,
  .wm-admin-light textarea::placeholder {
    color: #7d8999 !important;
  }

  /* Tablolar */
  .wm-admin-light thead {
    background:
      linear-gradient(
        180deg,
        #f5f7fa 0%,
        #e9eef3 100%
      ) !important;
  }

  .wm-admin-light tbody tr {
    background: rgba(247,249,251,.78) !important;
  }

  .wm-admin-light tbody tr:nth-child(even) {
    background: rgba(238,242,246,.92) !important;
  }

  .wm-admin-light tbody tr:hover {
    background: rgba(255,248,222,.70) !important;
  }

  /* Performans / grafik gibi radial-gradient kullanan büyük kutular */
  .wm-admin-light [class*="bg-[radial-gradient"] {
    background:
      radial-gradient(
        circle at top right,
        rgba(234,179,8,.10),
        transparent 26%
      ),
      linear-gradient(
        180deg,
        #edf1f5 0%,
        #e2e7ed 52%,
        #d8dee5 100%
      ) !important;
    border-color: #c5ced8 !important;
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,.72),
      0 10px 28px rgba(16,24,40,.07) !important;
  }

  /* Grafik iç yüzeyi */
  .wm-admin-light svg > rect[fill="rgba(0,0,0,0.18)"] {
    fill: rgba(255,255,255,.78) !important;
  }

  .wm-admin-light svg [class*="fill-yellow-"] {
    fill: #8f6a00 !important;
  }

  /* Sol menü metinleri net */
  .wm-admin-light aside button,
  .wm-admin-light aside span {
    color: #253247;
  }

  /* Gold vurgular açık temada koyu ve belirgin */
  .wm-admin-light [class*="text-yellow-"] {
    color: #8f6a00 !important;
  }

  /* Butonlar arka plan gradyanlarından etkilenmesin */
  .wm-admin-light button[class*="bg-yellow-500"] {
    background: #eab308 !important;
    color: #101010 !important;
    border-color: #d6a800 !important;
    box-shadow: none !important;
  }

  .wm-admin-light button[class*="bg-white"],
  .wm-admin-light button[class*="bg-black"],
  .wm-admin-light button[class*="bg-[#"] {
    background: #f8fafc !important;
    color: #1d2939 !important;
    border-color: #cbd5df !important;
  }

  /* Tema seçici */
  .wm-admin-light .wm-admin-theme-switch {
    background:
      linear-gradient(180deg,#f3f6f9 0%,#e5eaf0 100%) !important;
    border-color: #cbd5df !important;
  }

  .wm-admin-light .wm-admin-theme-button {
    background: transparent !important;
    color: #667085 !important;
    box-shadow: none !important;
  }

  .wm-admin-light .wm-admin-theme-button.is-active {
    background: #ffffff !important;
    color: #8f6a00 !important;
    border-color: #d7b64a !important;
    box-shadow: 0 1px 3px rgba(16,24,40,.08) !important;
  }

`;

const API_EMPLOYEE_PERFORMANCE = "/api/employee-performance-summary.php";
const API_MESSAGES_LIST = "/api/messages-list.php";
const API_MESSAGE_SEND = "/api/message-send.php";
const API_MESSAGE_READ = "/api/message-read.php";
const API_ACADEMY_LIST = "/api/academy-list.php";
const API_ACADEMY_SAVE = "/api/academy-save.php";
const API_ACADEMY_DELETE = "/api/academy-delete.php";
const API_PASSWORD_UPDATE = "/api/password-update.php";
const API_PASSWORD_RESET_LIST = "/api/password-reset-list.php";
const API_PASSWORD_RESET_STATUS = "/api/password-reset-status.php";
const API_REWARD_LIST = "/api/reward-list.php";
const API_REWARD_SAVE = "/api/reward-save.php";
const API_REWARD_DELETE = "/api/reward-delete.php";
const API_AGENCY_TRACKING_LIST = "/api/agency-tracking-list.php";
const API_FAQ_LIST = "/api/faq-list.php";
const API_FAQ_SAVE = "/api/faq-save.php";
const API_FAQ_DELETE = "/api/faq-delete.php";
const API_GIFT_WINNERS_LIST = "/api/gift-winners-list.php";
const API_GIFT_WINNER_SENT = "/api/gift-winner-sent.php";

const initialEvents = [
  {
    id: 1,
    title: "Haftalık Elmas Yarışması",
    target: "Tüm Yayıncılar",
    date: "15-22 Ekim",
    status: "Aktif",
    desc: "Haftalık en çok elmas toplayan ilk 3 yayıncıya özel bonus ödül.",
  },
  {
    id: 2,
    title: "PK Turnuvası Ön Eleme",
    target: "Kademe 3+",
    date: "18 Ekim",
    status: "Planlandı",
    desc: "Ajans içi yayıncılar arası eşleşmeli PK serisi.",
  },
  {
    id: 3,
    title: "Canlı Yayın Saat Çekilişi",
    target: "Aktif Yayıncılar",
    date: "Ay Sonu",
    status: "Taslak",
    desc: "Ay içinde düzenli yayın açan yayıncılar için edit desteği çekilişi.",
  },
];

function safeNumber(value) {
  return Number(value) || 0;
}

function formatNumber(value) {
  return safeNumber(value).toLocaleString("tr-TR");
}

function secondsToHours(seconds) {
  return Math.round((safeNumber(seconds) / 3600) * 10) / 10;
}

function normalizeAgencyTrackingStatus(value) {
  const text = String(value || "Aktif").toLowerCase().trim();
  return text.includes("pasif") || text.includes("passive") || text.includes("inactive") ? "Pasif" : "Aktif";
}

function getPublisherAgencyTrackingStatus(publisher = {}) {
  return normalizeAgencyTrackingStatus(
    publisher.agencyTrackingStatus ||
    publisher.agency_tracking_status ||
    publisher.agencyTracking ||
    publisher.agency_tracking ||
    publisher.raw?.agencyTrackingStatus ||
    publisher.raw?.agency_tracking_status ||
    "Aktif"
  );
}

function InfoBox({ title, value, note, trend }) {
  return (
    <div className="rounded-[1.2rem] border border-white/10 bg-black/20 p-4">
      <div className="text-xs uppercase tracking-[0.18em] text-yellow-300">
        {title}
      </div>

      <div className="mt-3 text-xl font-semibold text-white">{value}</div>

      <div className="mt-2 text-sm text-white/45">{note}</div>

      {trend}
    </div>
  );
}

function formatDateForDisplay(value) {
  if (!value) return "-";
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;
  return `${day}.${month}.${year}`;
}

function toInputDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultReportRange() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  return {
    start: toInputDate(start),
    end: toInputDate(end),
  };
}

function getPublisherHiddenId(publisher) {
  return String(
    publisher?.hiddenId ||
    publisher?.hidden_id ||
    publisher?.hiddenID ||
    ""
  ).trim();
}

function mergePublisherPerformance(publishers, performanceData) {
  const performanceMap = {};
  const usernameMap = {};
  const lastSevenMap = {};

  (performanceData?.publishers || []).forEach((item) => {
    const hiddenId = String(
      item.hiddenId || item.hidden_id || item.hiddenID || ""
    ).trim();
    if (hiddenId) {
      performanceMap[hiddenId] = item;
    }
    const uName = String(item.username || "").toLowerCase().replace(/[@\s]/g, "");
    if (uName) {
      usernameMap[uName] = item;
    }
  });

  (performanceData?.lastSeven?.rows || []).forEach((item) => {
    const hiddenId = String(
      item.hiddenId || item.hidden_id || item.hiddenID || ""
    ).trim();
    if (hiddenId) {
      lastSevenMap[hiddenId] = item;
    }
  });

  return publishers.map((publisher) => {
    const hiddenId = getPublisherHiddenId(publisher);
    const uName = String(publisher.username || publisher.name || "").toLowerCase().replace(/[@\s]/g, "");
    const perf = (hiddenId && performanceMap[hiddenId]) || (uName && usernameMap[uName]) || {};
    const lastSeven = lastSevenMap[hiddenId] || {};

    const liveSeconds = safeNumber(
      perf.totalLiveSeconds || perf.total_live_seconds
    );
    const diamonds = safeNumber(
      perf.totalDiamonds || perf.total_diamonds || publisher.currentDiamonds
    );
    const liveCount = safeNumber(
      perf.totalLiveCount || perf.total_live_count
    );
    const lastLiveDate =
      perf.lastLiveDate ||
      perf.last_live_date ||
      perf.maxReportDate ||
      perf.max_report_date ||
      "";
    const avgDiamondsPerLive = safeNumber(
      perf.avgDiamondsPerLive ||
      perf.avg_diamonds_per_live ||
      (liveCount > 0 ? Math.round(diamonds / liveCount) : 0)
    );

    return {
      ...publisher,
      hiddenId,
      currentSeconds: liveSeconds,
      currentHours: secondsToHours(liveSeconds),
      currentDiamonds: diamonds,
      currentPublishCount: liveCount,
      performanceLiveSeconds: liveSeconds,
      performanceLiveCount: liveCount,
      performanceDiamonds: diamonds,
      performanceLastLiveDate: lastLiveDate,
      performanceAvgDiamondsPerLive: avgDiamondsPerLive,
      targetDiamonds: safeNumber(publisher.targetDiamonds || publisher.target_diamonds || 0),
      lastSevenActiveDays: safeNumber(lastSeven.activeDays),
      lastSevenTotalDays: safeNumber(lastSeven.totalDays || 7),
      lastSevenDays: lastSeven.days || [],
      openedLastThreeDays: safeNumber(lastSeven.activeDays) > 0,
    };
  });
}

function normalizePublisher(user, index = 0) {
  const diamonds = safeNumber(user.currentDiamonds ?? user.diamonds ?? user.elmas ?? 0);
  const hours = safeNumber(user.currentHours ?? user.liveHours ?? user.hours ?? user.yayinSaati ?? 0);

  return {
    id: user.id ?? user.userId ?? user.hiddenId ?? index + 1,
    code: user.code || `D${index + 1}`,
    fullName: user.fullName || user.name || user.adSoyad || `Yayıncı ${index + 1}`,
    username: user.username || user.userName || user.kullaniciAdi || "-",
    phone: user.phone || user.telefon || user.mobile || "-",
    hiddenId: user.hiddenId || user.hidden_id || user.hiddenID || "-",
    tier: user.tier || user.kademe || user.tierLabel || user.kademeLabel || "",
    status: user.status || user.durum || "Aktif",
    agencyTrackingStatus: normalizeAgencyTrackingStatus(user.agencyTrackingStatus || user.agency_tracking_status || user.agencyTracking || user.agency_tracking || "Aktif"),
    currentHours: hours,
    currentDiamonds: diamonds,
    targetDiamonds: safeNumber(user.targetDiamonds ?? user.hedefElmas ?? diamonds * 1.1),
    previousMonthHours: safeNumber(user.previousMonthHours ?? user.lastMonthHours ?? hours),
    previousMonthDiamonds: safeNumber(user.previousMonthDiamonds ?? user.lastMonthDiamonds ?? diamonds),
    openedLastThreeDays: Boolean(user.openedLastThreeDays ?? user.lastThreeDaysActive ?? user.openedYesterday ?? user.status === "Aktif"),
    createdAt:
      user.createdAt ||
      user.created_at ||
      user.created ||
      user.registeredAt ||
      user.registered_at ||
      user.kayitTarihi ||
      user.kayit_tarihi ||
      "",
  };
}

function normalizeReport(report, index = 0) {
  return {
    id: report.id ?? index + 1,
    name: report.name || report.reportName || report.title || `Rapor ${index + 1}`,
    code: report.code || report.reportCode || "MYSQL-REPORT",
    url: report.url || "",
  };
}

function getTier(diamonds = 0) {
  const d = typeof diamonds === "object" ? safeNumber(diamonds?.currentDiamonds || diamonds?.diamonds) : safeNumber(diamonds);
  if (typeof getPublisherTierInfo === "function") {
    return getPublisherTierInfo(typeof diamonds === "object" ? diamonds : {}, d).kademeLabel;
  }
  return "Kademe 1";
}

function filterPublishers(list, searchText) {
  const search = String(searchText || "").toLowerCase().trim();
  if (!search) return list;
  return list.filter((item) =>
    `${item.fullName} ${item.username} ${item.hiddenId} ${item.phone}`.toLowerCase().includes(search)
  );
}

function filterFaqs(list, searchText) {
  const search = String(searchText || "").toLowerCase().trim();
  if (!search) return list;
  return list.filter((item) => `${item.question} ${item.answer}`.toLowerCase().includes(search));
}

function createFaqItem(question, answer, currentLength) {
  return {
    id: Date.now(),
    question: question.trim(),
    answer: answer.trim(),
    status: "Aktif",
    order: currentLength + 1,
  };
}

const emptyPasswordForm = {
  newPassword: "",
  confirmPassword: "",
};

const emptyTrainingForm = {
  id: "",
  title: "",
  description: "",
  contentType: "video",
  targetGroup: "all",
  fileUrl: "",
  externalUrl: "",
  status: "Aktif",
};

function buildMenuGroups(publisherCount, faqCount, unreadMessageCount = 0, passwordRequestCount = 0) {
  return [
    {
      title: "Ana Menü",
      items: [
        { key: "dashboard", label: "Gösterge Paneli", badge: "Canlı", description: "Genel performans özeti" },
        { key: "publishers", label: "Yayıncılar", count: publisherCount, description: "Bağlı yayıncı listesi ve performans" },
        {
          key: "publisherMessages",
          label: "Yayıncı Mesajları",
          badge: unreadMessageCount > 0 ? String(unreadMessageCount) : null,
          description: "Yayıncılardan gelen mesajlar",
        },
        { key: "academy", label: "Wolf Akademi", badge: "Yeni", description: "Eğitim video ve bilgi paylaşımları" },
        { key: "reports", label: "Raporlar", badge: "MySQL", description: "MySQL performans raporları" },
        { key: "events", label: "Etkinlikler", description: "Yarışma ve etkinlik planlamaları" },
        {
          key: "gifts",
          label: "Şifre Talepleri",
          badge: passwordRequestCount > 0 ? String(passwordRequestCount) : null,
          description: "Yayıncı şifre yenileme talepleri",
        },
        { key: "editorSupport", label: "Editör Desteği", badge: "%", description: "Editör paketleri ve indirim yönetimi" },
        { key: "giftDelivery", label: "Hediye Takibi", badge: "🎁", description: "Hediye bekleyen ve gönderilen yayıncılar" },
      ],
    },
    {
      title: "Yönetim",
      items: [
        { key: "faq", label: "Sık Sorulan Sorular", count: faqCount, description: "Yayıncı destek soru havuzu" },
        { key: "toplist", label: "Ekip Top Listesi", badge: "Top 10", description: "Admin ile aynı performans sıralama kuralları" },
        {
          key: "settings",
          label: "Ayarlar",
          description: "Genel panel ayarları",
          children: [
            { key: "users", label: "Kullanıcı Yetkileri" },
            { key: "roles", label: "Rol Tanımları" },
            { key: "security", label: "Güvenlik" },
          ],
        },
      ],
    },
  ];
}

function getDemoPublishers() {
  return [
    normalizePublisher({ id: 1, code: "D1", fullName: "Demo Yayıncı 1", username: "yayinci1", phone: "+90 555 123 45 67", hiddenId: "7479023620174594049", status: "Aktif", currentHours: 36, currentDiamonds: 326490, previousMonthHours: 34, previousMonthDiamonds: 296800, openedLastThreeDays: true }, 0),
    normalizePublisher({ id: 2, code: "D2", fullName: "Demo Yayıncı 2", username: "yayinci2", phone: "+90 555 987 65 43", hiddenId: "8427193620188891021", status: "Aktif", currentHours: 42, currentDiamonds: 512800, previousMonthHours: 40, previousMonthDiamonds: 466100, openedLastThreeDays: true }, 1),
    normalizePublisher({ id: 3, code: "D3", fullName: "Demo Yayıncı 3", username: "yayinci3", phone: "+90 554 321 00 11", hiddenId: "1298736450011287364", status: "Pasif", currentHours: 18, currentDiamonds: 78500, previousMonthHours: 20, previousMonthDiamonds: 71300, openedLastThreeDays: false }, 2),
    normalizePublisher({ id: 4, code: "D4", fullName: "Demo Yayıncı 4", username: "yayinci4", phone: "+90 553 222 33 44", hiddenId: "5566778899001122334", status: "Aktif", currentHours: 55, currentDiamonds: 910250, previousMonthHours: 52, previousMonthDiamonds: 827500, openedLastThreeDays: true }, 3),
    normalizePublisher({ id: 5, code: "D5", fullName: "Demo Yayıncı 5", username: "yayinci5", phone: "+90 552 444 55 66", hiddenId: "9988776655443322110", status: "Pasif", currentHours: 12, currentDiamonds: 42000, previousMonthHours: 14, previousMonthDiamonds: 38200, openedLastThreeDays: false }, 4),
    normalizePublisher({ id: 6, code: "D6", fullName: "Demo Yayıncı 6", username: "yayinci6", phone: "+90 551 777 88 99", hiddenId: "4567812390001123488", status: "Aktif", currentHours: 61, currentDiamonds: 1280400, previousMonthHours: 58, previousMonthDiamonds: 1164000, openedLastThreeDays: true }, 5),
  ];
}

function getDemoReports() {
  return [
    normalizeReport(
      {
        id: 1,
        name: "Yayıncı Performans Raporu",
        code: "MYSQL-PERFORMANCE",
        url: "",
      },
      0
    ),
  ];
}


function formatDurationFromSeconds(seconds) {
  const total = safeNumber(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${hours} sa ${minutes} dk`;
}

function parseInputDate(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

function addDaysInputDate(value, amount) {
  const date = parseInputDate(value);
  date.setDate(date.getDate() + amount);
  return toInputDate(date);
}

function getDateRangeDayCount(startDate, endDate) {
  const start = parseInputDate(startDate);
  const end = parseInputDate(endDate);
  const diff = end.getTime() - start.getTime();

  return Math.max(1, Math.floor(diff / (1000 * 60 * 60 * 24)) + 1);
}

function getPreviousEqualDateRange(startDate, endDate) {
  const dayCount = getDateRangeDayCount(startDate, endDate);

  return {
    start: addDaysInputDate(startDate, -dayCount),
    end: addDaysInputDate(startDate, -1),
  };
}

function getSummaryNumber(summary, keys = []) {
  for (const key of keys) {
    const value = summary?.[key];

    if (value !== undefined && value !== null && value !== "") {
      return safeNumber(value);
    }
  }

  return 0;
}

function getTrendPercent(current, previous) {
  const currentValue = safeNumber(current);
  const previousValue = safeNumber(previous);

  if (previousValue === 0 && currentValue === 0) return 0;
  if (previousValue === 0 && currentValue > 0) return 100;

  return Math.round(((currentValue - previousValue) / previousValue) * 100);
}

function CompareNote({
  current,
  previous,
  formatter = formatNumber,
  prefix = "",
  suffix = "",
}) {
  const currentValue = safeNumber(current);
  const previousValue = safeNumber(previous);
  const diff = currentValue - previousValue;
  const percent = getTrendPercent(currentValue, previousValue);

  const colorClass =
    diff > 0
      ? "text-green-400"
      : diff < 0
        ? "text-red-400"
        : "text-white/40";

  const icon = diff > 0 ? "▲" : diff < 0 ? "▼" : "■";

  return (
    <div className={`mt-1 text-[11px] font-bold ${colorClass}`}>
      {icon} %{Math.abs(percent)}{" "}
      <span className="text-white/35">
        Önceki: {prefix}
        {formatter(previousValue)}
        {suffix}
      </span>
    </div>
  );
}



function SvgDiamondTooltip({ point, chartWidth = 1000 }) {
  if (!point) return null;

  const boxWidth = 210;
  const boxHeight = 48;
  const x = Math.min(Math.max(point.x - boxWidth / 2, 8), chartWidth - boxWidth - 8);
  const y = Math.max(point.y - boxHeight - 14, 8);

  return (
    <g pointerEvents="none">
      <rect
        x={x}
        y={y}
        width={boxWidth}
        height={boxHeight}
        rx="14"
        fill="rgba(5,5,5,0.94)"
        stroke="rgba(250,204,21,0.46)"
      />
      <text x={x + 14} y={y + 19} className="fill-white/55 text-[11px] font-semibold">
        {point.dateText}
      </text>
      <text x={x + 14} y={y + 36} className="fill-yellow-300 text-[13px] font-black">
        Elmas: {formatNumber(point.value)}
      </text>
    </g>
  );
}

function getPublisherTierInfo(source = {}, diamondOverride) {
  const diamondValue =
    diamondOverride !== undefined && diamondOverride !== null
      ? safeNumber(diamondOverride)
      : safeNumber(
          source?.performanceDiamonds ??
            source?.currentDiamonds ??
            source?.totalDiamonds ??
            source?.total_diamonds ??
            source?.diamonds ??
            source?.elmas ??
            source?.previousMonthDiamonds ??
            0
        );

  const rawTier = String(
    source?.tier ||
      source?.kademe ||
      source?.tierLabel ||
      source?.kademeLabel ||
      ""
  );

  let tierNo = 1;

  if (diamondValue >= 3000000) tierNo = 10;
  else if (diamondValue >= 1600000) tierNo = 9;
  else if (diamondValue >= 1200000) tierNo = 8;
  else if (diamondValue >= 800000) tierNo = 7;
  else if (diamondValue >= 500000) tierNo = 6;
  else if (diamondValue >= 300000) tierNo = 5;
  else if (diamondValue >= 150000) tierNo = 4;
  else if (diamondValue >= 80000) tierNo = 3;
  else if (diamondValue >= 40000) tierNo = 2;
  else {
    const parsedTier = Number(rawTier.match(/\d+/)?.[0] || 0);
    tierNo = parsedTier > 0 ? parsedTier : 1;
  }

  if (tierNo >= 10) {
    return {
      tierNo,
      tierLabel: "Tier 10",
      kademeLabel: "Kademe 10",
      groupLabel: "Top",
      badgeClass:
        "border-yellow-300/60 bg-yellow-400/15 text-yellow-200 shadow-[0_0_18px_rgba(250,204,21,.18)]",
      textClass: "text-yellow-200",
      rowClass: "border-yellow-400/25 bg-yellow-500/[0.055]",
    };
  }

  if (tierNo >= 9) {
    return {
      tierNo,
      tierLabel: "Tier 9",
      kademeLabel: "Kademe 9",
      groupLabel: "Top",
      badgeClass:
        "border-yellow-300/60 bg-yellow-400/15 text-yellow-200 shadow-[0_0_18px_rgba(250,204,21,.18)]",
      textClass: "text-yellow-200",
      rowClass: "border-yellow-400/25 bg-yellow-500/[0.055]",
    };
  }

  if (tierNo >= 6) {
    return {
      tierNo,
      tierLabel: `Tier ${tierNo}`,
      kademeLabel: `Kademe ${tierNo}`,
      groupLabel: "Pre-Top",
      badgeClass: "border-fuchsia-400/50 bg-fuchsia-500/12 text-fuchsia-200",
      textClass: "text-fuchsia-200",
      rowClass: "border-fuchsia-400/20 bg-fuchsia-500/[0.045]",
    };
  }

  if (tierNo >= 3) {
    return {
      tierNo,
      tierLabel: `Tier ${tierNo}`,
      kademeLabel: `Kademe ${tierNo}`,
      groupLabel: "Mature",
      badgeClass: "border-cyan-400/45 bg-cyan-500/12 text-cyan-200",
      textClass: "text-cyan-200",
      rowClass: "border-cyan-400/20 bg-cyan-500/[0.045]",
    };
  }

  if (tierNo === 2) {
    return {
      tierNo,
      tierLabel: "Tier 2",
      kademeLabel: "Kademe 2",
      groupLabel: "G4",
      badgeClass: "border-emerald-400/45 bg-emerald-500/12 text-emerald-200",
      textClass: "text-emerald-200",
      rowClass: "border-emerald-400/20 bg-emerald-500/[0.045]",
    };
  }

  return {
    tierNo: 1,
    tierLabel: "Tier 1",
    kademeLabel: "Kademe 1",
    groupLabel: "G0-G3",
    badgeClass: "border-white/20 bg-white/[0.045] text-white/65",
    textClass: "text-white/70",
    rowClass: "border-white/10 bg-white/[0.025]",
  };
}

function sortPublishersByTierDesc(list = []) {
  return [...list].sort((a, b) => {
    const tierA = getPublisherTierInfo(a).tierNo;
    const tierB = getPublisherTierInfo(b).tierNo;

    if (tierB !== tierA) return tierB - tierA;

    const diamondsA = safeNumber(
      a?.performanceDiamonds ??
        a?.currentDiamonds ??
        a?.totalDiamonds ??
        a?.total_diamonds ??
        a?.diamonds ??
        0
    );

    const diamondsB = safeNumber(
      b?.performanceDiamonds ??
        b?.currentDiamonds ??
        b?.totalDiamonds ??
        b?.total_diamonds ??
        b?.diamonds ??
        0
    );

    return diamondsB - diamondsA;
  });
}

function PublisherTierBadge({ publisher = {}, diamonds, compact = false }) {
  const tierInfo = getPublisherTierInfo(publisher, diamonds);
  const sizeClass = compact ? "px-2 py-0.5 text-[9px]" : "px-2.5 py-1 text-[10px]";

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${compact ? "mt-1" : "mt-2"}`}>
      <span
        className={`inline-flex shrink-0 items-center rounded-full border font-medium leading-none ${sizeClass} ${tierInfo.badgeClass}`}
      >
        {tierInfo.groupLabel}
      </span>

      <span
        className={`inline-flex shrink-0 items-center rounded-full border font-medium leading-none ${sizeClass} ${tierInfo.badgeClass}`}
      >
        {tierInfo.tierLabel}
      </span>
    </div>
  );
}

function PublisherNameWithTier({ publisher = {}, diamonds, label, className = "", nameClassName = "" }) {
  const tierInfo = getPublisherTierInfo(publisher, diamonds);
  const displayName = label || publisher?.fullName || publisher?.username || "-";

  return (
    <div className={`flex min-w-0 items-center ${className}`}>
      <span className={`truncate ${tierInfo.textClass} ${nameClassName}`}>
        {displayName}
      </span>
    </div>
  );
}


const TARGET_GROUP_OPTIONS = [
  { value: "all", label: "Tüm Yayıncılar", badgeClass: "border-yellow-400/45 bg-yellow-500/12 text-yellow-200", rowClass: "border-yellow-400/20 bg-yellow-500/[0.035]" },
  { value: "top", label: "Top", badgeClass: "border-yellow-300/60 bg-yellow-400/15 text-yellow-200 shadow-[0_0_18px_rgba(250,204,21,.14)]", rowClass: "border-yellow-400/25 bg-yellow-500/[0.055]" },
  { value: "pre_top", label: "Pre-Top", badgeClass: "border-fuchsia-400/50 bg-fuchsia-500/12 text-fuchsia-200", rowClass: "border-fuchsia-400/20 bg-fuchsia-500/[0.045]" },
  { value: "mature", label: "Mature", badgeClass: "border-cyan-400/45 bg-cyan-500/12 text-cyan-200", rowClass: "border-cyan-400/20 bg-cyan-500/[0.045]" },
  { value: "g4", label: "G4", badgeClass: "border-emerald-400/45 bg-emerald-500/12 text-emerald-200", rowClass: "border-emerald-400/20 bg-emerald-500/[0.045]" },
  { value: "g0_g3", label: "G0-G3", badgeClass: "border-white/20 bg-white/[0.045] text-white/65", rowClass: "border-white/10 bg-white/[0.025]" },
];

function normalizeTargetGroup(value) {
  const text = String(value || "all").trim();
  const normalized = text.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");

  if (["all", "tum_yayincilar", "tüm_yayıncılar", "tüm_yayincilar", "tum_yayıncılar"].includes(normalized)) return "all";
  if (["top", "tier_9_plus", "tier_10", "tier_9"].includes(normalized)) return "top";
  if (["pre_top", "pretop", "pre_top", "tier_6_8", "tier_6_plus"].includes(normalized)) return "pre_top";
  if (["mature", "tier_3_5", "tier_5_plus", "kademe_5_ve_üzeri", "kademe_5_ve_uzeri"].includes(normalized)) return "mature";
  if (["g4", "tier_2", "kademe_2"].includes(normalized)) return "g4";
  if (["g0_g3", "g0_g3", "g0-g3", "tier_1", "kademe_1"].includes(normalized)) return "g0_g3";

  const byLabel = TARGET_GROUP_OPTIONS.find(
    (item) => item.label.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_") === normalized
  );

  return byLabel?.value || "all";
}

function getTargetGroupInfo(value) {
  const normalized = normalizeTargetGroup(value);
  return TARGET_GROUP_OPTIONS.find((item) => item.value === normalized) || TARGET_GROUP_OPTIONS[0];
}

function getTargetGroupLabel(value) {
  return getTargetGroupInfo(value).label;
}

function TargetGroupBadge({ value, compact = false }) {
  const info = getTargetGroupInfo(value);

  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full border font-semibold leading-none ${
        compact ? "px-2 py-0.5 text-[9px]" : "px-3 py-1 text-[11px]"
      } ${info.badgeClass}`}
    >
      {info.label}
    </span>
  );
}

function getTargetGroupFromTierInfo(tierInfo = {}) {
  const group = String(tierInfo.groupLabel || "").toLowerCase();

  if (group === "top") return "top";
  if (group === "pre-top" || group === "pre_top" || group === "pretop") return "pre_top";
  if (group === "mature") return "mature";
  if (group === "g4") return "g4";
  return "g0_g3";
}

function targetGroupMatchesPublisher(targetGroup, publisher = {}, diamonds) {
  const target = normalizeTargetGroup(targetGroup);
  if (target === "all") return true;

  const publisherGroup = getTargetGroupFromTierInfo(
    getPublisherTierInfo(publisher, diamonds)
  );

  return target === publisherGroup;
}

function formatAgencyTenure(value) {
  if (!value) return "-";

  const start = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(start.getTime())) return "-";

  const today = new Date();
  const diffMs = today.getTime() - start.getTime();
  const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

  let months =
    (today.getFullYear() - start.getFullYear()) * 12 +
    (today.getMonth() - start.getMonth());

  if (today.getDate() < start.getDate()) {
    months -= 1;
  }

  if (months < 1) return `${diffDays} gün`;

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years > 0 && remainingMonths > 0) {
    return `${years} yıl ${remainingMonths} ay`;
  }

  if (years > 0) return `${years} yıl`;

  return `${months} ay`;
}

function formatLastLiveDate(value) {
  if (!value) return "-";
  return formatDateForDisplay(String(value).slice(0, 10));
}

function formatAverageDiamondsPerLive(value) {
  const numberValue = safeNumber(value);
  if (numberValue <= 0) return "-";
  return formatNumber(numberValue);
}

export default function EmployeeDashboard({ overrideEmployee = null, isImpersonated = false } = {}) {
  useMobileOnlyResponsiveLayout("wm-employee-dashboard-mobile");

  const navigate = (path) => {
    window.location.href = path;
  };

  const [activeMenu, setActiveMenu] = useState("dashboard");
  const { theme, toggleTheme, isDark } = useTheme();
  const [openMenus, setOpenMenus] = useState({});
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [assignedPublishers, setAssignedPublishers] = useState([]);
  const [assignedReports, setAssignedReports] = useState([]);
  const [agencyTrackingMap, setAgencyTrackingMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [publisherSearch, setPublisherSearch] = useState("");
  const [faqSearch, setFaqSearch] = useState("");
  const [faqs, setFaqs] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const [showAddFaq, setShowAddFaq] = useState(false);
  const [newFaqQuestion, setNewFaqQuestion] = useState("");
  const [newFaqAnswer, setNewFaqAnswer] = useState("");
  const [showInactivePopup, setShowInactivePopup] = useState(false);
  const [employeeMessages, setEmployeeMessages] = useState([]);
  const [passwordRequests, setPasswordRequests] = useState([]);
  const [passwordRequestsLoading, setPasswordRequestsLoading] = useState(false);
  const [managerRows, setManagerRows] = useState([]);
  const [passwordModal, setPasswordModal] = useState({ open: false, target: null });
  const [passwordForm, setPasswordForm] = useState({ newPassword: "", confirmPassword: "" });
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    async function loadManagers() {
      try {
        const res = await fetch("/api/users-list.php");
        const data = await res.json();
        if (data && data.success && Array.isArray(data.users)) {
          const mgrs = data.users.filter((u) => u.role === "manager" || u.role === "employee");
          setManagerRows(mgrs);
        }
      } catch (e) {
        console.warn("Yönetici listesi alınamadı:", e);
      }
    }
    loadManagers();
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordForm.newPassword || passwordForm.newPassword.length < 6) {
      alert("Şifre en az 6 karakter olmalıdır.");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("Şifreler birbiriyle eşleşmiyor.");
      return;
    }
    if (!passwordModal.target?.id) {
      alert("Kullanıcı ID bulunamadı.");
      return;
    }
    try {
      setPasswordSaving(true);
      const res = await fetch("/api/user-password-update.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: passwordModal.target.id,
          password: passwordForm.newPassword.trim(),
        }),
      });
      const data = await res.json();
      if (data && data.success) {
        alert("Şifre başarıyla güncellendi.");
        setPasswordModal({ open: false, target: null });
        setPasswordForm({ newPassword: "", confirmPassword: "" });
      } else {
        alert(data?.message || "Şifre güncellenemedi.");
      }
    } catch (err) {
      alert("Şifre güncelleme hatası: " + err.message);
    } finally {
      setPasswordSaving(false);
    }
  };  
  const defaultReportRange = useMemo(() => getDefaultReportRange(), []);
  const [reportStartDate, setReportStartDate] = useState(defaultReportRange.start);
  const [reportEndDate, setReportEndDate] = useState(defaultReportRange.end);
  const [employeePerformance, setEmployeePerformance] = useState(null);
  const [previousEmployeePerformance, setPreviousEmployeePerformance] = useState(null);
  const [employeePerformanceLoading, setEmployeePerformanceLoading] = useState(false);
  const [employeePerformanceError, setEmployeePerformanceError] = useState("");
  const loadPasswordRequests = useCallback(async () => {
  try {
    setPasswordRequestsLoading(true);

    const response = await fetch(API_PASSWORD_RESET_LIST);
    const data = await response.json();

    if (data.success) {
      setPasswordRequests(data.requests || []);
    }
  } catch (err) {
    console.error("Şifre talepleri alınamadı:", err);
  } finally {
    setPasswordRequestsLoading(false);
  }
}, []);

useEffect(() => {
  loadPasswordRequests();
}, [loadPasswordRequests]);

const loadFaqRows = useCallback(async () => {
  try {
    setFaqLoading(true);

    const response = await fetch(API_FAQ_LIST, { cache: "no-store" });
    const data = await response.json();

    if (data.success) {
      setFaqs(data.faqs || []);
    } else {
      alert(data.message || "SS arşivi alınamadı.");
    }
  } catch (err) {
    console.error(err);
    alert("SS arşivi alınırken bağlantı hatası oluştu.");
  } finally {
    setFaqLoading(false);
  }
}, []);

useEffect(() => {
  loadFaqRows();
}, [loadFaqRows]);

useEffect(() => {
  let ignore = false;
  async function loadAgencyTrackingRows() {
    try {
      const response = await fetch(API_AGENCY_TRACKING_LIST, { cache: "no-store" });
      const data = await response.json();
      const map = {};
      (data.items || []).forEach((item) => {
        const status = normalizeAgencyTrackingStatus(item.agencyTrackingStatus || item.agency_tracking_status);
        if (item.id) map[`id:${item.id}`] = status;
        if (item.username) map[`u:${item.username}`] = status;
        if (item.hiddenId || item.hidden_id) map[`h:${item.hiddenId || item.hidden_id}`] = status;
      });
      if (!ignore) setAgencyTrackingMap(map);
    } catch {
      if (!ignore) setAgencyTrackingMap({});
    }
  }
  loadAgencyTrackingRows();
  return () => { ignore = true; };
}, []);

const loadPublishers = useCallback(async () => {
  let user = overrideEmployee || null;
  if (!user) {
    try {
      user = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      user = null;
    }
  }

  const previewUser = {
    fullName: "Wolf Media Yönetici",
    username: "yonetici",
    status: "Aktif",
  };

  const activeUser = user?.username ? user : previewUser;
  setLoading(true);

  try {
    const res = await fetch(`/api/get-employee.php?username=${encodeURIComponent(activeUser.username)}`);
    const data = await res.json();
    if (!data.success) {
      setErrorMessage(data.message || "Çalışan bilgisi alınamadı.");
      setCurrentEmployee(activeUser);
    } else {
      setCurrentEmployee(data.employee || activeUser);
      setAssignedPublishers((data.publishers || []).map(normalizePublisher));
      setAssignedReports((data.reports || []).map(normalizeReport));
    }
  } catch (err) {
    console.error(err);
    setCurrentEmployee(activeUser);
    setAssignedPublishers(getDemoPublishers());
    setAssignedReports(getDemoReports());
    setErrorMessage("Veriler alınamadı, önizleme demo verilerle açıldı.");
  } finally {
    setLoading(false);
  }
}, [overrideEmployee]);

useEffect(() => {
  loadPublishers();
}, [loadPublishers]);

  const loadEmployeeMessages = useCallback(async () => {
    if (!currentEmployee?.id || !currentEmployee?.role) return;

    try {
      const response = await fetch(
        `${API_MESSAGES_LIST}?user_id=${currentEmployee.id}&role=${currentEmployee.role}`
      );

      const data = await response.json();

      if (data.success) {
        setEmployeeMessages(data.messages || []);

      }
    } catch (err) {
      console.error("Yönetici mesajları alınamadı:", err);
    }
  }, [currentEmployee?.id, currentEmployee?.role]);

  useEffect(() => {
    loadEmployeeMessages();
  }, [loadEmployeeMessages]);

  const rawPublishers = useMemo(() => assignedPublishers, [assignedPublishers]);
  const reports = useMemo(() => assignedReports, [assignedReports]);

  const hiddenIdsForPerformance = useMemo(() => {
    return rawPublishers
      .map((publisher) => getPublisherHiddenId(publisher))
      .filter((hiddenId) => hiddenId && hiddenId !== "-")
      .join(",");
  }, [rawPublishers]);

  useEffect(() => {
  if (!hiddenIdsForPerformance) {
    setEmployeePerformance(null);
    setPreviousEmployeePerformance(null);
    return;
  }

  let ignore = false;

  async function loadEmployeePerformance() {
    try {
      setEmployeePerformanceLoading(true);
      setEmployeePerformanceError("");

      const previousRange = getPreviousEqualDateRange(
        reportStartDate,
        reportEndDate
      );

      const currentUrl = `${API_EMPLOYEE_PERFORMANCE}?hiddenIds=${encodeURIComponent(
        hiddenIdsForPerformance
      )}&startDate=${reportStartDate}&endDate=${reportEndDate}`;

      const previousUrl = `${API_EMPLOYEE_PERFORMANCE}?hiddenIds=${encodeURIComponent(
        hiddenIdsForPerformance
      )}&startDate=${previousRange.start}&endDate=${previousRange.end}`;

      const [currentResponse, previousResponse] = await Promise.all([
        fetch(currentUrl, { cache: "no-store" }),
        fetch(previousUrl, { cache: "no-store" }),
      ]);

      const [currentData, previousData] = await Promise.all([
        currentResponse.json(),
        previousResponse.json(),
      ]);

      if (!currentData.success) {
        throw new Error(currentData.message || "Yönetici performans verisi alınamadı.");
      }

      if (!previousData.success) {
        throw new Error(previousData.message || "Önceki dönem performans verisi alınamadı.");
      }

      if (!ignore) {
        setEmployeePerformance(currentData);
        setPreviousEmployeePerformance(previousData);
      }
    } catch (err) {
      if (!ignore) {
        setEmployeePerformanceError(err.message || "Yönetici performans verisi alınamadı.");
        setPreviousEmployeePerformance(null);
      }
    } finally {
      if (!ignore) {
        setEmployeePerformanceLoading(false);
      }
    }
  }

  loadEmployeePerformance();

  return () => {
    ignore = true;
  };
}, [hiddenIdsForPerformance, reportStartDate, reportEndDate]);

  const publishers = useMemo(() => {
    return mergePublisherPerformance(rawPublishers, employeePerformance).map((publisher) => {
      const status =
        agencyTrackingMap[`id:${publisher.id}`] ||
        agencyTrackingMap[`u:${publisher.username}`] ||
        agencyTrackingMap[`h:${publisher.hiddenId}`] ||
        getPublisherAgencyTrackingStatus(publisher);
      return { ...publisher, agencyTrackingStatus: normalizeAgencyTrackingStatus(status) };
    });
  }, [rawPublishers, employeePerformance, agencyTrackingMap]);
   
  const visiblePasswordRequests = useMemo(() => {
    return passwordRequests.filter((request) => {
      const requestUsername = String(request.username || "").toLowerCase().trim();
      const requestPhone = String(request.phone || "").replace(/\D/g, "");

      return publishers.some((publisher) => {
        const publisherUsername = String(publisher.username || "").toLowerCase().trim();
        const publisherPhone = String(publisher.phone || "").replace(/\D/g, "");

        return (
          requestUsername === publisherUsername ||
          (requestPhone && publisherPhone && requestPhone === publisherPhone)
        );
      });
    });
  }, [passwordRequests, publishers]);

  const pendingPasswordRequestCount = visiblePasswordRequests.filter(
    (item) => item.status === "Bekliyor"
  ).length;

  const unreadMessageCount = employeeMessages.filter(
    (item) =>
      item.status === "unread" &&
      Number(item.receiver_id) === Number(currentEmployee?.id)
  ).length;

  
  const navigationGroups = useMemo(() => [
    {
      key: "dashboard",
      isDirect: true,
      title: "Yönetim Özeti",
      iconSvg: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/>
        </svg>
      ),
      badge: "Canlı",
      targetKey: "dashboard",
    },
    {
      groupKey: "management",
      title: "Yönetim Paneli",
      iconSvg: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      items: [
        { key: "publisher_list", label: "Yayıncı Paneli", badge: publishers.length > 0 ? String(publishers.length) : null },
        { key: "manager_messages", label: "Yönetici Mesajları", badge: unreadMessageCount > 0 ? String(unreadMessageCount) : null },
        { key: "publisher_content", label: "Yayıncı İçerikleri" },
      ],
    },
    {
      groupKey: "scout",
      title: "Yayıncı Takip Modülü",
      iconSvg: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
        </svg>
      ),
      items: [
        { key: "scout_dashboard", label: "Yayıncı Aday Takibi" },
        { key: "scout_followup", label: "Yeni Yayıncı Takibi" },
        { key: "scout_publisher_tracking", label: "Yayıncı Takibi" },
        { key: "scout_reports", label: "Yayıncı Raporları" },
      ],
    },
  ], [publishers.length, unreadMessageCount, faqs.length, pendingPasswordRequestCount]);

  const allMenuItems = useMemo(() => [
    { key: "dashboard", label: "Yönetim Özeti" },
    { key: "publishers", label: "Yayıncılar" },
    { key: "publisherMessages", label: "Yayıncı Mesajları" },
    { key: "academy", label: "Wolf Akademi" },
    { key: "giftDelivery", label: "Hediye Takibi" },
    { key: "editorSupport", label: "Editör Desteği" },
    { key: "reports", label: "Performans Raporları" },
    { key: "toplist", label: "Ekip Sıralaması (Top 10)" },
    { key: "events", label: "Etkinlik & Ödüller" },
    { key: "gifts", label: "Şifre Talepleri" },
    { key: "faq", label: "Sık Sorulan Sorular" },
    { key: "users", label: "Kullanıcı Yetkileri" },
    { key: "roles", label: "Rol Tanımları" },
    { key: "security", label: "Güvenlik" },
  ], []);

  const activeLabel = useMemo(() => {
    const found = allMenuItems.find((m) => m.key === activeMenu);
    return found ? found.label : activeMenu;
  }, [allMenuItems, activeMenu]);


  const filteredPublishers = useMemo(
    () => filterPublishers(publishers, publisherSearch),
    [publishers, publisherSearch]
  );

  const filteredFaqs = useMemo(
    () => filterFaqs(faqs, faqSearch),
    [faqs, faqSearch]
  );

  const performanceSummary = employeePerformance?.summary || {};

  const realizedLiveHours = safeNumber(performanceSummary.totalLiveHours);
  const realizedDiamonds = safeNumber(performanceSummary.totalDiamonds);
  const realizedPublishCount = safeNumber(performanceSummary.totalLiveCount);

  const targetLiveHours = safeNumber(performanceSummary.targetLiveHours);
  const targetDiamonds = safeNumber(performanceSummary.targetDiamonds);
  const targetPublishCount = safeNumber(performanceSummary.targetPublishCount);

  const lastSevenActiveCount = publishers.filter(
    (publisher) => safeNumber(publisher.lastSevenActiveDays) > 0
  ).length;

  const lastSevenDetailRows = publishers;

  const topLiveRows = useMemo(() => {
    return [...publishers]
      .filter((item) => safeNumber(item.currentSeconds) > 0)
      .sort((a, b) => safeNumber(b.currentSeconds) - safeNumber(a.currentSeconds))
      .slice(0, 10);
  }, [publishers]);

  const topDiamondRows = useMemo(() => {
    return [...publishers]
      .filter((item) => safeNumber(item.currentDiamonds) > 0)
      .sort((a, b) => safeNumber(b.currentDiamonds) - safeNumber(a.currentDiamonds))
      .slice(0, 10);
  }, [publishers]);

  const lowLiveRows = useMemo(() => {
    return [...publishers]
      .filter((item) => safeNumber(item.currentSeconds) > 0)
      .sort((a, b) => safeNumber(a.currentSeconds) - safeNumber(b.currentSeconds))
      .slice(0, 10);
  }, [publishers]);

  const lowDiamondRows = useMemo(() => {
    return [...publishers]
      .filter((item) => safeNumber(item.currentDiamonds) > 0)
      .sort((a, b) => safeNumber(a.currentDiamonds) - safeNumber(b.currentDiamonds))
      .slice(0, 10);
  }, [publishers]);

  const noLiveRows = useMemo(() => {
    return [...publishers]
      .filter(
        (item) =>
          safeNumber(item.currentSeconds) === 0 &&
          safeNumber(item.currentPublishCount) === 0
      )
      .sort((a, b) =>
        String(a.username || "").localeCompare(String(b.username || ""), "tr")
      );
  }, [publishers]);

  function handleMenuClick(item) {
    if (item.children?.length) {
      setOpenMenus((prev) => ({ ...prev, [item.key]: !prev[item.key] }));
      setActiveMenu(item.key);
      return;
    }
    setActiveMenu(item.key);
  }

  function handleAddFaq() {
    if (!newFaqQuestion.trim() || !newFaqAnswer.trim()) {
      alert("Soru ve cevap alanı zorunlu.");
      return;
    }
    setFaqs((prev) => [createFaqItem(newFaqQuestion, newFaqAnswer, prev.length), ...prev]);
    setNewFaqQuestion("");
    setNewFaqAnswer("");
    setShowAddFaq(false);
  }

  function handleDeleteFaq(id) {
    setFaqs((prev) => prev.filter((item) => item.id !== id));
  }

  function handleLogout() {
    localStorage.removeItem("user");
    navigate("/login");
  }

  if (loading) {
    return <LoadingScreen text="Yönetici paneli yükleniyor..." />;
  }

  if (!currentEmployee) {
    return <LoadingScreen text={errorMessage || "Çalışan kaydı bulunamadı."} />;
  }

    return (
    <div
      className={`wm-admin-theme wm-admin-${theme} min-h-screen ${
        theme === "dark" ? "bg-black text-white" : "bg-[#f4f6f8] text-[#182230]"
      }`}
    >
      <style>{`::selection { background: rgba(234,179,8,.35); }`}</style>
      <style>{ADMIN_THEME_STYLES}</style>
      {theme === "dark" ? (
        <>
          <div className="fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.13),transparent_34%),linear-gradient(180deg,#050505_0%,#090909_45%,#000_100%)]" />
          <div className="fixed inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,.14)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.14)_1px,transparent_1px)] [background-size:42px_42px]" />
        </>
      ) : (
        <>
          <div className="fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(234,179,8,0.06),transparent_40%),linear-gradient(180deg,#ffffff_0%,#f8fafc_50%,#f1f5f9_100%)]" />
          <div className="fixed inset-0 opacity-[0.03] [background-image:linear-gradient(rgba(0,0,0,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,.12)_1px,transparent_1px)] [background-size:42px_42px]" />
        </>
      )}

      <div className="relative z-10 flex min-h-screen">
        <EmployeeSidebar
          activeKey={activeMenu}
          setActiveKey={setActiveMenu}
          navigationGroups={navigationGroups}
          currentEmployee={currentEmployee}
          handleLogout={handleLogout}
        />

        <main className="min-w-0 flex-1 overflow-x-hidden p-4 pb-24 lg:p-6 xl:p-8 xl:pb-8">
          <EmployeeHeader
            currentEmployee={currentEmployee}
            activeLabel={activeLabel}
            theme={theme}
            toggleTheme={toggleTheme}
            unreadMessageCount={unreadMessageCount}
          />

          <EmployeeMobileMenu
            activeKey={activeMenu}
            setActiveKey={setActiveMenu}
            navigationGroups={navigationGroups}
          />

          {errorMessage && (
            <div className="mb-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-2.5 text-xs font-semibold text-red-300">
              {errorMessage}
            </div>
          )}

          {activeMenu === "dashboard" && (
            <DashboardContent
              publishers={publishers}
              currentEmployee={currentEmployee}
              performanceData={employeePerformance}
              previousPerformanceData={previousEmployeePerformance}
              performanceLoading={employeePerformanceLoading}
              performanceError={employeePerformanceError}
              reportStartDate={reportStartDate}
              reportEndDate={reportEndDate}
              setReportStartDate={setReportStartDate}
              setReportEndDate={setReportEndDate}
              targetLiveHours={targetLiveHours}
              realizedLiveHours={realizedLiveHours}
              targetDiamonds={targetDiamonds}
              realizedDiamonds={realizedDiamonds}
              targetPublishCount={targetPublishCount}
              realizedPublishCount={realizedPublishCount}
              lastSevenActiveCount={lastSevenActiveCount}
              lastSevenDetailRows={lastSevenDetailRows}
              showInactivePopup={showInactivePopup}
              setShowInactivePopup={setShowInactivePopup}
              topLiveRows={topLiveRows}
              topDiamondRows={topDiamondRows}
              lowLiveRows={lowLiveRows}
              lowDiamondRows={lowDiamondRows}
              noLiveRows={noLiveRows}
            />
          )}

          {activeMenu === "publisher_list" && (
            <PublisherListContent
              rows={publishers}
              managers={managerRows}
              loading={loading}
              error={errorMessage}
              onReload={loadPublishers}
              onPassword={(target) => setPasswordModal({ open: true, target })}
              canEdit={false}
              canDelete={false}
              currentAdmin={currentEmployee}
            />
          )}

          {activeMenu === "manager_messages" && (
            <PublisherMessagesSection
              currentEmployee={currentEmployee}
              publishers={publishers}
              messages={employeeMessages}
              setMessages={setEmployeeMessages}
              reloadMessages={loadEmployeeMessages}
              themeMode={theme}
            />
          )}

          {activeMenu === "publisher_content" && (
            <PublisherEyeExperienceContent
              publisherRows={publishers}
              currentAdmin={currentEmployee}
            />
          )}

          {activeMenu === "scout_dashboard" && (
            <ScoutCenterPage
              embedded
              theme={theme}
              section="pool"
              currentAdmin={currentEmployee}
              publisherRows={publishers}
              managerRows={managerRows}
              onReload={loadPublishers}
            />
          )}

          {activeMenu === "scout_followup" && (
            <ScoutCenterPage
              embedded
              theme={theme}
              section="followup"
              currentAdmin={currentEmployee}
              publisherRows={publishers}
              managerRows={managerRows}
              onReload={loadPublishers}
            />
          )}

          {activeMenu === "scout_publisher_tracking" && (
            <PublisherTrackingContent
              publisherRows={publishers}
              managers={managerRows}
              loading={loading}
              onReload={loadPublishers}
              currentAdmin={currentEmployee}
            />
          )}

          {activeMenu === "scout_reports" && (
            <PublisherReportsManagementContent
              publisherRows={publishers}
              managers={managerRows}
              loading={loading}
              onReload={loadPublishers}
              currentAdmin={currentEmployee}
            />
          )}

          {![
            "dashboard",
            "publisher_list",
            "manager_messages",
            "publisher_content",
            "scout_dashboard",
            "scout_followup",
            "scout_publisher_tracking",
            "scout_reports",
          ].includes(activeMenu) && <MenuPlaceholder activeMenu={activeMenu} />}
          
          <PasswordModal
            open={passwordModal.open}
            target={passwordModal.target}
            form={passwordForm}
            saving={passwordSaving}
            onChange={(e) => setPasswordForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))}
            onClose={() => setPasswordModal({ open: false, target: null })}
            onSubmit={handlePasswordSubmit}
          />
        </main>
      </div>
    </div>
  );
}


function EmployeeGiftDeliverySection({ currentEmployee = {}, publishers = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiNote, setApiNote] = useState("");
  const [filter, setFilter] = useState("pending");

  const publisherMap = useMemo(() => {
    const map = {};
    publishers.forEach((publisher) => {
      map[String(publisher.username || "")] = publisher;
      map[String(publisher.hiddenId || "")] = publisher;
    });
    return map;
  }, [publishers]);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setApiNote("");
      const managerUsername = currentEmployee?.username || "";
      const response = await fetch(`${API_GIFT_WINNERS_LIST}?manager_username=${encodeURIComponent(managerUsername)}`, { cache: "no-store" });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Hediye listesi alınamadı.");
      setRows(data.winners || []);
    } catch (err) {
      setRows([]);
      setApiNote(err.message || "Hediye listesi alınamadı.");
    } finally {
      setLoading(false);
    }
  }, [currentEmployee?.username]);

  useEffect(() => { loadRows(); }, [loadRows]);

  async function markSent(row) {
    if (!window.confirm(`${row.publisherName || row.publisher_name || row.publisherUsername || row.publisher_username} için hediye gönderildi olarak işaretlensin mi?`)) return;
    try {
      setLoading(true);
      const response = await fetch(API_GIFT_WINNER_SENT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          winnerId: row.id,
          sentBy: currentEmployee?.username || "manager",
          note: "Yönetici tarafından gönderildi olarak işaretlendi.",
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Gönderildi işaretlenemedi.");
      setApiNote("Hediye gönderildi olarak işaretlendi.");
      await loadRows();
    } catch (err) {
      setApiNote(err.message || "Gönderildi işaretlenemedi.");
    } finally {
      setLoading(false);
    }
  }

  const filteredRows = rows.filter((row) => {
    const status = String(row.deliveryStatus || row.delivery_status || "");
    if (filter === "sent") return status === "sent" || status === "completed";
    if (filter === "planned") return status === "planned";
    if (filter === "pending") return status !== "sent" && status !== "completed";
    return true;
  });

  return (
    <Panel title="Hediye Takibi">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="text-sm leading-6 text-white/55">
          Yayıncıların hediye planı, seçtiği gün/saat ve gönderim durumu bu alanda takip edilir.
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={filter} onChange={(event) => setFilter(event.target.value)} className="rounded-xl border border-yellow-500/20 bg-black/60 px-4 py-3 text-sm text-white outline-none [color-scheme:dark]">
            <option value="pending">Bekleyenler</option>
            <option value="planned">Planlananlar</option>
            <option value="sent">Gönderilenler</option>
            <option value="all">Tümü</option>
          </select>
          <button type="button" onClick={loadRows} className="rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-sm font-black text-yellow-200">Yenile</button>
        </div>
      </div>

      {apiNote && <div className="mb-4 rounded-2xl border border-yellow-500/20 bg-yellow-500/10 p-4 text-sm font-bold text-yellow-100">{apiNote}</div>}
      {loading && <div className="rounded-2xl border border-white/10 bg-black/25 p-5 text-sm text-white/45">Hediye listesi yükleniyor...</div>}

      <div className="overflow-hidden rounded-2xl border border-white/10 bg-black/25">
        <div className="grid grid-cols-[1.2fr_.8fr_.8fr_.7fr_.8fr_.8fr_.8fr] gap-3 bg-white/[0.04] px-4 py-3 text-[10px] font-black uppercase tracking-[0.13em] text-white/35">
          <div>Yayıncı</div><div>Hedef</div><div>Hediye</div><div>Gün</div><div>Saat</div><div>Durum</div><div>İşlem</div>
        </div>
        {filteredRows.map((row) => {
          const status = row.deliveryStatus || row.delivery_status || "published";
          const sent = status === "sent" || status === "completed";
          return (
            <div key={row.id} className="grid grid-cols-[1.2fr_.8fr_.8fr_.7fr_.8fr_.8fr_.8fr] gap-3 border-t border-white/10 px-4 py-3 text-sm text-white/70">
              <div><div className="font-black text-white">{row.publisherName || row.publisher_name || row.publisherUsername || row.publisher_username || "-"}</div><div className="text-xs text-white/35">{row.hiddenId || row.hidden_id || "-"}</div></div>
              <div>{row.rewardLabel || row.reward_label || row.rewardType || row.reward_type || "-"}</div>
              <div className="font-black text-yellow-300">{formatNumber(row.finalGiftAmount || row.final_gift_amount || row.giftAmount || row.gift_amount)} elmas</div>
              <div>{row.preferredDay || row.preferred_day || "-"}</div>
              <div>{row.preferredTime || row.preferred_time || "-"}</div>
              <div>{sent ? "✅ Gönderildi" : status === "planned" ? "Planlandı" : "Bekliyor"}</div>
              <div>
                <button type="button" onClick={() => markSent(row)} disabled={sent || loading} className="rounded-xl border border-yellow-500/25 bg-yellow-500/10 px-3 py-2 text-xs font-black text-yellow-200 disabled:opacity-40">
                  Gönderildi
                </button>
              </div>
            </div>
          );
        })}
        {filteredRows.length === 0 && !loading && <div className="p-5 text-sm text-white/45">Kayıt bulunamadı.</div>}
      </div>
    </Panel>
  );
}


function EmployeeSidebar({ activeKey, setActiveKey, navigationGroups, currentEmployee, handleLogout }) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("wm_employee_sidebar_collapsed") === "true";
  });

  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [openGroups, setOpenGroups] = useState({
    management: true,
    scout: true,
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== "undefined") {
        localStorage.setItem("wm_employee_sidebar_collapsed", String(next));
      }
      return next;
    });
  };

  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === "[") {
        e.preventDefault();
        toggleCollapsed();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <aside
      className={`sticky top-0 hidden h-screen shrink-0 border-r transition-all duration-300 ease-in-out backdrop-blur-xl lg:flex flex-col justify-between z-40 ${
        collapsed ? "w-[76px] p-2.5" : "w-[285px] p-4"
      } border-slate-200 dark:border-white/10 bg-white/95 dark:bg-black/85 shadow-sm`}
    >
      {/* 🚀 ÜST LOGO / BAŞLIK ALANI (WOLF-HEAD.PNG KULLANIMI) */}
      <div className="shrink-0 pb-3 border-b border-slate-200 dark:border-white/10">
        {collapsed ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            title="Kenar çubuğunu genişlet"
            className="flex h-11 w-11 mx-auto items-center justify-center rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.04] hover:bg-slate-100 dark:hover:bg-white/10 transition shadow-sm"
          >
            <img src="/wolf-head.png" alt="Wolf Media" className="h-8 w-8 object-contain" />
          </button>
        ) : (
          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.04] shadow-sm">
                <img src="/wolf-head.png" alt="Wolf Media" className="h-7 w-7 object-contain" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-black text-sm tracking-tight text-slate-900 dark:text-white">WOLF MEDIA</span>
                  <span className="rounded bg-yellow-500/20 border border-yellow-500/30 px-1 py-0.2 text-[9px] font-extrabold text-amber-700 dark:text-yellow-300">
                    PRO
                  </span>
                </div>
                <div className="text-[11px] font-semibold text-slate-500 dark:text-white/40 truncate">
                  TikTok LIVE Backstage
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleCollapsed}
              title="Kenar çubuğunu daralt"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 dark:border-white/10 bg-slate-100 dark:bg-white/[0.04] text-slate-600 dark:text-white/60 hover:bg-slate-200 dark:hover:bg-white/10 transition"
            >
              ◧
            </button>
          </div>
        )}
      </div>

      {/* 📋 MENÜ LİSTESİ */}
      <div className="my-3 flex-1 overflow-y-auto overflow-x-hidden space-y-1.5 pr-0.5 custom-scrollbar">
        {navigationGroups.map((group) => {
          if (group.isDirect) {
            const isDirectActive = activeKey === group.targetKey || activeKey === "dashboard";
            return (
              <div key={group.key} className="relative">
                {collapsed ? (
                  <div
                    onMouseEnter={() => setHoveredGroup(group.key)}
                    onMouseLeave={() => setHoveredGroup(null)}
                    className="relative flex justify-center py-1"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setActiveKey("dashboard");
                        setOpenGroups({ publisher: false, reports: false, support: false, settings: false });
                      }}
                      title={group.title}
                      className={`flex h-11 w-11 items-center justify-center rounded-2xl transition ${
                        isDirectActive
                          ? "bg-amber-500 text-black font-black shadow-md scale-105"
                          : "border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-slate-700 dark:text-white/70 hover:border-yellow-500 hover:text-amber-600"
                      }`}
                    >
                      {group.iconSvg}
                    </button>

                    {hoveredGroup === group.key && (
                      <div className="absolute left-[64px] top-1 z-50 min-w-[170px] rounded-xl border border-slate-200 dark:border-white/10 bg-white/95 dark:bg-[#12141f]/95 p-2.5 shadow-2xl backdrop-blur-xl animate-in fade-in zoom-in-95">
                        <div className="font-bold text-xs text-slate-900 dark:text-white flex items-center justify-between">
                          <span>{group.title}</span>
                          <span className="rounded bg-yellow-500/20 px-1 text-[9px] font-black text-amber-700 dark:text-yellow-300">
                            Canlı
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveKey("dashboard");
                      setOpenGroups({ publisher: false, reports: false, support: false, settings: false });
                    }}
                    className={`flex w-full items-center justify-between rounded-xl border px-3.5 py-2.5 text-left text-xs font-bold transition shadow-sm ${
                      isDirectActive
                        ? "border-yellow-500 bg-yellow-500 text-black font-black shadow-md"
                        : "border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.025] text-slate-800 dark:text-white/80 hover:border-yellow-500/40 hover:bg-slate-50 dark:hover:bg-white/[0.05]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className={isDirectActive ? "text-black" : "text-slate-600 dark:text-white/70"}>
                        {group.iconSvg}
                      </span>
                      <span>{group.title}</span>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                        isDirectActive
                          ? "bg-white text-black shadow-sm"
                          : "border border-yellow-500/30 bg-yellow-500/15 text-amber-700 dark:text-yellow-300"
                      }`}
                    >
                      {group.badge}
                    </span>
                  </button>
                )}
              </div>
            );
          }

          const hasActiveChild = group.items?.some((it) => it.key === activeKey);
          const isOpen = (openGroups[group.groupKey] ?? false) || (activeKey !== "dashboard" && hasActiveChild);

          return (
            <div key={group.groupKey} className="relative">
              {collapsed ? (
                <div
                  onMouseEnter={() => setHoveredGroup(group.groupKey)}
                  onMouseLeave={() => setHoveredGroup(null)}
                  className="relative flex justify-center py-1"
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (group.items?.[0]?.key) {
                        setActiveKey(group.items[0].key);
                      }
                    }}
                    title={group.title}
                    className={`flex h-11 w-11 items-center justify-center rounded-2xl transition ${
                      hasActiveChild
                        ? "bg-gradient-to-br from-yellow-500 to-amber-500 text-black font-black shadow-md scale-105"
                        : "border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] text-slate-700 dark:text-white/70 hover:border-yellow-500 hover:text-amber-600"
                    }`}
                  >
                    {group.iconSvg}
                  </button>

                  {hoveredGroup === group.groupKey && (
                    <div className="absolute left-[64px] top-0 z-50 min-w-[210px] rounded-2xl border border-slate-200 dark:border-white/10 bg-white/95 dark:bg-[#12141f]/95 p-3 shadow-2xl backdrop-blur-2xl animate-in fade-in zoom-in-95">
                      <div className="mb-2 pb-1.5 border-b border-slate-100 dark:border-white/10 font-black text-xs text-slate-900 dark:text-white flex items-center gap-1.5">
                        <span className="text-slate-600 dark:text-white/70">{group.iconSvg}</span>
                        <span>{group.title}</span>
                      </div>
                      <div className="space-y-1">
                        {group.items.map((sub) => (
                          <button
                            key={sub.key}
                            type="button"
                            onClick={() => {
                              setActiveKey(sub.key);
                              setHoveredGroup(null);
                            }}
                            className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
                              activeKey === sub.key
                                ? "bg-amber-500 text-black font-black"
                                : "text-slate-700 dark:text-white/70 hover:bg-slate-100 dark:hover:bg-white/[0.06] hover:text-slate-900 dark:hover:text-white"
                            }`}
                          >
                            <span>{sub.label}</span>
                            {sub.badge && (
                              <span
                                className={`rounded-full px-1.5 py-0.2 text-[9px] font-bold ${
                                  activeKey === sub.key
                                    ? "bg-black/20 text-black"
                                    : "border border-yellow-500/20 bg-yellow-500/15 text-amber-700 dark:text-yellow-300"
                                }`}
                              >
                                {sub.badge}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200/90 dark:border-white/10 bg-white/70 dark:bg-white/[0.02] overflow-hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setOpenGroups((prev) => ({
                        ...prev,
                        [group.groupKey]: !prev[group.groupKey],
                      }));
                    }}
                    className={`flex w-full items-center justify-between px-3.5 py-2.5 text-left text-xs font-bold transition ${
                      hasActiveChild
                        ? "bg-amber-500/10 text-amber-700 dark:text-yellow-300 font-black"
                        : "text-slate-800 dark:text-white/80 hover:bg-slate-50 dark:hover:bg-white/[0.04]"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-slate-600 dark:text-white/70">{group.iconSvg}</span>
                      <span>{group.title}</span>
                    </div>
                    <span className="text-[11px] font-bold text-slate-400 dark:text-white/40">
                      {isOpen ? "▾" : "▸"}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-2 py-1.5 space-y-0.5 bg-slate-50/70 dark:bg-black/20 border-t border-slate-100 dark:border-white/5">
                      {group.items.map((sub) => {
                        const isSubActive = activeKey === sub.key;
                        return (
                          <button
                            key={sub.key}
                            type="button"
                            onClick={() => setActiveKey(sub.key)}
                            className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-xs transition ${
                              isSubActive
                                ? "bg-amber-500 text-black font-black shadow-sm"
                                : "text-slate-700 dark:text-white/60 font-semibold hover:bg-white dark:hover:bg-white/[0.05] hover:text-slate-900 dark:hover:text-white"
                            }`}
                          >
                            <span>{sub.label}</span>
                            {sub.badge && (
                              <span
                                className={`rounded-full px-1.5 py-0.2 text-[9px] font-bold ${
                                  isSubActive
                                    ? "bg-black/20 text-black"
                                    : "border border-yellow-500/20 bg-yellow-500/15 text-amber-700 dark:text-yellow-300"
                                }`}
                              >
                                {sub.badge}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 🚀 ALT KISIM: KULLANICI BİLGİSİ + ÇIKIŞ + DARALT BUTONU */}
      <div className="shrink-0 space-y-2 pt-3 border-t border-slate-200 dark:border-white/10">
        {!collapsed && currentEmployee && (
          <div className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50/70 dark:bg-white/[0.025] p-2.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 font-bold text-xs">
                {currentEmployee?.fullName ? currentEmployee.fullName.slice(0, 2).toUpperCase() : "YM"}
              </div>
              <div className="min-w-0">
                <div className="truncate text-xs font-bold text-slate-800 dark:text-white">
                  {currentEmployee?.fullName || "Yönetici"}
                </div>
                <div className="truncate text-[10px] text-slate-400 dark:text-white/40">
                  {currentEmployee?.username || "yonetici"}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Çıkış Yap"
              className="shrink-0 p-1.5 rounded-lg text-slate-500 dark:text-white/50 hover:text-red-500 hover:bg-red-500/10 transition text-sm font-black"
            >
              ⎋
            </button>
          </div>
        )}

        {collapsed ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            title="Kenar çubuğunu genişlet (Ctrl+[)"
            className="flex h-11 w-full items-center justify-center rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] text-slate-700 dark:text-white/70 hover:bg-slate-100 dark:hover:bg-white/[0.08] transition shadow-sm text-base font-black"
          >
            <span>◨</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={toggleCollapsed}
            title="Kenar çubuğunu daralt (Ctrl+[)"
            className="flex w-full items-center justify-between rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] px-3.5 py-2.5 text-xs font-bold text-slate-700 dark:text-white/70 hover:bg-slate-100 dark:hover:bg-white/[0.08] transition shadow-sm"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm">◧</span>
              <span>Kenar çubuğunu daralt</span>
            </div>
            <span className="text-[10px] text-slate-400 font-semibold">Ctrl+[</span>
          </button>
        )}
      </div>
    </aside>
  );
}

function EmployeeHeader({ currentEmployee, activeLabel, theme, toggleTheme, unreadMessageCount }) {
  return (
    <header className="mb-5 rounded-[1.6rem] border border-slate-200/90 dark:border-white/[0.08] bg-white/80 dark:bg-white/[0.035] p-5 shadow-sm dark:shadow-[0_18px_65px_rgba(0,0,0,.35)] backdrop-blur-xl">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.38em] text-amber-700 dark:text-yellow-300">
            WOLF MEDIA YÖNETİM MERKEZİ
          </div>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900 dark:text-white lg:text-4xl">
            Yönetici Paneli
          </h1>
          <p className="mt-2 text-[13px] leading-6 text-slate-600 dark:text-white/60">
            Merhaba, <span className="font-bold text-slate-900 dark:text-white">{currentEmployee?.fullName || "Yönetici"}</span> — Yayıncı, rapor ve ekip süreçleri tek ekranda yönetilir.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={toggleTheme}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 dark:border-white/20 bg-white dark:bg-[#18181c] px-4 py-2 text-xs font-black text-slate-800 dark:text-white shadow-sm hover:border-yellow-500/50 hover:bg-slate-50 dark:hover:bg-white/[0.12] transition-all active:scale-95"
            title={theme === "dark" ? "Açık temaya geç" : "Koyu temaya geç"}
          >
            <span className="text-sm">{theme === "dark" ? "☀️" : "🌙"}</span>
            <span>{theme === "dark" ? "Açık" : "Koyu"}</span>
          </button>
          {unreadMessageCount > 0 && (
            <div className="inline-flex items-center gap-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-3.5 py-1.5 text-xs font-bold text-amber-700 dark:text-yellow-300">
              🔔 {unreadMessageCount} Yeni Mesaj
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-black/25 px-4 py-3 text-sm text-slate-700 dark:text-white/60">
        Aktif ekran: <span className="font-bold text-amber-700 dark:text-yellow-300">{activeLabel}</span>
      </div>
    </header>
  );
}

function EmployeeMobileMenu({ activeKey, setActiveKey, navigationGroups }) {
  const allItems = [
    { key: "dashboard", label: "Yönetim Özeti" },
    ...navigationGroups.flatMap((g) => (g.items ? g.items : [])),
  ];

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 dark:border-white/10 bg-white/80 dark:bg-black/35 p-3 lg:hidden">
      <select
        value={activeKey}
        onChange={(e) => setActiveKey(e.target.value)}
        className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-black/40 px-4 py-3 text-sm text-slate-900 dark:text-white outline-none [color-scheme:dark]"
      >
        {allItems.map((item) => (
          <option key={item.key} value={item.key}>
            {item.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function LoadingScreen({ text }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-black text-white">
      <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.035] px-6 py-5 text-center shadow-[0_10px_35px_rgba(0,0,0,.35)]">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-yellow-500/25 bg-yellow-500/10 text-yellow-300">WM</div>
        <div className="text-sm text-white/65">{text}</div>
      </div>
    </div>
  );
}

function DashboardContent({
  publishers = [],
  currentEmployee = {},
  performanceData,
  performanceLoading,
  performanceError,
  previousPerformanceData,
  reportStartDate,
  reportEndDate,
  setReportStartDate,
  setReportEndDate,
  targetLiveHours,
  realizedLiveHours,
  targetDiamonds,
  realizedDiamonds,
  targetPublishCount,
  realizedPublishCount,
  lastSevenActiveCount,
  lastSevenDetailRows,
  showInactivePopup,
  setShowInactivePopup,
  topLiveRows,
  topDiamondRows,
  lowLiveRows,
  lowDiamondRows,
  noLiveRows,
}) {
  const summaryDiamonds = performanceData?.summary?.totalDiamonds ?? realizedDiamonds ?? 0;
  const summaryLiveSeconds = performanceData?.summary?.totalLiveSeconds ?? (realizedLiveHours * 3600);
  const summaryLiveCount = performanceData?.summary?.totalLiveCount ?? realizedPublishCount ?? 0;

  const totalHours = Math.floor(summaryLiveSeconds / 3600);
  const totalMinutes = Math.floor((summaryLiveSeconds % 3600) / 60);

  return (
    <>
      <SectionHeader
        eyebrow="Yönetim Özeti"
        title="Genel operasyon görünümü"
        desc="Yayıncı, rapor ve ekip süreçleri tek ekranda özetlenir."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <StatCard
          title="Toplam Yayıncı"
          value={formatNumber(publishers.length)}
          note="Bağlı yayıncı sayısı"
        />
        <StatCard
          title="Sorumlu Yönetici"
          value={currentEmployee?.fullName || currentEmployee?.username || "Yönetici"}
          note="Yönetici hesabı"
        />
        <StatCard
          title="Toplam Elmas"
          value={formatNumber(summaryDiamonds)}
          note="Yayıncı toplam elmas"
        />
        <StatCard
          title="Toplam Süre"
          value={`${totalHours} sa ${totalMinutes} dk`}
          note="Canlı yayın süresi"
        />
        <StatCard
          title="Toplam Yayın"
          value={formatNumber(summaryLiveCount)}
          note="Canlı yayın adedi"
        />
      </div>

      <section className="mt-6">
        <PerformanceGraphPanel    
          publishers={publishers}
          performanceData={performanceData}
          previousPerformanceData={previousPerformanceData}
          performanceLoading={performanceLoading}
          performanceError={performanceError}
          startDate={reportStartDate}
          endDate={reportEndDate}
          setStartDate={setReportStartDate}
          setEndDate={setReportEndDate}
        />
      </section>

      <section className="mt-6">
        <TopListSection
          topLiveRows={topLiveRows}
          topDiamondRows={topDiamondRows}
          lowLiveRows={lowLiveRows}
          lowDiamondRows={lowDiamondRows}
          noLiveRows={noLiveRows}
        />
      </section>
    </>
  );
}

function PerformanceGraphPanel({
  publishers,
  performanceData,
  previousPerformanceData,
  performanceLoading,
  performanceError,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}) {
  const { isDark } = useTheme();
  const [activeDiamondPoint, setActiveDiamondPoint] = useState(null);
  const [selectedPublisherId, setSelectedPublisherId] = useState("all");

  const filterFieldClass = isDark
    ? "h-11 w-full rounded-xl border border-yellow-500/20 bg-black/60 px-3 text-sm text-white outline-none transition focus:border-yellow-400 focus:ring-2 focus:ring-yellow-500/20 placeholder:text-white/35 [color-scheme:dark]"
    : "h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 placeholder:text-slate-400 shadow-sm [color-scheme:light]";

  const quickButtonClass = isDark
    ? "rounded-xl border border-white/10 bg-black/45 px-5 py-3 text-sm font-semibold text-white/70 transition hover:border-yellow-500/35 hover:bg-yellow-500/10 hover:text-yellow-300"
    : "rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-yellow-500 hover:bg-slate-50 hover:text-slate-900 shadow-sm";

  const activeQuickButtonClass = isDark
    ? "rounded-xl border border-yellow-500/40 bg-yellow-500 px-5 py-3 text-sm font-semibold text-black"
    : "rounded-xl border border-yellow-500 bg-yellow-500 px-5 py-3 text-sm font-black text-black shadow-md";

  function getQuickRange(type) {
    const today = new Date();
    const start = new Date(today);

    if (type === "yesterday") {
      start.setDate(today.getDate() - 1);
      return {
        start: toInputDate(start),
        end: toInputDate(start),
      };
    }

    if (type === "last7") {
      start.setDate(today.getDate() - 6);
      return {
        start: toInputDate(start),
        end: toInputDate(today),
      };
    }

    start.setMonth(today.getMonth() - 1);
    return {
      start: toInputDate(start),
      end: toInputDate(today),
    };
  }

  function setQuickRange(type) {
    const range = getQuickRange(type);
    setStartDate(range.start);
    setEndDate(range.end);
  }

  function getQuickButtonClass(type) {
    const range = getQuickRange(type);
    return startDate === range.start && endDate === range.end
      ? activeQuickButtonClass
      : quickButtonClass;
  }

  const selectedPublisher =
    selectedPublisherId === "all"
      ? null
      : publishers.find((item) => String(item.id) === String(selectedPublisherId));

  const selectedHiddenId = selectedPublisher ? getPublisherHiddenId(selectedPublisher) : "";

  const daily = selectedPublisher
    ? (performanceData?.publisherDaily || []).filter(
        (item) => String(item.hiddenId) === String(selectedHiddenId)
      )
    : performanceData?.daily || [];

  const totalLiveCount = selectedPublisher
    ? safeNumber(selectedPublisher.currentPublishCount)
    : safeNumber(performanceData?.summary?.totalLiveCount);

  const totalLiveSeconds = selectedPublisher
    ? safeNumber(selectedPublisher.currentSeconds)
    : safeNumber(performanceData?.summary?.totalLiveSeconds);

  const totalDiamonds = selectedPublisher
    ? safeNumber(selectedPublisher.currentDiamonds)
    : safeNumber(performanceData?.summary?.totalDiamonds);
  
  const previousSummary = previousPerformanceData?.summary || {};

  const previousPublisherPerf = selectedPublisher
    ? (previousPerformanceData?.publishers || []).find(
        (item) => String(item.hiddenId) === String(selectedHiddenId)
      )
    : null;

  const previousTotalLiveCount = selectedPublisher
    ? safeNumber(previousPublisherPerf?.totalLiveCount)
    : safeNumber(previousSummary.totalLiveCount);

  const previousTotalLiveSeconds = selectedPublisher
    ? safeNumber(previousPublisherPerf?.totalLiveSeconds)
    : safeNumber(previousSummary.totalLiveSeconds);

  const previousTotalDiamonds = selectedPublisher
    ? safeNumber(previousPublisherPerf?.totalDiamonds)
    : safeNumber(previousSummary.totalDiamonds);  

  const chartWidth = 1000;
  const chartHeight = 340;
  const chartPadding = { top: 28, right: 34, bottom: 48, left: 78 };
  const chartInnerWidth = chartWidth - chartPadding.left - chartPadding.right;
  const chartInnerHeight = chartHeight - chartPadding.top - chartPadding.bottom;

  const chartMax = Math.max(...daily.map((item) => safeNumber(item.diamonds)), 1);

  const getChartX = (index) =>
    chartPadding.left +
    (daily.length === 1
      ? chartInnerWidth / 2
      : (index * chartInnerWidth) / Math.max(daily.length - 1, 1));

  const getChartY = (value) =>
    chartPadding.top + chartInnerHeight - (safeNumber(value) / chartMax) * chartInnerHeight;

  const linePoints = daily
    .map((item, index) => `${getChartX(index)},${getChartY(item.diamonds)}`)
    .join(" ");

  const areaPoints = `${chartPadding.left},${chartPadding.top + chartInnerHeight} ${linePoints} ${
    chartPadding.left + chartInnerWidth
  },${chartPadding.top + chartInnerHeight}`;

  const yTicks = [
    chartMax,
    Math.round(chartMax * 0.75),
    Math.round(chartMax * 0.5),
    Math.round(chartMax * 0.25),
    0,
  ];

  const xLabelStep = Math.max(Math.ceil(daily.length / 6), 1);

  return (
    <Panel title="Performans Paneli">
      <div className={`mb-5 rounded-[1.5rem] border p-5 transition-colors ${
        isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-slate-50/70"
      }`}>
        <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className={`text-xs font-bold uppercase tracking-[0.32em] ${
              isDark ? "text-yellow-300" : "text-yellow-600"
            }`}>
              Rapor Tarih Aralığı
            </div>
            <div className={`mt-1.5 text-sm ${isDark ? "text-white/50" : "text-slate-600"}`}>
              Performans verileri filtrelenir.
            </div>
          </div>

          <div className={`rounded-full border px-4 py-2 text-xs font-bold ${
            isDark ? "border-yellow-500/20 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
          }`}>
            {selectedPublisher ? selectedPublisher.fullName : "Tüm Yayıncılar"}
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.5fr_1fr]">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setQuickRange("yesterday")}
              className={getQuickButtonClass("yesterday")}
            >
              Dün
            </button>

            <button
              type="button"
              onClick={() => setQuickRange("last7")}
              className={getQuickButtonClass("last7")}
            >
              Son 7 Gün
            </button>

            <button
              type="button"
              onClick={() => setQuickRange("lastMonth")}
              className={getQuickButtonClass("lastMonth")}
            >
              Son Ay
            </button>

            <div className={`flex min-w-[360px] items-center gap-3 rounded-xl border px-4 py-2 ${
              isDark ? "border-yellow-500/25 bg-black/45" : "border-slate-300 bg-white shadow-sm"
            }`}>
              <span className={`text-sm font-bold ${isDark ? "text-yellow-300" : "text-yellow-700"}`}>Tarih Seç</span>

              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className={`h-9 w-[145px] bg-transparent text-sm outline-none ${
                  isDark
                    ? "text-white [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:invert"
                    : "text-slate-900 font-semibold [color-scheme:light] [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                }`}
              />

              <span className={isDark ? "text-white/35" : "text-slate-400"}>—</span>

              <input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className={`h-9 w-[145px] bg-transparent text-sm outline-none ${
                  isDark
                    ? "text-white [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:invert"
                    : "text-slate-900 font-semibold [color-scheme:light] [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                }`}
              />
            </div>
          </div>

          <div className="flex justify-start xl:justify-end">
            <select
              value={selectedPublisherId}
              onChange={(event) => setSelectedPublisherId(event.target.value)}
              className={`${filterFieldClass} max-w-[360px] pr-10`}
            >
              <option value="all">Tüm Yayıncılar</option>
              {publishers.map((publisher) => (
                <option
                  key={publisher.id}
                  value={publisher.id}
                  translate="no"
                  className="notranslate"
                >
                  {publisher.username || publisher.hiddenId || publisher.id}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className={`mb-5 rounded-2xl border p-4 text-sm leading-6 ${
        isDark
          ? "border-yellow-500/20 bg-yellow-500/10 text-yellow-100"
          : "border-yellow-200 bg-yellow-50/80 text-yellow-900 font-semibold shadow-sm"
      }`}>
        {selectedPublisher ? `${selectedPublisher.fullName} verileri` : "Tüm bağlı yayıncı verileri"} •{" "}
        {formatDateForDisplay(startDate)} - {formatDateForDisplay(endDate)} tarih aralığı.
      </div>

      {performanceLoading && (
        <div className={`rounded-2xl border p-5 text-sm ${
          isDark ? "border-yellow-500/15 bg-black/30 text-white/55" : "border-slate-200 bg-slate-50 text-slate-600"
        }`}>
          Performans verileri yükleniyor...
        </div>
      )}

      {performanceError && (
        <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-5 text-sm text-red-200">
          {performanceError}
        </div>
      )}

      {!performanceLoading && !performanceError && (
        <>
          <div className="grid gap-4 xl:grid-cols-3">
            <InfoBox
              title="Toplam Yayın"
              value={formatNumber(totalLiveCount)}
              note="Seçili dönem yayın adedi"
              trend={
                <CompareNote
                  current={totalLiveCount}
                  previous={previousTotalLiveCount}
                />
              }
            />

            <InfoBox
              title="Toplam Süre"
              value={formatDurationFromSeconds(totalLiveSeconds)}
              note="Seçili dönem yayın süresi"
              trend={
                <CompareNote
                  current={totalLiveSeconds}
                  previous={previousTotalLiveSeconds}
                  formatter={formatDurationFromSeconds}
                />
              }
            />

            <InfoBox
              title="Toplam Elmas"
              value={formatNumber(totalDiamonds)}
              note="Seçili dönem elmas"
              trend={
                <CompareNote
                  current={totalDiamonds}
                  previous={previousTotalDiamonds}
                />
              }
            />
          </div>

          <div className={`mt-5 overflow-hidden rounded-[1.8rem] border p-5 transition-colors ${
            isDark
              ? "border-yellow-500/20 bg-black/40 shadow-[0_20px_80px_rgba(0,0,0,.45)]"
              : "border-slate-200/90 bg-white shadow-[0_4px_25px_rgba(0,0,0,0.03)]"
          }`}>
            <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className={`text-lg font-black ${isDark ? "text-white" : "text-slate-900"}`}>Elmas Trend Grafiği</div>
                <div className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>
                  Günlük performans verilerine göre çizilir.
                </div>
              </div>

              <div className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] ${
                isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
              }`}>
                Canlı Rapor
              </div>
            </div>

            {daily.length === 0 ? (
              <div className={`rounded-2xl border p-6 text-sm ${
                isDark ? "border-white/10 bg-black/30 text-white/45" : "border-slate-200 bg-slate-50 text-slate-500"
              }`}>
                Seçili tarih aralığında grafik verisi bulunamadı.
              </div>
            ) : (
              <div className={`relative overflow-hidden rounded-[1.4rem] border p-4 ${
                isDark
                  ? "border-yellow-500/25 bg-[radial-gradient(circle_at_top_right,rgba(234,179,8,.18),transparent_28%),linear-gradient(180deg,rgba(255,255,255,.045),rgba(0,0,0,.35))]"
                  : "border-slate-200 bg-slate-50/50"
              }`}>
                <svg
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  className="h-[360px] w-full"
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient id="employeeDiamondArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgb(234,179,8)" stopOpacity={isDark ? "0.42" : "0.35"} />
                      <stop offset="55%" stopColor="rgb(234,179,8)" stopOpacity={isDark ? "0.12" : "0.10"} />
                      <stop offset="100%" stopColor="rgb(234,179,8)" stopOpacity="0.01" />
                    </linearGradient>

                    <linearGradient id="employeeDiamondLine" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="rgb(161,98,7)" />
                      <stop offset="45%" stopColor="rgb(250,204,21)" />
                      <stop offset="100%" stopColor="rgb(253,224,71)" />
                    </linearGradient>
                  </defs>

                  {yTicks.map((tick) => {
                    const y = getChartY(tick);

                    return (
                      <g key={tick}>
                        <line
                          x1={chartPadding.left}
                          y1={y}
                          x2={chartPadding.left + chartInnerWidth}
                          y2={y}
                          stroke={isDark ? "rgba(234,179,8,0.16)" : "rgba(0,0,0,0.06)"}
                          strokeDasharray="5 9"
                        />

                        <text
                          x={18}
                          y={y + 4}
                          className={`${isDark ? "fill-yellow-300/55" : "fill-slate-600"} text-[11px] font-bold`}
                        >
                          {formatNumber(tick)}
                        </text>
                      </g>
                    );
                  })}

                  {daily.map((item, index) => {
                    if (index % xLabelStep !== 0 && index !== daily.length - 1) {
                      return null;
                    }

                    const x = getChartX(index);

                    return (
                      <text
                        key={`${item.date}-${index}`}
                        x={x}
                        y={chartHeight - 16}
                        textAnchor="middle"
                        className={`${isDark ? "fill-yellow-300/55" : "fill-slate-600"} text-[11px] font-bold`}
                      >
                        {formatDateForDisplay(item.date).slice(0, 5)}
                      </text>
                    );
                  })}

                  <polygon
                    points={areaPoints}
                    fill="url(#employeeDiamondArea)"
                  />

                  <polyline
                    fill="none"
                    stroke="url(#employeeDiamondLine)"
                    strokeWidth={4}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={linePoints}
                  />

                  {daily.map((item, index) => {
                    const cx = getChartX(index);
                    const cy = getChartY(item.diamonds);

                    return (
                      <g key={`${item.date}-point-${index}`}>
                        <circle
                          cx={cx}
                          cy={cy}
                          r={6}
                          className="fill-yellow-400 stroke-black stroke-2 transition hover:r-8 hover:fill-white"
                          onMouseEnter={() =>
                            setActiveDiamondPoint({
                              x: cx,
                              y: cy,
                              dateText: formatDateForDisplay(item.date),
                              value: item.diamonds,
                            })
                          }
                          onMouseLeave={() => setActiveDiamondPoint(null)}
                        />
                      </g>
                    );
                  })}

                  <SvgDiamondTooltip point={activeDiamondPoint} chartWidth={chartWidth} />
                </svg>
              </div>
            )}
          </div>
        </>
      )}
    </Panel>
  );
}

function FullPublishersSection({ filteredPublishers, publisherSearch, setPublisherSearch }) {
  return (
    <Panel title="Yayıncılar">
      <PublisherListPanel filteredPublishers={filteredPublishers} publisherSearch={publisherSearch} setPublisherSearch={setPublisherSearch} insideOnly />
    </Panel>
  );
}

function PublisherListPanel({ filteredPublishers, publisherSearch, setPublisherSearch, insideOnly = false }) {
  const { isDark } = useTheme();
  const content = (
    <>
      <div className="mb-4 flex gap-3">
        <div className="relative min-w-0 flex-1">
          <span className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? "text-white/45" : "text-slate-400"}`}>⌕</span>
          <input
            value={publisherSearch}
            onChange={(event) => setPublisherSearch(event.target.value)}
            placeholder="Yayıncı adı, kullanıcı adı veya Hidden ID ara..."
            className="form-input pl-11"
          />
        </div>
        <button
          type="button"
          className={`rounded-2xl px-6 py-3 font-bold transition ${
            isDark
              ? "border border-yellow-500/35 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500 hover:text-black"
              : "bg-yellow-500 text-black font-black hover:bg-yellow-400 shadow-md"
          }`}
        >
          Filtrele
        </button>
      </div>

      <div className={`${insideOnly ? "h-[620px]" : "h-[405px]"} space-y-3 overflow-y-auto pr-2`}>
        {filteredPublishers.map((user) => {
          const tierInfo = getPublisherTierInfo(user);
          const tierColor = tierInfo.badgeClass;
          const isActive = user.status === "Aktif";

          return (
            <button
              key={user.id}
              type="button"
              className={`grid w-full grid-cols-[3.2rem_1.25fr_.8fr_.55fr_.5fr_.45fr] items-center gap-4 rounded-2xl border px-5 py-4 text-left transition ${
                isDark
                  ? `hover:border-yellow-500/25 hover:bg-yellow-500/[0.04] ${tierInfo.rowClass}`
                  : "border-slate-200/90 bg-white hover:border-yellow-500/60 hover:bg-yellow-50/40 shadow-sm"
              }`}
            >
              <div
                className={`flex h-11 w-11 items-center justify-center rounded-full border text-base font-black ${
                  isDark ? tierColor : "border-slate-300 bg-slate-100 text-slate-800"
                }`}
              >
                {user.code}
              </div>
              <div className="min-w-0">
                <PublisherNameWithTier
                  publisher={user}
                  label={user.fullName}
                  nameClassName="text-base font-black"
                />
                <div className={`mt-1 truncate text-sm ${isDark ? "text-white/55" : "text-slate-500 font-medium"}`}>
                  @{user.username} <span className={isDark ? "text-white/25" : "text-slate-300"}>•</span> Hidden ID: {user.hiddenId}
                </div>
              </div>
              <div className={`text-sm font-bold ${isDark ? "text-white/85" : "text-slate-800"}`}>
                {user.phone}
              </div>
              <div><PublisherTierBadge publisher={user} /></div>
              <div>
                <span
                  className={`rounded-full border px-2.5 py-1 text-[10px] font-bold ${
                    getPublisherAgencyTrackingStatus(user) === "Aktif"
                      ? isDark
                        ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-200"
                        : "border-emerald-500/30 bg-emerald-50 text-emerald-700"
                      : isDark
                      ? "border-white/15 bg-white/[0.04] text-white/55"
                      : "border-slate-200 bg-slate-100 text-slate-600"
                  }`}
                >
                  Ajans: {getPublisherAgencyTrackingStatus(user)}
                </span>
              </div>
              <div className={`text-sm font-black ${isActive ? "text-green-500" : isDark ? "text-white/55" : "text-slate-400"}`}>
                ● {user.status}
              </div>
            </button>
          );
        })}

        {filteredPublishers.length === 0 && (
          <div className={`rounded-2xl border p-5 text-sm ${
            isDark ? "border-white/10 bg-black/25 text-white/45" : "border-slate-200 bg-white text-slate-500"
          }`}>
            Bu kullanıcıya atanmış yayıncı bulunmuyor.
          </div>
        )}
      </div>
    </>
  );

  if (insideOnly) return content;
  return <Panel title="Bağlı Yayıncılar">{content}</Panel>;
}

function FaqArchiveManagementSection({ currentUser, mode = "manager" }) {
  const { isDark } = useTheme();
  const emptyForm = {
    question: "",
    answer: "",
    category: "Genel",
    targetRole: "publisher",
    status: "Aktif",
    sortOrder: 0,
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");

  async function loadFaqs() {
    try {
      setLoading(true);

      const response = await fetch(API_FAQ_LIST, { cache: "no-store" });
      const data = await response.json();

      if (!data.success) {
        alert(data.message || "SS arşivi alınamadı.");
        return;
      }

      setRows(data.faqs || []);
    } catch (err) {
      console.error(err);
      alert("SS arşivi alınırken bağlantı hatası oluştu.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadFaqs();
  }, []);

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
  }

  function handleEdit(item) {
    setEditingId(item.id);
    setForm({
      question: item.question || item.q || "",
      answer: item.answer || item.a || "",
      category: item.category || "Genel",
      targetRole: item.targetRole || "publisher",
      status: item.status || "Aktif",
      sortOrder: item.sortOrder || 0,
    });
  }

  async function handleSaveFaq() {
    if (!form.question.trim() || !form.answer.trim()) {
      alert("Soru ve cevap zorunlu.");
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(API_FAQ_SAVE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: editingId,
          ...form,
          createdBy: currentUser?.username || "-",
          createdByRole: mode,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.message || "SS kaydı kaydedilemedi.");
        return;
      }

      resetForm();
      await loadFaqs();
      alert("SS kaydı kaydedildi.");
    } catch (err) {
      console.error(err);
      alert("SS kaydı kaydedilirken bağlantı hatası oluştu.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteFaq(item) {
    if (!window.confirm(`${item.question || item.q} kaydını kaldırmak istiyor musunuz?`)) return;

    try {
      setSaving(true);

      const response = await fetch(API_FAQ_DELETE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: item.id }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.message || "SS kaydı kaldırılamadı.");
        return;
      }

      await loadFaqs();
      alert("SS kaydı kaldırıldı.");
    } catch (err) {
      console.error(err);
      alert("SS kaydı kaldırılırken bağlantı hatası oluştu.");
    } finally {
      setSaving(false);
    }
  }

  const activeRows = rows.filter((item) => item.status !== "Silindi");
  const filteredRows = activeRows.filter((item) =>
    `${item.question || item.q} ${item.answer || item.a} ${item.category}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-white/10 bg-white/[0.035] p-5 shadow-[0_18px_60px_rgba(0,0,0,.25)]">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.34em] text-yellow-300">
              SS Arşivi Yönetimi
            </div>
            <h2 className={`mt-2 text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
              Sık Sorulan Sorular
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-600"}`}>
              Buradan eklenen aktif kayıtlar yayıncı panelindeki SS arşivinde görüntülenir.
            </p>
          </div>

          <button
            type="button"
            onClick={loadFaqs}
            className={`rounded-2xl border px-5 py-3 text-sm font-bold transition ${
              isDark
                ? "border-white/10 bg-white/[0.035] text-white/70 hover:border-yellow-500/30 hover:text-yellow-300"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900 shadow-sm"
            }`}
          >
            Yenile
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <InfoBox title="Toplam SS" value={activeRows.length} note="Silinmemiş kayıt" />
          <InfoBox title="Aktif" value={activeRows.filter((i) => i.status === "Aktif").length} note="Yayıncıya açık" />
          <InfoBox title="Pasif" value={activeRows.filter((i) => i.status === "Pasif").length} note="Arşivde bekleyen" />
          <InfoBox title="Durum" value={loading ? "Yükleniyor" : "Hazır"} note="API veri durumu" />
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[1fr_.75fr]">
        <section className={`rounded-[1.8rem] border p-5 transition-colors ${
          isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
        }`}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="SS ara..."
            className="form-input mb-4"
          />

          <div className="space-y-3">
            {filteredRows.length === 0 && (
              <div className={`rounded-2xl border p-4 text-sm ${
                isDark ? "border-white/10 bg-white/[0.025] text-white/45" : "border-slate-200 bg-slate-50 text-slate-500"
              }`}>
                SS kaydı bulunamadı.
              </div>
            )}

            {filteredRows.map((item) => (
              <div
                key={item.id}
                className={`rounded-2xl border p-4 transition ${
                  isDark ? "border-white/10 bg-white/[0.025]" : "border-slate-200 bg-slate-50 hover:border-amber-400 shadow-sm"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {item.question || item.q}
                    </div>
                    <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/55" : "text-slate-600"}`}>
                      {item.answer || item.a}
                    </p>
                  </div>

                  <span className={`rounded-full border px-3 py-1 text-xs font-bold ${
                    isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
                  }`}>
                    {item.status}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className={`rounded-xl px-4 py-2 text-xs font-bold transition ${
                      isDark
                        ? "border border-yellow-500/25 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500 hover:text-black"
                        : "border border-yellow-400 bg-yellow-100 text-yellow-900 hover:bg-yellow-500 hover:text-black"
                    }`}
                  >
                    Düzenle
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteFaq(item)}
                    className={`rounded-xl px-4 py-2 text-xs font-bold transition ${
                      isDark
                        ? "border border-red-500/25 bg-red-500/10 text-red-300 hover:bg-red-500 hover:text-white"
                        : "border border-red-200 bg-red-50 text-red-700 hover:bg-red-500 hover:text-white"
                    }`}
                  >
                    Kaldır
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={`rounded-[1.8rem] border p-5 transition-colors ${
          isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
        }`}>
          <div className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
            {editingId ? "SS Kaydını Düzenle" : "Yeni SS Kaydı"}
          </div>

          <div className="mt-5 space-y-3">
            <input value={form.question} onChange={(e) => setForm((p) => ({ ...p, question: e.target.value }))} placeholder="Soru başlığı" className="form-input" />
            <textarea value={form.answer} onChange={(e) => setForm((p) => ({ ...p, answer: e.target.value }))} placeholder="Cevap metni" className="form-textarea min-h-[130px]" />
            <input value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} placeholder="Kategori" className="form-input" />

            <select value={form.targetRole} onChange={(e) => setForm((p) => ({ ...p, targetRole: e.target.value }))} className="form-input">
              <option value="publisher">Yayıncı Paneli</option>
              <option value="manager">Yönetici Paneli</option>
              <option value="all">Tüm Paneller</option>
            </select>

            <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} className="form-input">
              <option>Aktif</option>
              <option>Pasif</option>
              <option>Taslak</option>
            </select>

            <input type="number" value={form.sortOrder} onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))} placeholder="Sıra" className="form-input" />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleSaveFaq}
                disabled={saving}
                className="rounded-2xl bg-yellow-500 px-5 py-3 text-sm font-bold text-black hover:bg-yellow-400 disabled:opacity-60"
              >
                {saving ? "Kaydediliyor..." : editingId ? "Güncelle" : "Yayınla"}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className={`rounded-2xl border px-5 py-3 text-sm font-semibold transition ${
                    isDark ? "border-white/10 bg-white/[0.035] text-white/65 hover:text-yellow-300" : "border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  Vazgeç
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function FaqSection({ filteredFaqs, faqSearch, setFaqSearch, showAddFaq, setShowAddFaq, newFaqQuestion, setNewFaqQuestion, newFaqAnswer, setNewFaqAnswer, handleAddFaq, handleDeleteFaq, large = false }) {
  const { isDark } = useTheme();
  return (
    <Panel title="Sık Sorulan Sorular Yönetimi">
      <div className="mb-4 flex gap-3">
        <input value={faqSearch} onChange={(event) => setFaqSearch(event.target.value)} placeholder="Soru veya cevap ara..." className="form-input" />
        <button
          type="button"
          onClick={() => setShowAddFaq((prev) => !prev)}
          className={`rounded-2xl px-5 py-3 font-bold transition ${
            isDark ? "border border-yellow-500/30 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500 hover:text-black" : "bg-yellow-500 text-black font-black hover:bg-yellow-400 shadow-md"
          }`}
        >
          + Soru Ekle
        </button>
      </div>

      {showAddFaq && (
        <div className={`mb-4 rounded-2xl border p-4 ${
          isDark ? "border-yellow-500/20 bg-yellow-500/10" : "border-yellow-200 bg-yellow-50/90 shadow-sm"
        }`}>
          <input value={newFaqQuestion} onChange={(event) => setNewFaqQuestion(event.target.value)} placeholder="Soru başlığı" className="form-input mb-3" />
          <textarea value={newFaqAnswer} onChange={(event) => setNewFaqAnswer(event.target.value)} placeholder="Cevap metni" className="form-textarea mb-3 h-24" />
          <button type="button" onClick={handleAddFaq} className="w-full rounded-2xl bg-yellow-500 px-5 py-3 font-bold text-black transition hover:bg-yellow-400 shadow-md">
            Sık Sorulan Sorulara Ekle
          </button>
        </div>
      )}

      <div className={`${large ? "h-[620px]" : "h-[395px]"} space-y-3 overflow-y-auto pr-1`}>
        {filteredFaqs.map((item) => (
          <div
            key={item.id}
            className={`rounded-2xl border p-4 transition ${
              isDark ? "border-white/10 bg-black/25" : "border-slate-200 bg-slate-50/80 shadow-sm"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{item.question}</div>
                <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/60" : "text-slate-600"}`}>{item.answer}</p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-2">
                <span className={`rounded-full border px-3 py-1 text-xs font-bold ${
                  isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
                }`}>
                  {item.status}
                </span>
                <button type="button" onClick={() => handleDeleteFaq(item.id)} className="text-xs font-bold text-red-500 hover:text-red-700">
                  Sil
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function TopListSection({
  topLiveRows,
  topDiamondRows,
  lowLiveRows,
  lowDiamondRows,
  noLiveRows,
}) {
  const { isDark } = useTheme();
  return (
    <Panel title="Ekip Top Listesi">
      <div className={`mb-4 rounded-2xl border p-4 text-sm leading-6 ${
        isDark
          ? "border-yellow-500/20 bg-yellow-500/10 text-yellow-100"
          : "border-yellow-200 bg-yellow-50/80 text-yellow-900 font-semibold shadow-sm"
      }`}>
        Bu alan yalnızca giriş yapan yöneticinin bağlı yayıncılarını ve seçili
        tarih aralığını kullanır. Sıfır performanslı yayıncılar “en az”
        listelerine alınmaz; ayrı yayın açmayanlar tablosunda gösterilir.
      </div>

      <PerformanceTopLists
        topLiveRows={topLiveRows}
        topDiamondRows={topDiamondRows}
        lowLiveRows={lowLiveRows}
        lowDiamondRows={lowDiamondRows}
        noLiveRows={noLiveRows}
      />
    </Panel>
  );
}

function PerformanceTopLists({
  topLiveRows,
  topDiamondRows,
  lowLiveRows,
  lowDiamondRows,
  noLiveRows,
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-2">
        <TopListTable title="En Çok Yayın Açan Top 10" rows={topLiveRows} />
        <TopListTable title="En Çok Kazanan Top 10" rows={topDiamondRows} />
        <TopListTable title="En Az Yayın Açan Top 10" rows={lowLiveRows} />
        <TopListTable title="En Az Kazanan Top 10" rows={lowDiamondRows} />
      </div>

      <NoLivePublisherList rows={noLiveRows} />
    </div>
  );
}

function TopListTable({ title, rows = [] }) {
  const { isDark } = useTheme();
  return (
    <div className={`rounded-2xl border p-4 transition-colors ${
      isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
    }`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className={`text-base font-black ${isDark ? "text-white" : "text-slate-900"}`}>{title}</div>

        <span className={`rounded-full border px-3 py-1 text-[11px] font-black ${
          isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
        }`}>
          Top 10
        </span>
      </div>

      <div className="overflow-x-auto pb-1 [scrollbar-color:rgba(234,179,8,.75)_rgba(255,255,255,.06)] [scrollbar-width:thin]">
        <div className="min-w-[720px] space-y-2">
          <div className={`grid grid-cols-[1.45fr_.65fr_.8fr_.8fr] gap-3 px-4 pb-2 text-[10px] font-black uppercase tracking-[0.16em] ${
            isDark ? "text-white/35" : "text-slate-500"
          }`}>
            <div>Kullanıcı Adı</div>
            <div>Yayın</div>
            <div>Süre</div>
            <div>Elmas</div>
          </div>

          <div className="max-h-[610px] overflow-y-auto pr-1 [scrollbar-color:rgba(234,179,8,.75)_rgba(255,255,255,.06)] [scrollbar-width:thin]">
            <div className="space-y-2">
              {rows.map((item, index) => (
                <div
                  key={`${item.id}-${index}-${title}`}
                  className={`grid min-h-[66px] grid-cols-[1.45fr_.65fr_.8fr_.8fr] items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition ${
                    isDark
                      ? "border-white/10 bg-white/[0.035] text-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,.04)] hover:border-yellow-500/30 hover:bg-yellow-500/[0.055]"
                      : "border-slate-200 bg-slate-50/80 text-slate-800 shadow-sm hover:border-amber-400 hover:bg-yellow-50/50"
                  }`}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-xs font-black ${
                      isDark ? "border-yellow-500/35 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
                    }`}>
                      {index + 1}
                    </span>

                    <div className="min-w-0">
                      <div translate="no" className="notranslate">
                        <PublisherNameWithTier
                          publisher={item}
                          diamonds={item.currentDiamonds}
                          label={item.username || "-"}
                          nameClassName="text-sm font-black"
                        />
                      </div>

                      <div className="mt-1">
                        <PublisherTierBadge
                          publisher={item}
                          diamonds={item.currentDiamonds}
                          compact
                        />
                      </div>
                    </div>
                  </div>

                  <div className={`font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                    {formatNumber(item.currentPublishCount)}
                  </div>

                  <div className={`font-black ${isDark ? "text-white/75" : "text-slate-700"}`}>
                    {formatDurationFromSeconds(item.currentSeconds)}
                  </div>

                  <div className={`font-black ${isDark ? "text-yellow-300" : "text-yellow-700"}`}>
                    {formatNumber(item.currentDiamonds)}
                  </div>
                </div>
              ))}

              {rows.length === 0 && (
                <div className={`rounded-2xl border p-4 text-sm ${
                  isDark ? "border-white/10 bg-white/[0.035] text-white/45" : "border-slate-200 bg-slate-50 text-slate-500"
                }`}>
                  Veri bulunamadı.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NoLivePublisherList({ rows = [] }) {
  const { isDark } = useTheme();
  return (
    <div className={`rounded-2xl border p-4 ${
      isDark ? "border-red-500/20 bg-red-500/10" : "border-red-200 bg-red-50/80 shadow-sm"
    }`}>
      <div className={`mb-1 text-base font-black ${isDark ? "text-white" : "text-red-950"}`}>
        Seçili Tarihte Yayın Açmayan Yayıncılar
      </div>

      <div className={`mb-4 text-xs leading-5 ${isDark ? "text-white/45" : "text-red-900/70 font-medium"}`}>
        Seçilen tarih aralığında yayın süresi ve yayın adedi sıfır olan bağlı
        yayıncılar listelenir. İlk 10 kayıt görünür, diğer kayıtlar aşağı
        kaydırılarak görüntülenir.
      </div>

      <div className={`overflow-x-auto rounded-2xl border [scrollbar-color:rgba(234,179,8,.75)_rgba(255,255,255,.06)] [scrollbar-width:thin] ${
        isDark ? "border-white/10 bg-black/25" : "border-red-200 bg-white shadow-sm"
      }`}>
        <div className="min-w-[760px]">
          <div className={`grid grid-cols-[1.1fr_.7fr_.8fr_.8fr] gap-3 px-4 py-3 text-[11px] font-black uppercase tracking-[0.14em] ${
            isDark ? "bg-white/[0.04] text-white/38" : "bg-red-50/60 text-red-900/70"
          }`}>
            <div>Kullanıcı Adı</div>
            <div>Kıdem</div>
            <div>Son Yayın Tarihi</div>
            <div>Ort. Elmas</div>
          </div>

          <div className="max-h-[560px] overflow-y-auto [scrollbar-color:rgba(234,179,8,.75)_rgba(255,255,255,.06)] [scrollbar-width:thin]">
            {rows.map((item, index) => (
              <div
                key={`${item.id}-${index}-no-live`}
                className={`grid min-h-[56px] grid-cols-[1.1fr_.7fr_.8fr_.8fr] items-center gap-3 border-t px-4 py-3 text-sm transition ${
                  isDark ? "border-white/10 text-white/68 hover:bg-red-500/[0.04]" : "border-red-100 text-slate-800 hover:bg-red-50/40"
                }`}
              >
                <div className="min-w-0">
                  <div translate="no" className="notranslate">
                    <PublisherNameWithTier
                      publisher={item}
                      diamonds={item.currentDiamonds}
                      label={`${index + 1}. ${item.username || "-"}`}
                      nameClassName="font-black"
                    />
                  </div>
                </div>

                <div className={`font-bold ${isDark ? "text-white/65" : "text-slate-700"}`}>
                  {formatAgencyTenure(item.createdAt)}
                </div>

                <div className={`font-bold ${isDark ? "text-white/65" : "text-slate-700"}`}>
                  {formatLastLiveDate(item.performanceLastLiveDate)}
                </div>

                <div className={`font-black ${isDark ? "text-yellow-300" : "text-yellow-700"}`}>
                  {formatAverageDiamondsPerLive(
                    item.performanceAvgDiamondsPerLive
                  )}
                </div>
              </div>
            ))}

            {rows.length === 0 && (
              <div className={`border-t p-4 text-sm ${isDark ? "border-white/10 text-white/45" : "border-red-100 text-slate-500"}`}>
                Seçili tarihte yayın açmayan yayıncı bulunmuyor.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AcademyManagementSection({ publishers = [], currentUser, mode = "manager" }) {
  const { isDark } = useTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: "",
    type: "Video",
    category: "all",
    videoUrl: "",
    status: "Aktif",
    description: "",
  });

  async function loadAcademy() {
    try {
      setLoading(true);
      const response = await fetch(API_ACADEMY_LIST, { cache: "no-store" });
      const data = await response.json();

      if (!data.success) {
        alert(data.message || "Akademi içerikleri alınamadı.");
        return;
      }

      setRows(data.trainings || []);
    } catch (err) {
      console.error(err);
      alert("Akademi içerikleri alınırken bağlantı hatası oluştu.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAcademy();
  }, []);

  function resetForm() {
    setEditingId(null);
    setForm({
      title: "",
      type: "Video",
      category: "all",
      videoUrl: "",
      status: "Aktif",
      description: "",
    });
  }

  function handleEdit(item) {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      type: item.type || "Video",
      category: item.targetGroup || item.month || "all",
      videoUrl: item.videoUrl || "",
      status: item.status === "Silindi" ? "Pasif" : item.status || "Aktif",
      description: item.desc || "",
    });
  }

  async function handleSaveAcademy() {
    if (!form.title.trim()) {
      alert("Eğitim başlığı zorunlu.");
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(API_ACADEMY_SAVE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: editingId,
          title: form.title.trim(),
          type: form.type,
          category: form.category,
          videoUrl: form.videoUrl.trim(),
          status: form.status,
          description: form.description.trim(),
          createdBy: currentUser?.username || "-",
          createdByRole: mode,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.message || "Eğitim kaydedilemedi.");
        return;
      }

      resetForm();
      await loadAcademy();
      alert("Eğitim kaydedildi.");
    } catch (err) {
      console.error(err);
      alert("Eğitim kaydedilirken bağlantı hatası oluştu.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteAcademy(item) {
    if (!window.confirm(`${item.title} eğitimini kaldırmak istiyor musunuz?`)) return;

    try {
      setSaving(true);

      const response = await fetch(API_ACADEMY_DELETE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: item.id }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.message || "Eğitim kaldırılamadı.");
        return;
      }

      await loadAcademy();
      alert("Eğitim kaldırıldı.");
    } catch (err) {
      console.error(err);
      alert("Eğitim kaldırılırken bağlantı hatası oluştu.");
    } finally {
      setSaving(false);
    }
  }

  const activeRows = rows.filter((item) => item.status !== "Silindi");
  const watchedTotal = activeRows.reduce((sum, item) => sum + safeNumber(item.completedCount), 0);
  const viewTotal = activeRows.reduce((sum, item) => sum + safeNumber(item.viewCount), 0);

  return (
    <div className="space-y-6">
      <section className={`rounded-[2rem] border p-6 transition-colors ${
        isDark
          ? "border-white/10 bg-white/[0.035] shadow-[0_18px_60px_rgba(0,0,0,.25)]"
          : "border-slate-200/90 bg-white shadow-sm"
      }`}>
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className={`text-xs font-bold uppercase tracking-[0.34em] ${
              isDark ? "text-yellow-300" : "text-yellow-600"
            }`}>
              Wolf Akademi Yönetimi
            </div>
            <h2 className={`mt-2 text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
              Eğitim İçerikleri
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-600"}`}>
              Yönetici veya admin tarafından eklenen eğitimler yayıncı panelinde otomatik görünür.
            </p>
          </div>

          <button
            type="button"
            onClick={loadAcademy}
            className={`rounded-2xl border px-5 py-3 text-sm font-bold transition ${
              isDark
                ? "border-white/10 bg-white/[0.035] text-white/70 hover:border-yellow-500/30 hover:text-yellow-300"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900 shadow-sm"
            }`}
          >
            Yenile
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <InfoBox title="Toplam Eğitim" value={activeRows.length} note="Aktif içerik sayısı" />
          <InfoBox title="Bağlı Yayıncı" value={publishers.length} note="Yöneticiye bağlı yayıncı" />
          <InfoBox title="Görüntüleme" value={viewTotal} note="Toplam görüntüleme kaydı" />
          <InfoBox title="Tamamlayan" value={watchedTotal} note="İzlendi olarak işaretlenen" />
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[1fr_.75fr]">
        <section className={`rounded-[1.8rem] border p-5 transition-colors ${
          isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
        }`}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                Yayınlanan Eğitimler
              </div>
              <div className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>
                Video, bilgi notu ve duyuru listesi
              </div>
            </div>
            <span className={`rounded-full border px-3.5 py-1.5 text-xs font-bold ${
              isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
            }`}>
              {loading ? "Yükleniyor" : `${activeRows.length} İçerik`}
            </span>
          </div>

          <div className="space-y-3">
            {activeRows.length === 0 && (
              <div className={`rounded-2xl border p-4 text-sm ${
                isDark ? "border-white/10 bg-white/[0.025] text-white/45" : "border-slate-200 bg-slate-50 text-slate-500"
              }`}>
                Henüz eğitim içeriği bulunmuyor.
              </div>
            )}

            {activeRows.map((item) => (
              <div
                key={item.id}
                className={`rounded-2xl border p-4 transition ${
                  isDark ? "border-white/10 bg-white/[0.025]" : "border-slate-200 bg-slate-50 hover:border-amber-400 shadow-sm"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className={`text-base font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                      {item.title}
                    </div>
                    <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/55" : "text-slate-600"}`}>
                      {item.desc || "Açıklama yok."}
                    </p>
                  </div>

                  <span className={`rounded-full border px-3 py-1 text-xs font-bold ${
                    isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
                  }`}>
                    {item.status || "Aktif"}
                  </span>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  <MiniInfo label="Tür" value={item.type || "-"} />
                  <MiniInfo label="Hedef" value={<TargetGroupBadge value={item.targetGroup || item.month || item.category} compact />} />
                  <MiniInfo label="İzlenme" value={item.viewCount || 0} />
                  <MiniInfo label="Tamamlayan" value={item.completedCount || 0} />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className={`rounded-xl px-4 py-2 text-xs font-bold transition ${
                      isDark
                        ? "border border-yellow-500/25 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500 hover:text-black"
                        : "border border-yellow-400 bg-yellow-100 text-yellow-900 hover:bg-yellow-500 hover:text-black"
                    }`}
                  >
                    Düzenle
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeleteAcademy(item)}
                    className={`rounded-xl px-4 py-2 text-xs font-bold transition ${
                      isDark
                        ? "border border-red-500/25 bg-red-500/10 text-red-300 hover:bg-red-500 hover:text-white"
                        : "border border-red-200 bg-red-50 text-red-700 hover:bg-red-500 hover:text-white"
                    }`}
                  >
                    Kaldır
                  </button>

                  {item.videoUrl && (
                    <a
                      href={item.videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={`rounded-xl border px-4 py-2 text-xs font-bold transition ${
                        isDark
                          ? "border-white/10 bg-white/[0.035] text-white/65 hover:text-yellow-300"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900 shadow-sm"
                      }`}
                    >
                      Videoyu Aç
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={`rounded-[1.8rem] border p-5 transition-colors ${
          isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
        }`}>
          <div className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
            {editingId ? "Akademi İçeriğini Düzenle" : "Yeni Akademi İçeriği"}
          </div>
          <div className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>
            Yayıncı panelinde gösterilecek eğitim içeriği oluştur.
          </div>

          <div className="mt-5 space-y-3">
            <input
              value={form.title}
              onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              placeholder="İçerik başlığı"
              className="form-input"
            />

            <select
              value={form.type}
              onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}
              className="form-input"
            >
              <option>Video</option>
              <option>Bilgi Notu</option>
              <option>Duyuru</option>
              <option>Hedef Hatırlatma</option>
            </select>

            <select
              value={form.category}
              onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
              className="form-input"
            >
              {TARGET_GROUP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <input
              value={form.videoUrl}
              onChange={(event) => setForm((prev) => ({ ...prev, videoUrl: event.target.value }))}
              placeholder="Video / içerik linki"
              className="form-input"
            />

            <select
              value={form.status}
              onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}
              className="form-input"
            >
              <option>Aktif</option>
              <option>Planlandı</option>
              <option>Pasif</option>
            </select>

            <textarea
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Kısa açıklama veya eğitim notu..."
              className="form-textarea min-h-[120px]"
            />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleSaveAcademy}
                disabled={saving}
                className="rounded-2xl bg-yellow-500 px-5 py-3 text-sm font-bold text-black hover:bg-yellow-400 disabled:opacity-60"
              >
                {saving ? "Kaydediliyor..." : editingId ? "Güncelle" : "Yayınla"}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-2xl border border-white/10 bg-white/[0.035] px-5 py-3 text-sm font-semibold text-white/65 hover:text-yellow-300"
                >
                  Vazgeç
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function MiniInfo({ label, value }) {
  const { isDark } = useTheme();
  return (
    <div className={`rounded-2xl border p-3 transition-colors ${
      isDark ? "border-white/10 bg-black/30 text-white" : "border-slate-200 bg-slate-50 text-slate-900"
    }`}>
      <div className={`text-[10px] font-bold uppercase tracking-[0.18em] ${
        isDark ? "text-white/35" : "text-slate-500"
      }`}>
        {label}
      </div>
      <div className={`mt-1 text-sm font-black ${
        isDark ? "text-white" : "text-slate-900"
      }`}>
        {value}
      </div>
    </div>
  );
}

function PublisherMessagesSection({
  currentEmployee,
  publishers = [],
  messages = [],
  setMessages,
  reloadMessages,
  themeMode = "dark",
}) {
  const isDark = themeMode !== "light";
  const [replyText, setReplyText] = useState("");
  const [selectedPublisherKey, setSelectedPublisherKey] = useState("");
  const [selectedTopicKey, setSelectedTopicKey] = useState("");
  const [composerRole, setComposerRole] = useState("manager"); // 'admin' | 'manager' | 'internal'
  const [activePublisherFilter, setActivePublisherFilter] = useState("all"); // 'all' | 'unread' | 'vip'
  const [activeTopicFilter, setActiveTopicFilter] = useState("all"); // 'all' | 'unread'
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewTopicModal, setShowNewTopicModal] = useState(false);
  const [modalPublisherId, setModalPublisherId] = useState("");
  const [modalCategory, setModalCategory] = useState("💎 ELMAS & HEDEF");
  const [modalSubject, setModalSubject] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  function formatMessageDate(value) {
    if (!value) return "-";
    try {
      const date = new Date(String(value).replace(" ", "T"));
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      
      const timeStr = date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
      if (isToday) return `Bugün ${timeStr}`;
      
      return date.toLocaleString("tr-TR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(value);
    }
  }

  function cleanSubject(subject) {
    return String(subject || "Genel Konu")
      .replace(/^yanıt:\s*/i, "")
      .replace(/^re:\s*/i, "")
      .trim();
  }

  const getPublisherKeyFromMessage = useCallback((message) => {
    const senderId = String(message.sender_id || "");
    const receiverId = String(message.receiver_id || "");
    const employeeId = String(currentEmployee?.id || "");

    if (senderId && senderId !== employeeId) return `id:${senderId}`;
    if (receiverId && receiverId !== employeeId) return `id:${receiverId}`;

    if (message.senderUsername && message.senderUsername !== currentEmployee?.username) {
      return `username:${message.senderUsername}`;
    }
    if (message.receiverUsername && message.receiverUsername !== currentEmployee?.username) {
      return `username:${message.receiverUsername}`;
    }

    return "unknown";
  }, [currentEmployee?.id, currentEmployee?.username]);

  const findPublisherByKey = useCallback((key) => {
    if (!key) return null;
    if (key.startsWith("id:")) {
      const id = key.replace("id:", "");
      return publishers.find((p) => String(p.id) === String(id)) || null;
    }
    if (key.startsWith("username:")) {
      const username = key.replace("username:", "");
      return publishers.find((p) => String(p.username) === String(username)) || null;
    }
    return null;
  }, [publishers]);

  // Combine publishers from messages AND assigned publishers
  const groupedPublishers = useMemo(() => {
    const map = {};

    // 1. Önce atanmış tüm yayıncıları haritaya ekle
    publishers.forEach((p) => {
      const key = `id:${p.id}`;
      const diamonds = safeNumber(p.currentDiamonds ?? p.diamonds ?? 0);
      const targetDiamonds = safeNumber(p.targetDiamonds || p.target_diamonds || 0);
      const tierInfo = getPublisherTierInfo(p);

      map[key] = {
        key,
        publisher: p,
        id: p.id,
        name: p.fullName || p.name || p.username || "Yayıncı",
        username: p.username || "-",
        hiddenId: p.hiddenId || "-",
        tier: tierInfo.kademeLabel || "Kademe 1",
        tierGroup: tierInfo.groupLabel || "G0-G3",
        diamonds,
        targetDiamonds,
        status: p.status || "Aktif",
        unreadCount: 0,
        lastDate: p.createdAt || "",
        topics: {},
      };
    });

    // 2. Mesajları yayıncı ve konularına göre yerleştir
    messages.forEach((message) => {
      const pubKey = getPublisherKeyFromMessage(message);
      const matchedPub = findPublisherByKey(pubKey);
      const subject = cleanSubject(message.subject);
      const topicKey = subject.toLowerCase();

      if (!map[pubKey]) {
        const diamonds = safeNumber(matchedPub?.currentDiamonds ?? matchedPub?.diamonds ?? 0);
        const targetDiamonds = safeNumber(matchedPub?.targetDiamonds || matchedPub?.target_diamonds || 0);
        const tierInfo = matchedPub ? getPublisherTierInfo(matchedPub) : { kademeLabel: "Kademe 1", groupLabel: "G0-G3" };

        map[pubKey] = {
          key: pubKey,
          publisher: matchedPub,
          id: matchedPub?.id || pubKey.replace("id:", ""),
          name:
            matchedPub?.fullName ||
            message.senderName ||
            message.senderUsername ||
            message.receiverName ||
            "Bilinmeyen Yayıncı",
          username:
            matchedPub?.username ||
            message.senderUsername ||
            message.receiverUsername ||
            "-",
          hiddenId: matchedPub?.hiddenId || "-",
          tier: tierInfo.kademeLabel,
          tierGroup: tierInfo.groupLabel,
          diamonds,
          targetDiamonds,
          status: matchedPub?.status || "Aktif",
          unreadCount: 0,
          lastDate: message.created_at,
          topics: {},
        };
      }

      // Kategori tahmini
      let category = "💎 ELMAS & HEDEF";
      const subLower = subject.toLowerCase();
      if (subLower.includes("kargo") || subLower.includes("ödül") || subLower.includes("hediye") || subLower.includes("plaket")) {
        category = "📦 ÖDÜL & KARGO";
      } else if (subLower.includes("kurgu") || subLower.includes("video") || subLower.includes("edit") || subLower.includes("reels")) {
        category = "🎬 VİDEO KURGU";
      } else if (subLower.includes("şifre") || subLower.includes("destek") || subLower.includes("ayar") || subLower.includes("teknik")) {
        category = "⚙️ TEKNİK DESTEK";
      }

      if (!map[pubKey].topics[topicKey]) {
        map[pubKey].topics[topicKey] = {
          key: topicKey,
          subject,
          category,
          status: "Yanıt Bekliyor",
          messages: [],
          unreadCount: 0,
          lastDate: message.created_at,
        };
      }

      map[pubKey].topics[topicKey].messages.push(message);

      if (
        message.status === "unread" &&
        Number(message.receiver_id) === Number(currentEmployee?.id)
      ) {
        map[pubKey].unreadCount += 1;
        map[pubKey].topics[topicKey].unreadCount += 1;
      }

      if (new Date(message.created_at) > new Date(map[pubKey].lastDate || 0)) {
        map[pubKey].lastDate = message.created_at;
      }

      if (new Date(message.created_at) > new Date(map[pubKey].topics[topicKey].lastDate || 0)) {
        map[pubKey].topics[topicKey].lastDate = message.created_at;
      }
    });

    return Object.values(map)
      .map((pubGroup) => {
        const topicsList = Object.values(pubGroup.topics).sort(
          (a, b) => new Date(b.lastDate || 0) - new Date(a.lastDate || 0)
        );

        topicsList.forEach((top) => {
          const lastMsg = top.messages[top.messages.length - 1];
          if (lastMsg && Number(lastMsg.sender_id) === Number(currentEmployee?.id)) {
            top.status = "Yanıtlandı";
          } else {
            top.status = top.unreadCount > 0 ? "Yanıt Bekliyor" : "İşlemde";
          }
        });

        return {
          ...pubGroup,
          topics: topicsList,
        };
      })
      .sort((a, b) => {
        if (b.unreadCount !== a.unreadCount) return b.unreadCount - a.unreadCount;
        if (b.topics.length !== a.topics.length) return b.topics.length - a.topics.length;
        return new Date(b.lastDate || 0) - new Date(a.lastDate || 0);
      });
  }, [publishers, messages, currentEmployee?.id, findPublisherByKey, getPublisherKeyFromMessage]);

  useEffect(() => {
    if (!selectedPublisherKey && groupedPublishers.length > 0) {
      setSelectedPublisherKey(groupedPublishers[0].key);
    }
  }, [groupedPublishers, selectedPublisherKey]);

  const selectedPublisherGroup =
    groupedPublishers.find((item) => item.key === selectedPublisherKey) ||
    groupedPublishers[0] ||
    null;

  useEffect(() => {
    if (selectedPublisherGroup?.topics?.length) {
      const topicExists = selectedPublisherGroup.topics.some(
        (t) => t.key === selectedTopicKey
      );
      if (!topicExists) {
        setSelectedTopicKey(selectedPublisherGroup.topics[0].key);
      }
    } else {
      setSelectedTopicKey("");
    }
  }, [selectedPublisherGroup, selectedTopicKey]);

  const selectedTopic =
    selectedPublisherGroup?.topics?.find((t) => t.key === selectedTopicKey) ||
    selectedPublisherGroup?.topics?.[0] ||
    null;

  const threadMessages = selectedTopic?.messages
    ? [...selectedTopic.messages].sort(
        (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
      )
    : [];

  async function markTopicAsRead(topic) {
    if (!topic?.messages?.length) return;

    const unreadMessages = topic.messages.filter(
      (m) =>
        m.status === "unread" &&
        Number(m.receiver_id) === Number(currentEmployee?.id)
    );

    for (const msg of unreadMessages) {
      try {
        await fetch(API_MESSAGE_READ, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message_id: msg.id }),
        });

        setMessages((prev) =>
          prev.map((item) =>
            String(item.id) === String(msg.id)
              ? { ...item, status: "read" }
              : item
          )
        );
      } catch (err) {
        console.error("Mesaj okundu işaretlenemedi:", err);
      }
    }
  }

  async function handleSelectTopic(topic) {
    setSelectedTopicKey(topic.key);
    await markTopicAsRead(topic);
  }

  async function handleSendReply() {
    if (!selectedPublisherGroup) {
      alert("Lütfen önce bir yayıncı seçiniz.");
      return;
    }

    if (!selectedTopic && threadMessages.length === 0) {
      alert("Yanıtlanacak konu seçiniz veya 'Yeni Konu Aç' butonunu kullanınız.");
      return;
    }

    if (!replyText.trim()) {
      alert("Yanıt alanı boş olamaz.");
      return;
    }

    let finalMessage = replyText.trim();
    if (composerRole === "admin" && !finalMessage.startsWith("👑")) {
      finalMessage = `👑 [AJANS YÖNETİMİ MÜDAHALESİ]: ${finalMessage}`;
    } else if (composerRole === "internal" && !finalMessage.startsWith("🔒")) {
      finalMessage = `🔒 [DAHİLİ NOT - YALNIZCA MENAJER/ADMİN]: ${finalMessage}`;
    }

    const lastMessage = threadMessages[threadMessages.length - 1];
    const employeeId = Number(currentEmployee?.id);

    const receiverId = lastMessage
      ? (Number(lastMessage.sender_id) === employeeId ? lastMessage.receiver_id : lastMessage.sender_id)
      : selectedPublisherGroup.id;

    try {
      setIsSending(true);
      const response = await fetch(API_MESSAGE_SEND, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sender_id: currentEmployee.id,
          sender_role: composerRole === "admin" ? "admin" : "employee",
          receiver_id: receiverId,
          subject: selectedTopic?.subject || "Genel Konu",
          message: finalMessage,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.message || "Yanıt gönderilemedi.");
        return;
      }

      setReplyText("");
      await reloadMessages();
    } catch (err) {
      console.error(err);
      alert("Yanıt gönderilirken bağlantı hatası oluştu.");
    } finally {
      setIsSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  }

  async function handleCreateNewTopic() {
    if (!modalPublisherId) {
      alert("Lütfen ilgili yayıncıyı seçiniz.");
      return;
    }
    if (!modalSubject.trim()) {
      alert("Lütfen konu başlığı giriniz.");
      return;
    }
    if (!modalMessage.trim()) {
      alert("Lütfen başlangıç mesajını yazınız.");
      return;
    }

    try {
      setIsSending(true);
      const response = await fetch(API_MESSAGE_SEND, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sender_id: currentEmployee.id,
          sender_role: "employee",
          receiver_id: modalPublisherId,
          subject: `[${modalCategory}] ${modalSubject.trim()}`,
          message: modalMessage.trim(),
        }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.message || "Yeni konu başlatılamadı.");
        return;
      }

      setModalSubject("");
      setModalMessage("");
      setShowNewTopicModal(false);
      await reloadMessages();
      setSelectedPublisherKey(`id:${modalPublisherId}`);
    } catch (err) {
      console.error(err);
      alert("Yeni konu başlatılırken bağlantı hatası oluştu.");
    } finally {
      setIsSending(false);
    }
  }

  // Sol panel filtreleme
  const filteredPublishers = useMemo(() => {
    let list = groupedPublishers;
    const q = searchQuery.toLowerCase().trim();

    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.username.toLowerCase().includes(q) ||
          p.hiddenId.toLowerCase().includes(q) ||
          p.topics.some((t) => t.subject.toLowerCase().includes(q))
      );
    }

    if (activePublisherFilter === "unread") {
      list = list.filter((p) => p.unreadCount > 0);
    } else if (activePublisherFilter === "vip") {
      list = list.filter((p) => ["Kademe 4", "Kademe 5", "Kademe 6", "Kademe 7", "Kademe 8", "Kademe 9", "Kademe 10"].includes(p.tier));
    }

    return list;
  }, [groupedPublishers, searchQuery, activePublisherFilter]);

  // Orta panel filtreleme
  const filteredTopics = useMemo(() => {
    if (!selectedPublisherGroup?.topics) return [];
    if (activeTopicFilter === "unread") {
      return selectedPublisherGroup.topics.filter((t) => t.unreadCount > 0);
    }
    return selectedPublisherGroup.topics;
  }, [selectedPublisherGroup, activeTopicFilter]);

  const totalUnreadAll = useMemo(() => {
    return groupedPublishers.reduce((acc, p) => acc + (p.unreadCount || 0), 0);
  }, [groupedPublishers]);

  const vipCount = useMemo(() => {
    return groupedPublishers.filter((p) => ["Kademe 4", "Kademe 5", "Kademe 6", "Kademe 7", "Kademe 8", "Kademe 9", "Kademe 10"].includes(p.tier)).length;
  }, [groupedPublishers]);

  return (
    <div
      className={`overflow-hidden rounded-[1.8rem] border transition-colors duration-200 ${
        isDark
          ? "border-white/10 bg-[#050505] text-white shadow-[0_20px_60px_rgba(0,0,0,.6)]"
          : "border-slate-300 bg-white text-slate-900 shadow-xl"
      }`}
    >
      {/* ==================== ÜST AKSİYON & ARAMA ŞERİDİ ==================== */}
      <div
        className={`flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3.5 ${
          isDark ? "border-white/10 bg-[#0a0a0a]" : "border-slate-200 bg-slate-50"
        }`}
      >
        <div className="flex items-center space-x-2.5">
          <button
            type="button"
            onClick={() => {
              setModalPublisherId(selectedPublisherGroup?.id || publishers[0]?.id || "");
              setShowNewTopicModal(true);
            }}
            className="flex items-center space-x-2 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 px-4 py-2 text-xs font-black text-black shadow-lg transition transform active:scale-95 hover:from-yellow-400 hover:to-amber-400"
          >
            <span className="text-sm">＋</span>
            <span>Yeni Konu / Talep Aç</span>
          </button>

          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("managerReplyInput");
              if (el) el.focus();
            }}
            className={`flex items-center space-x-1.5 rounded-xl border px-3.5 py-2 text-xs font-semibold transition ${
              isDark
                ? "border-white/10 bg-white/[0.04] text-slate-300 hover:bg-white/[0.08] hover:text-white"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            <span>↩</span>
            <span>Yanıtla</span>
          </button>
        </div>

        {/* Hızlı Arama Kutusu */}
        <div className="relative min-w-[260px] flex-1 max-w-md">
          <span className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-xs ${isDark ? "text-white/40" : "text-slate-400"}`}>
            🔍
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Yayıncı, ID veya Konu ara... (Ctrl+E)"
            className={`w-full rounded-xl border py-2 pl-9 pr-3 text-xs outline-none transition ${
              isDark
                ? "border-white/10 bg-[#111111] text-white placeholder-white/40 focus:border-yellow-500/50"
                : "border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-yellow-500"
            }`}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${isDark ? "text-white/50 hover:text-white" : "text-slate-400 hover:text-slate-700"}`}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ==================== ANA 3 KOLONLU GÖVDE ==================== */}
      <div className={`grid min-h-[660px] max-h-[820px] grid-cols-1 border-b xl:grid-cols-[290px_330px_1fr] ${
        isDark ? "border-white/10" : "border-slate-200"
      }`}>

        {/* ==================== 1. KOLON (SOL): FAVORİLER & YAYINCILAR ==================== */}
        <aside className={`flex flex-col border-r ${
          isDark ? "border-white/10 bg-[#080808]" : "border-slate-200 bg-slate-50"
        }`}>
          
          {/* Favoriler & Filtreler Bölümü */}
          <div className={`border-b p-3 ${isDark ? "border-white/10" : "border-slate-200"}`}>
            <div className={`mb-2 px-2 text-[10px] font-black uppercase tracking-widest ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              ⭐ FAVORİLER & FİLTRELER
            </div>
            <div className="space-y-1 text-xs">
              <button
                type="button"
                onClick={() => setActivePublisherFilter("all")}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 transition ${
                  activePublisherFilter === "all"
                    ? isDark
                      ? "bg-white/[0.08] font-bold text-white shadow-sm"
                      : "bg-slate-200 font-bold text-slate-900 shadow-sm"
                    : isDark
                      ? "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span>📥</span>
                  <span>Tüm Mesajlar</span>
                </div>
                <span className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
                  isDark ? "bg-white/10 text-slate-300" : "bg-slate-300 text-slate-800"
                }`}>
                  {groupedPublishers.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActivePublisherFilter("unread")}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 transition ${
                  activePublisherFilter === "unread"
                    ? "bg-yellow-500/20 font-bold text-yellow-300"
                    : isDark
                      ? "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span>✉️</span>
                  <span>Yanıt Bekleyenler</span>
                </div>
                {totalUnreadAll > 0 ? (
                  <span className="rounded-md bg-yellow-500 px-1.5 py-0.5 text-[11px] font-black text-black">
                    {totalUnreadAll}
                  </span>
                ) : (
                  <span className={`text-[11px] ${isDark ? "text-white/30" : "text-slate-400"}`}>0</span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setActivePublisherFilter("vip")}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 transition ${
                  activePublisherFilter === "vip"
                    ? "bg-amber-500/20 font-bold text-amber-300"
                    : isDark
                      ? "text-slate-400 hover:bg-white/[0.04] hover:text-slate-200"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span>👑</span>
                  <span>VIP / Top Kademeler</span>
                </div>
                <span className="rounded-md bg-yellow-500/10 px-1.5 py-0.5 text-[11px] font-bold text-yellow-400">
                  {vipCount}
                </span>
              </button>
            </div>
          </div>

          {/* Yayıncı Listesi Başlığı */}
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <span className={`text-[10px] font-black uppercase tracking-widest ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              👥 YAYINCILARIMIZ
            </span>
            <span className={`text-[10px] font-semibold ${isDark ? "text-slate-500" : "text-slate-500"}`}>
              {filteredPublishers.length} Yayıncı
            </span>
          </div>

          {/* Yayıncı Kartları Listesi */}
          <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
            {filteredPublishers.map((pub) => {
              const isSelected = selectedPublisherGroup?.key === pub.key;
              const progressPercent = Math.min(100, Math.round((pub.diamonds / (pub.targetDiamonds || 1)) * 100));

              return (
                <button
                  key={pub.key}
                  type="button"
                  onClick={() => {
                    setSelectedPublisherKey(pub.key);
                    setSelectedTopicKey("");
                  }}
                  className={`w-full rounded-xl p-2.5 text-left transition border ${
                    isSelected
                      ? isDark
                        ? "border-yellow-500/60 bg-[#1a1708] shadow-md"
                        : "border-yellow-500 bg-yellow-50/90 shadow-md"
                      : isDark
                        ? "border-white/[0.06] bg-[#0e0e0e] hover:bg-[#151515]"
                        : "border-slate-200 bg-white hover:bg-slate-100 shadow-sm"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center space-x-2.5 min-w-0">
                      <div className="relative shrink-0">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-tr from-amber-600 to-yellow-400 text-xs font-black text-black">
                          {pub.name.charAt(0).toUpperCase()}
                        </div>
                        {pub.status === "Aktif" && (
                          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-black bg-emerald-500" title="Aktif" />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className={`truncate text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                          {pub.name}
                        </div>
                        <div className={`truncate text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          @{pub.username} • <span className={isDark ? "text-slate-500" : "text-slate-400"}>{pub.hiddenId}</span>
                        </div>
                      </div>
                    </div>

                    {pub.unreadCount > 0 && (
                      <span className="shrink-0 rounded-full bg-yellow-500 px-1.5 py-0.5 text-[10px] font-black text-black">
                        {pub.unreadCount}
                      </span>
                    )}
                  </div>

                  {/* Mini Kademe & Hedef İlerleme Barı */}
                  <div className={`mt-2 flex items-center justify-between border-t pt-1.5 text-[10px] ${
                    isDark ? "border-white/[0.05] text-slate-400" : "border-slate-200 text-slate-600"
                  }`}>
                    <span className="font-bold text-yellow-500">{pub.tier}</span>
                    <span>💎 {(pub.diamonds / 1000).toFixed(0)}K / {(pub.targetDiamonds / 1000).toFixed(0)}K ({progressPercent}%)</span>
                  </div>
                  <div className={`mt-1 h-1 w-full overflow-hidden rounded-full ${isDark ? "bg-black/90" : "bg-slate-200"}`}>
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-yellow-500 to-amber-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </button>
              );
            })}

            {filteredPublishers.length === 0 && (
              <div className={`rounded-xl border p-5 text-center text-xs ${
                isDark ? "border-white/10 bg-white/[0.02] text-slate-400" : "border-slate-200 bg-white text-slate-500"
              }`}>
                Eşleşen yayıncı bulunamadı.
              </div>
            )}
          </div>
        </aside>

        {/* ==================== 2. KOLON (ORTA): MESAJ KONULARI ==================== */}
        <section className={`flex flex-col border-r ${
          isDark ? "border-white/10 bg-[#070707]" : "border-slate-200 bg-slate-50/50"
        }`}>
          {/* Konu Üst Barı */}
          <div className={`border-b p-3.5 ${
            isDark ? "border-white/10 bg-[#0a0a0a]" : "border-slate-200 bg-white"
          }`}>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="truncate text-xs font-black uppercase tracking-wider text-yellow-500">
                {selectedPublisherGroup?.name || "YAYINCI SEÇİNİZ"}
              </h2>
              <button
                type="button"
                title="Yeni Konu Ekle"
                onClick={() => {
                  setModalPublisherId(selectedPublisherGroup?.id || "");
                  setShowNewTopicModal(true);
                }}
                className={`rounded-lg p-1.5 transition ${
                  isDark
                    ? "bg-white/[0.06] text-slate-300 hover:bg-yellow-500 hover:text-black"
                    : "bg-slate-100 text-slate-700 hover:bg-yellow-500 hover:text-black"
                }`}
              >
                <span className="text-sm font-bold">＋</span>
              </button>
            </div>

            {/* Filtre Sekmeleri */}
            <div className={`flex items-center justify-between border-t pt-2 text-xs ${
              isDark ? "border-white/[0.06]" : "border-slate-200"
            }`}>
              <div className="flex space-x-1">
                <button
                  type="button"
                  onClick={() => setActiveTopicFilter("all")}
                  className={`rounded-lg px-2.5 py-1 text-xs transition ${
                    activeTopicFilter === "all"
                      ? isDark
                        ? "bg-white/[0.09] font-bold text-white"
                        : "bg-slate-200 font-bold text-slate-900"
                      : isDark
                        ? "text-slate-400 hover:text-white"
                        : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Tümü
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTopicFilter("unread")}
                  className={`rounded-lg px-2.5 py-1 text-xs transition ${
                    activeTopicFilter === "unread"
                      ? isDark
                        ? "bg-white/[0.09] font-bold text-white"
                        : "bg-slate-200 font-bold text-slate-900"
                      : isDark
                        ? "text-slate-400 hover:text-white"
                        : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  Okunmamış ({selectedPublisherGroup?.topics?.filter((t) => t.unreadCount > 0).length || 0})
                </button>
              </div>
              <span className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>Sırala: Tarih ▼</span>
            </div>
          </div>

          {/* Konu Kartları Listesi */}
          <div className="flex-1 space-y-2 overflow-y-auto p-2">
            {filteredTopics.map((topic) => {
              const isSelected = selectedTopic?.key === topic.key;
              const lastMsg = topic.messages[topic.messages.length - 1];

              return (
                <button
                  key={topic.key}
                  type="button"
                  onClick={() => handleSelectTopic(topic)}
                  className={`w-full rounded-xl p-3 text-left transition border ${
                    isSelected
                      ? isDark
                        ? "border-yellow-500/50 bg-[#1a1708] shadow-md"
                        : "border-yellow-500 bg-yellow-50/90 shadow-md"
                      : isDark
                        ? "border-white/[0.06] bg-[#0e0e0e] hover:bg-[#151515]"
                        : "border-slate-200 bg-white hover:bg-slate-100 shadow-sm"
                  }`}
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${
                      isDark ? "bg-white/[0.06] text-slate-300" : "bg-slate-100 text-slate-700"
                    }`}>
                      {topic.category}
                    </span>
                    <span className={`text-[10px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {formatMessageDate(topic.lastDate)}
                    </span>
                  </div>

                  <div className={`mb-1 line-clamp-1 text-xs font-bold ${
                    isSelected ? "text-yellow-500" : isDark ? "text-white" : "text-slate-900"
                  }`}>
                    {topic.subject}
                  </div>

                  <div className={`mb-2 line-clamp-2 text-[11px] leading-relaxed ${
                    isDark ? "text-slate-400" : "text-slate-600"
                  }`}>
                    <strong className={isDark ? "text-slate-300" : "text-slate-800"}>
                      {lastMsg ? (Number(lastMsg.sender_id) === Number(currentEmployee?.id) ? "Siz" : selectedPublisherGroup?.name.split(" ")[0]) : "Sistem"}:
                    </strong>{" "}
                    {lastMsg ? lastMsg.message : "Mesaj içeriği bulunmuyor."}
                  </div>

                  <div className={`flex items-center justify-between border-t pt-1.5 text-[10px] ${
                    isDark ? "border-white/[0.05] text-slate-400" : "border-slate-200 text-slate-500"
                  }`}>
                    <span className="flex items-center space-x-1">
                      <span className={`h-1.5 w-1.5 rounded-full ${topic.unreadCount > 0 ? "bg-amber-400" : "bg-emerald-400"}`} />
                      <span className="font-medium">{topic.status}</span>
                    </span>
                    <span>💬 {topic.messages.length} mesaj</span>
                  </div>
                </button>
              );
            })}

            {filteredTopics.length === 0 && (
              <div className={`rounded-xl border p-6 text-center text-xs ${
                isDark ? "border-white/10 bg-white/[0.02] text-slate-400" : "border-slate-200 bg-white text-slate-500"
              }`}>
                <p>Bu yayıncıya ait aktif konu bulunmuyor.</p>
                <button
                  type="button"
                  onClick={() => {
                    setModalPublisherId(selectedPublisherGroup?.id || "");
                    setShowNewTopicModal(true);
                  }}
                  className="mt-2 font-bold text-yellow-500 hover:underline"
                >
                  + Yeni Konu Başlat
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ==================== 3. KOLON (SAĞ): OKUMA VE YAZIŞMA MASASI ==================== */}
        <main className={`flex flex-col ${
          isDark ? "bg-[#040404]" : "bg-slate-100/60"
        }`}>
          {selectedTopic ? (
            <>
              {/* Konu Başlığı Barı */}
              <div className={`flex items-center justify-between border-b p-4 ${
                isDark ? "border-white/10 bg-[#0a0a0a]" : "border-slate-200 bg-white"
              }`}>
                <div className="min-w-0 pr-4">
                  <div className="mb-1 flex items-center space-x-2">
                    <span className="rounded-md border border-yellow-500/25 bg-yellow-500/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-yellow-400">
                      {selectedTopic.category}
                    </span>
                    <span className="rounded-md border border-amber-500/25 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-300">
                      🟡 {selectedTopic.status.toUpperCase()}
                    </span>
                  </div>
                  <h1 className={`truncate text-base font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                    {selectedTopic.subject}
                  </h1>
                  <div className={`mt-0.5 flex items-center space-x-2 text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    <span>👤 Yayıncı: <strong className={isDark ? "text-slate-200" : "text-slate-900"}>{selectedPublisherGroup?.name}</strong></span>
                    <span>•</span>
                    <span>👔 Menajer: <strong className={isDark ? "text-slate-200" : "text-slate-900"}>{currentEmployee?.fullName || "Yönetici"}</strong></span>
                  </div>
                </div>
              </div>

              {/* Mesaj Zaman Tüneli */}
              <div className="flex-1 space-y-4 overflow-y-auto p-5">
                <div className="my-1 text-center">
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-medium ${
                    isDark ? "border-white/[0.08] bg-white/[0.04] text-slate-400" : "border-slate-300 bg-white text-slate-600 shadow-sm"
                  }`}>
                    ──── Konuşma Başlangıcı: {selectedPublisherGroup?.name} & Wolf Media Ekibi ────
                  </span>
                </div>

                {threadMessages.map((msg) => {
                  const isMine = Number(msg.sender_id) === Number(currentEmployee?.id);
                  const msgText = String(msg.message || "");
                  const isAdminIntervention = msg.sender_role === "admin" || msgText.includes("[AJANS YÖNETİMİ MÜDAHALESİ]") || msgText.startsWith("👑");
                  const isInternalNote = msgText.includes("[DAHİLİ NOT") || msgText.startsWith("🔒");

                  if (isAdminIntervention) {
                    return (
                      <div
                        key={msg.id}
                        className="my-3 rounded-2xl border-2 border-yellow-500/60 bg-[#1e1a0a] p-4 shadow-lg"
                      >
                        <div className="mb-2.5 flex items-center justify-between border-b border-yellow-500/20 pb-2">
                          <div className="flex items-center space-x-2">
                            <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-yellow-400 text-xs font-black text-black">
                              👑
                            </span>
                            <span className="text-xs font-black uppercase tracking-wider text-yellow-400">
                              AJANS YÖNETİMİ MÜDAHALESİ • Admin
                            </span>
                          </div>
                          <span className="text-[11px] font-semibold text-yellow-300/70">
                            {formatMessageDate(msg.created_at)}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap text-sm font-medium leading-relaxed text-yellow-100/90">
                          {msgText.replace("👑 [AJANS YÖNETİMİ MÜDAHALESİ]:", "").replace(/^👑\s*/, "")}
                        </p>
                      </div>
                    );
                  }

                  if (isInternalNote) {
                    return (
                      <div
                        key={msg.id}
                        className="my-3 rounded-2xl border-2 border-dashed border-amber-500/50 bg-[#1a1405] p-3.5"
                      >
                        <div className="mb-1.5 flex items-center justify-between text-xs font-bold text-amber-300">
                          <div className="flex items-center space-x-1.5">
                            <span>🔒</span>
                            <span>DAHİLİ NOT (Yalnızca Menajer ve Yönetici Görür - Yayıncı Göremez)</span>
                          </div>
                          <span className="text-[11px] text-amber-400/70">
                            {formatMessageDate(msg.created_at)}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-amber-200/90">
                          {msgText.replace("🔒 [DAHİLİ NOT - YALNIZCA MENAJER/ADMİN]:", "").replace(/^🔒\s*/, "")}
                        </p>
                      </div>
                    );
                  }

                  if (isMine) {
                    return (
                      <div key={msg.id} className="my-2 flex justify-end">
                        <div className={`max-w-2xl rounded-2xl rounded-tr-sm border p-4 text-xs shadow-sm ${
                          isDark
                            ? "border-blue-500/30 bg-[#10141e] text-slate-100"
                            : "border-blue-200 bg-blue-50 text-slate-900"
                        }`}>
                          <div className="mb-1.5 flex items-center justify-between space-x-4 text-[11px] font-bold text-blue-500">
                            <span>{currentEmployee?.fullName || "Menajer"}</span>
                            <span className={`font-normal ${isDark ? "text-slate-500" : "text-slate-400"}`}>✓✓ {formatMessageDate(msg.created_at)}</span>
                          </div>
                          <p className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? "text-slate-100" : "text-slate-800"}`}>
                            {msg.message}
                          </p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id} className="my-2 flex justify-start">
                      <div className={`max-w-2xl rounded-2xl rounded-tl-sm border p-4 text-xs shadow-sm ${
                        isDark
                          ? "border-white/[0.08] bg-[#0e0e0e] text-slate-200"
                          : "border-slate-200 bg-white text-slate-900"
                      }`}>
                        <div className="mb-1.5 flex items-center justify-between space-x-4 text-[11px] font-bold text-purple-400">
                          <span>{selectedPublisherGroup?.name || "Yayıncı"}</span>
                          <span className={`font-normal ${isDark ? "text-slate-500" : "text-slate-400"}`}>{formatMessageDate(msg.created_at)}</span>
                        </div>
                        <p className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                          {msg.message}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ==================== ALT YANIT YAZMA BÖLMESİ ==================== */}
              <div className={`border-t p-4 ${
                isDark ? "border-white/10 bg-[#090909]" : "border-slate-200 bg-white"
              }`}>
                <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs font-semibold ${isDark ? "text-slate-400" : "text-slate-600"}`}>Yanıt Rolü:</span>
                    <div className={`inline-flex rounded-lg border p-0.5 text-xs ${
                      isDark ? "border-white/10 bg-black/60" : "border-slate-200 bg-slate-100"
                    }`}>
                      <button
                        type="button"
                        onClick={() => setComposerRole("admin")}
                        className={`rounded-md px-2.5 py-1 transition ${
                          composerRole === "admin"
                            ? "bg-yellow-400 font-black text-black shadow"
                            : isDark ? "font-medium text-slate-300 hover:text-white" : "font-medium text-slate-700 hover:text-slate-900"
                        }`}
                      >
                        👑 Admin Müdahalesi
                      </button>
                      <button
                        type="button"
                        onClick={() => setComposerRole("manager")}
                        className={`rounded-md px-2.5 py-1 transition ${
                          composerRole === "manager"
                            ? "bg-blue-600 font-bold text-white shadow"
                            : isDark ? "font-medium text-slate-300 hover:text-white" : "font-medium text-slate-700 hover:text-slate-900"
                        }`}
                      >
                        👤 Menajer
                      </button>
                      <button
                        type="button"
                        onClick={() => setComposerRole("internal")}
                        className={`rounded-md px-2.5 py-1 transition ${
                          composerRole === "internal"
                            ? "bg-amber-500 font-bold text-black shadow"
                            : isDark ? "font-medium text-amber-300 hover:text-amber-200" : "font-medium text-amber-600 hover:text-amber-700"
                        }`}
                      >
                        🔒 Menajere Özel Gizli Not
                      </button>
                    </div>
                  </div>

                  {/* Hızlı Şablonlar */}
                  <div className="flex items-center space-x-1.5 text-[11px]">
                    <span className={isDark ? "text-slate-400" : "text-slate-500"}>Hızlı Ekle:</span>
                    <button
                      type="button"
                      onClick={() => setReplyText("Tebrikler! Bu ayki hedef barajınızı başarıyla tamamladınız. Prim ve ödülünüz tanımlanmıştır.")}
                      className={`rounded px-2 py-1 transition ${
                        isDark ? "bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      Tebrik
                    </button>
                    <button
                      type="button"
                      onClick={() => setReplyText("Yayın planınızı kontrol ettim, gayet iyi ilerliyorsunuz. Takipteyiz!")}
                      className={`rounded px-2 py-1 transition ${
                        isDark ? "bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      Teşvik
                    </button>
                    <button
                      type="button"
                      onClick={() => setReplyText("Kargo takip numaranız tanımlanmıştır. Detayları bildirimler kısmından inceleyebilirsiniz.")}
                      className={`rounded px-2 py-1 transition ${
                        isDark ? "bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      Kargo
                    </button>
                  </div>
                </div>

                {/* Yazım Kutusu */}
                <div className={`relative rounded-2xl border p-2.5 transition ${
                  isDark
                    ? "border-white/[0.12] bg-[#111111] focus-within:border-yellow-500/60"
                    : "border-slate-300 bg-slate-50 focus-within:border-yellow-500"
                }`}>
                  {composerRole === "internal" && (
                    <div className="mb-2 flex items-center space-x-2 rounded-lg border border-dashed border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300">
                      <span>🔒</span>
                      <span>Bu mesajı sadece menajer ve admin görebilir. Yayıncı ekranında görünmez!</span>
                    </div>
                  )}

                  <textarea
                    id="managerReplyInput"
                    rows={3}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Bu konuya cevabınızı veya kararınızı yazın... (Enter: Gönder, Shift+Enter: Yeni Satır)"
                    className={`w-full resize-none bg-transparent px-2 py-1 text-sm leading-relaxed outline-none ${
                      isDark ? "text-slate-100 placeholder-slate-500" : "text-slate-900 placeholder-slate-400"
                    }`}
                  />

                  <div className={`mt-1 flex items-center justify-between border-t px-1 pt-2 ${
                    isDark ? "border-white/[0.06]" : "border-slate-200"
                  }`}>
                    <div className={`flex items-center space-x-2 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                      <button
                        type="button"
                        onClick={() => setReplyText((prev) => `${prev} 💎 `)}
                        className="rounded-lg p-1 text-xs transition hover:bg-black/10"
                        title="Elmas Emojisi"
                      >
                        💎
                      </button>
                      <button
                        type="button"
                        onClick={() => setReplyText((prev) => `${prev} 👑 `)}
                        className="rounded-lg p-1 text-xs transition hover:bg-black/10"
                        title="Taç Emojisi"
                      >
                        👑
                      </button>
                      <button
                        type="button"
                        onClick={() => setReplyText((prev) => `${prev} 🎉 `)}
                        className="rounded-lg p-1 text-xs transition hover:bg-black/10"
                        title="Kutlama Emojisi"
                      >
                        🎉
                      </button>
                      <span className={`hidden text-[11px] sm:inline ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                        Enter ile hızlı gönder
                      </span>
                    </div>

                    <button
                      type="button"
                      disabled={isSending}
                      onClick={handleSendReply}
                      className={`flex items-center space-x-2 rounded-xl px-5 py-2 text-xs font-black transition active:scale-95 disabled:opacity-50 ${
                        composerRole === "admin"
                          ? "bg-yellow-400 text-black hover:bg-yellow-300"
                          : composerRole === "internal"
                            ? "bg-amber-500 text-black hover:bg-amber-400"
                            : "bg-blue-600 text-white hover:bg-blue-500"
                      }`}
                    >
                      <span>{isSending ? "Gönderiliyor..." : "Yanıtı Gönder"}</span>
                      <span>➔</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className={`flex flex-1 flex-col items-center justify-center p-8 text-center ${
              isDark ? "text-slate-400" : "text-slate-600"
            }`}>
              <div className="mb-3 text-4xl">✉️</div>
              <div className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>Görüntülenecek Konu Yok</div>
              <p className={`mt-1 max-w-sm text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                Sol listeden bir yayıncı seçebilir veya yukarıdaki "+ Yeni Konu / Talep Aç" butonuyla yeni bir mesajlaşma başlatabilirsiniz.
              </p>
            </div>
          )}
        </main>
      </div>

      {/* ==================== EN ALT DURUM ÇUBUĞU ==================== */}
      <footer className={`flex h-7 items-center justify-between border-t px-4 text-[11px] ${
        isDark ? "border-white/10 bg-[#050505] text-slate-400" : "border-slate-200 bg-slate-100 text-slate-600"
      }`}>
        <div className="flex items-center space-x-3">
          <span>Aktif Kullanıcı: <strong className={isDark ? "text-slate-300" : "text-slate-800"}>{currentEmployee?.fullName || "Yönetici"} ({currentEmployee?.role || "Menajer"})</strong></span>
          <span>•</span>
          <span>Seçili Yayıncı: <strong className={isDark ? "text-slate-300" : "text-slate-800"}>{selectedPublisherGroup?.name || "-"} ({selectedPublisherGroup?.hiddenId || "-"})</strong></span>
        </div>

        <div>
          <span>Görüntülenen Konu: <strong className={isDark ? "text-slate-300" : "text-slate-800"}>{selectedPublisherGroup?.topics?.length || 0} Konu</strong></span>
        </div>
      </footer>

      {/* ==================== YENİ KONU BAŞLATMA MODAL (POPUP) ==================== */}
      {showNewTopicModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-2xl border p-6 shadow-2xl ${
            isDark ? "border-yellow-500/30 bg-[#0c0c0c] text-white" : "border-slate-300 bg-white text-slate-900"
          }`}>
            <div className={`mb-4 flex items-center justify-between border-b pb-3 ${
              isDark ? "border-white/10" : "border-slate-200"
            }`}>
              <div className="flex items-center space-x-2">
                <span className="text-lg">✉️</span>
                <h3 className={`text-base font-black ${isDark ? "text-white" : "text-slate-900"}`}>Yeni Konu / Talep Başlat</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowNewTopicModal(false)}
                className={`text-lg ${isDark ? "text-slate-400 hover:text-white" : "text-slate-400 hover:text-slate-800"}`}
              >
                ✕
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div>
                <label className={`mb-1 block font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>İlgili Yayıncı</label>
                <select
                  value={modalPublisherId}
                  onChange={(e) => setModalPublisherId(e.target.value)}
                  className={`w-full rounded-xl border px-3 py-2.5 outline-none focus:border-yellow-500 ${
                    isDark ? "border-white/10 bg-[#141414] text-white" : "border-slate-300 bg-slate-50 text-slate-900"
                  }`}
                >
                  <option value="">Yayıncı Seçiniz...</option>
                  {publishers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.fullName || p.name} (@{p.username}) - {p.hiddenId}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`mb-1 block font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>Kategori</label>
                <select
                  value={modalCategory}
                  onChange={(e) => setModalCategory(e.target.value)}
                  className={`w-full rounded-xl border px-3 py-2.5 outline-none focus:border-yellow-500 ${
                    isDark ? "border-white/10 bg-[#141414] text-white" : "border-slate-300 bg-slate-50 text-slate-900"
                  }`}
                >
                  <option value="💎 ELMAS & HEDEF">💎 Elmas & Hedef Takibi</option>
                  <option value="📦 ÖDÜL & KARGO">📦 Hediye / Plaket / Kargo</option>
                  <option value="🎬 VİDEO KURGU">🎬 Video Editör Desteği</option>
                  <option value="⚙️ TEKNİK DESTEK">⚙️ Teknik Destek & Ayarlar</option>
                </select>
              </div>

              <div>
                <label className={`mb-1 block font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>Konu Başlığı</label>
                <input
                  type="text"
                  value={modalSubject}
                  onChange={(e) => setModalSubject(e.target.value)}
                  placeholder="Örn: Haziran Kademe Yükseltme Talebi"
                  className={`w-full rounded-xl border px-3 py-2.5 outline-none focus:border-yellow-500 ${
                    isDark ? "border-white/10 bg-[#141414] text-white" : "border-slate-300 bg-slate-50 text-slate-900"
                  }`}
                />
              </div>

              <div>
                <label className={`mb-1 block font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>Başlangıç Mesajı</label>
                <textarea
                  rows={3}
                  value={modalMessage}
                  onChange={(e) => setModalMessage(e.target.value)}
                  placeholder="Konuyla ilgili ilk mesajınızı yazın..."
                  className={`w-full resize-none rounded-xl border px-3 py-2.5 outline-none focus:border-yellow-500 ${
                    isDark ? "border-white/10 bg-[#141414] text-white" : "border-slate-300 bg-slate-50 text-slate-900"
                  }`}
                />
              </div>
            </div>

            <div className={`mt-5 flex justify-end space-x-2 border-t pt-4 ${
              isDark ? "border-white/10" : "border-slate-200"
            }`}>
              <button
                type="button"
                onClick={() => setShowNewTopicModal(false)}
                className={`rounded-xl px-4 py-2 text-xs font-semibold transition ${
                  isDark ? "bg-white/[0.05] text-slate-300 hover:bg-white/[0.1]" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                İptal
              </button>
              <button
                type="button"
                disabled={isSending}
                onClick={handleCreateNewTopic}
                className="rounded-xl bg-yellow-500 px-5 py-2 text-xs font-black text-black hover:bg-yellow-400 disabled:opacity-50"
              >
                {isSending ? "Oluşturuluyor..." : "Konuyu Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RewardManagementSection({ publishers = [], currentUser, mode = "manager" }) {
  const emptyForm = {
    title: "",
    badgeText: "",
    visualTitle: "",
    visualSub: "",
    desc: "",
    detailTitle: "",
    detailDescription: "",
    rewardText: "",
    targetText: "all",
    targetGroup: "all",
    contentKind: "image",
    imageUrl: "",
    videoUrl: "",
    startDate: "",
    endDate: "",
    accent: "gold",
    status: "Aktif",
  };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  async function loadRewards() {
    try {
      setLoading(true);

      const response = await fetch(API_REWARD_LIST, { cache: "no-store" });
      const data = await response.json();

      if (!data.success) {
        alert(data.message || "Ödül içerikleri alınamadı.");
        return;
      }

      setRows(data.rewards || []);
    } catch (err) {
      console.error(err);
      alert("Ödül içerikleri alınırken bağlantı hatası oluştu.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRewards();
  }, []);

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
  }

  function handleEdit(item) {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      badgeText: item.badgeText || "",
      visualTitle: item.visualTitle || "",
      visualSub: item.visualSub || "",
      desc: item.desc || "",
      detailTitle: item.detailTitle || "",
      detailDescription: item.detailDescription || "",
      rewardText: item.rewardText || "",
      targetText: normalizeTargetGroup(item.targetGroup || item.targetText || "all"),
      targetGroup: normalizeTargetGroup(item.targetGroup || item.targetText || "all"),
      contentKind: item.contentKind || "image",
      imageUrl: item.imageUrl || "",
      videoUrl: item.videoUrl || "",
      startDate: item.startDate || "",
      endDate: item.endDate || "",
      accent: item.accent || "gold",
      status: item.status || "Aktif",
    });
  }

  async function handleSaveReward() {
    if (!form.title.trim()) {
      alert("Başlık zorunlu.");
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(API_REWARD_SAVE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: editingId,
          ...form,
          targetGroup: normalizeTargetGroup(form.targetGroup || form.targetText),
          targetText: getTargetGroupLabel(form.targetGroup || form.targetText),
          createdBy: currentUser?.username || "-",
          createdByRole: mode,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.message || "Ödül içeriği kaydedilemedi.");
        return;
      }

      resetForm();
      await loadRewards();
      alert("Ödül içeriği kaydedildi.");
    } catch (err) {
      console.error(err);
      alert("Ödül içeriği kaydedilirken bağlantı hatası oluştu.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteReward(item) {
    if (!window.confirm(`${item.title} içeriğini kaldırmak istiyor musunuz?`)) return;

    try {
      setSaving(true);

      const response = await fetch(API_REWARD_DELETE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: item.id }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.message || "İçerik kaldırılamadı.");
        return;
      }

      await loadRewards();
      alert("Ödül içeriği kaldırıldı.");
    } catch (err) {
      console.error(err);
      alert("İçerik kaldırılırken bağlantı hatası oluştu.");
    } finally {
      setSaving(false);
    }
  }

  const activeRows = rows.filter((item) => item.status !== "Silindi");
  const liveRows = activeRows.filter((item) => item.status === "Aktif");
  const { isDark } = useTheme();

  return (
    <div className="space-y-6">
      <section className={`rounded-[2rem] border p-6 transition-colors ${
        isDark ? "border-white/10 bg-white/[0.035] shadow-[0_18px_60px_rgba(0,0,0,.25)]" : "border-slate-200/90 bg-white shadow-sm"
      }`}>
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className={`text-xs font-bold uppercase tracking-[0.34em] ${
              isDark ? "text-yellow-300" : "text-yellow-600"
            }`}>
              Etkinlik ve Ödül Yönetimi
            </div>
            <h2 className={`mt-2 text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
              Dinamik Ödül Ekranı
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-600"}`}>
              Yönetici tarafından eklenen görsel veya video içerikler belirlenen tarih aralığında yayıncı panelinde görünür.
            </p>
          </div>

          <button
            type="button"
            onClick={loadRewards}
            className={`rounded-2xl border px-5 py-3 text-sm font-bold transition ${
              isDark
                ? "border-white/10 bg-white/[0.035] text-white/70 hover:border-yellow-500/30 hover:text-yellow-300"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900 shadow-sm"
            }`}
          >
            Yenile
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <InfoBox title="Toplam İçerik" value={activeRows.length} note="Silinmemiş içerik sayısı" />
          <InfoBox title="Yayındaki İçerik" value={liveRows.length} note="Aktif durumdaki içerikler" />
          <InfoBox title="Bağlı Yayıncı" value={publishers.length} note="Yöneticiye bağlı yayıncı" />
          <InfoBox title="Durum" value={loading ? "Yükleniyor" : "Hazır"} note="API veri durumu" />
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-[1fr_.78fr]">
        <section className={`rounded-[1.8rem] border p-5 transition-colors ${
          isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
        }`}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                Yayınlanan Ödül / Etkinlikler
              </div>
              <div className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>
                Görsel, video, hedef, ödül ve yayın süresi listesi
              </div>
            </div>
            <span className={`rounded-full border px-3.5 py-1.5 text-xs font-bold ${
              isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
            }`}>
              {loading ? "Yükleniyor" : `${activeRows.length} İçerik`}
            </span>
          </div>

          <div className="space-y-3">
            {activeRows.length === 0 && (
              <div className={`rounded-2xl border p-4 text-sm ${
                isDark ? "border-white/10 bg-white/[0.025] text-white/45" : "border-slate-200 bg-slate-50 text-slate-500"
              }`}>
                Henüz ödül veya etkinlik içeriği bulunmuyor.
              </div>
            )}

            {activeRows.map((item) => (
              <div
                key={item.id}
                className={`rounded-2xl border p-4 transition ${
                  isDark ? "border-white/10 bg-white/[0.025]" : "border-slate-200 bg-slate-50 hover:border-amber-400 shadow-sm"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className={`text-base font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                      {item.title}
                    </div>
                    <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/55" : "text-slate-600"}`}>
                      {item.desc || "Açıklama yok."}
                    </p>
                  </div>

                  <span className={`rounded-full border px-3 py-1 text-xs font-bold ${
                    isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
                  }`}>
                    {item.status || "Aktif"}
                  </span>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  <InfoBox title="Tür" value={item.contentKind === "video" ? "Video" : "Görsel"} note={item.badgeText || "-"} />
                  <InfoBox title="Hedef" value={<TargetGroupBadge value={item.targetGroup || item.targetText} compact />} note="Yayıncı hedefi" />
                  <InfoBox title="Ödül" value={item.rewardText || "-"} note="Kazanç / bonus" />
                  <InfoBox title="Yayın Süresi" value={formatDateForDisplay(item.endDate)} note={`Başlangıç: ${formatDateForDisplay(item.startDate)}`} />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className={`rounded-xl px-4 py-2 text-xs font-bold transition ${
                      isDark
                        ? "border border-yellow-500/25 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500 hover:text-black"
                        : "border border-yellow-400 bg-yellow-100 text-yellow-900 hover:bg-yellow-500 hover:text-black"
                    }`}
                  >
                    Düzenle
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeleteReward(item)}
                    className={`rounded-xl px-4 py-2 text-xs font-bold transition ${
                      isDark
                        ? "border border-red-500/25 bg-red-500/10 text-red-300 hover:bg-red-500 hover:text-white"
                        : "border border-red-200 bg-red-50 text-red-700 hover:bg-red-500 hover:text-white"
                    }`}
                  >
                    Kaldır
                  </button>

                  {(item.imageUrl || item.videoUrl) && (
                    <a
                      href={item.contentKind === "video" ? item.videoUrl : item.imageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={`rounded-xl border px-4 py-2 text-xs font-bold transition ${
                        isDark
                          ? "border-white/10 bg-white/[0.035] text-white/65 hover:text-yellow-300"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100 hover:text-slate-900 shadow-sm"
                      }`}
                    >
                      İçeriği Aç
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={`rounded-[1.8rem] border p-5 transition-colors ${
          isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
        }`}>
          <div className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
            {editingId ? "Ödül İçeriğini Düzenle" : "Yeni Ödül / Etkinlik İçeriği"}
          </div>
          <div className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>
            İşaretli alanlarda görünecek başlık, hedef, bitiş tarihi, açıklama ve medya bilgilerini doldur.
          </div>

          <div className="mt-5 space-y-3">
            <input value={form.badgeText} onChange={(e) => setForm((p) => ({ ...p, badgeText: e.target.value }))} placeholder="Üst etiket: Elmas Yarışı" className="form-input" />
            <input value={form.visualTitle} onChange={(e) => setForm((p) => ({ ...p, visualTitle: e.target.value }))} placeholder="Büyük görsel başlık: MAYIS ELMAS YARIŞI" className="form-input" />
            <input value={form.visualSub} onChange={(e) => setForm((p) => ({ ...p, visualSub: e.target.value }))} placeholder="Alt başlık: HEDEF 500K ELMAS!" className="form-input" />
            <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Detay başlığı" className="form-input" />
            <input value={form.rewardText} onChange={(e) => setForm((p) => ({ ...p, rewardText: e.target.value }))} placeholder="Ödül detayı: Bonus / Tripod / Ses sistemi" className="form-input" />
            <select
              value={form.targetGroup || form.targetText || "all"}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  targetGroup: e.target.value,
                  targetText: getTargetGroupLabel(e.target.value),
                }))
              }
              className="form-input"
            >
              {TARGET_GROUP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select value={form.contentKind} onChange={(e) => setForm((p) => ({ ...p, contentKind: e.target.value }))} className="form-input">
              <option value="image">Görsel İçerik</option>
              <option value="video">Video İçerik</option>
            </select>

            {form.contentKind === "image" ? (
              <input value={form.imageUrl} onChange={(e) => setForm((p) => ({ ...p, imageUrl: e.target.value }))} placeholder="Görsel URL" className="form-input" />
            ) : (
              <input value={form.videoUrl} onChange={(e) => setForm((p) => ({ ...p, videoUrl: e.target.value }))} placeholder="Video URL / YouTube linki / mp4 linki" className="form-input" />
            )}

            <div className="grid gap-3 md:grid-cols-2">
              <input type="date" value={form.startDate} onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))} className="form-input" />
              <input type="date" value={form.endDate} onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))} className="form-input" />
            </div>

            <select value={form.accent} onChange={(e) => setForm((p) => ({ ...p, accent: e.target.value }))} className="form-input">
              <option value="gold">Gold Tema</option>
              <option value="purple">Mor Tema</option>
            </select>

            <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} className="form-input">
              <option>Aktif</option>
              <option>Planlandı</option>
              <option>Pasif</option>
            </select>

            <textarea value={form.desc} onChange={(e) => setForm((p) => ({ ...p, desc: e.target.value }))} placeholder="Kart açıklaması..." className="form-textarea min-h-[90px]" />
            <textarea value={form.detailDescription} onChange={(e) => setForm((p) => ({ ...p, detailDescription: e.target.value }))} placeholder="Detay açıklaması..." className="form-textarea min-h-[120px]" />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleSaveReward}
                disabled={saving}
                className="rounded-2xl bg-yellow-500 px-5 py-3 text-sm font-bold text-black hover:bg-yellow-400 disabled:opacity-60"
              >
                {saving ? "Kaydediliyor..." : editingId ? "Güncelle" : "Yayınla"}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-2xl border border-white/10 bg-white/[0.035] px-5 py-3 text-sm font-semibold text-white/65 hover:text-yellow-300"
                >
                  Vazgeç
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function EventsSection({ publishers }) {
  const [eventList, setEventList] = useState(initialEvents);
  const { isDark } = useTheme();
  const seenReport = publishers.map((publisher, index) => ({
    name: publisher.fullName,
    status: index % 3 === 2 ? "Görmedi" : "Gördü",
    action: index % 3 === 0 ? "Katılacak" : index % 3 === 1 ? "Kararsız" : "-",
    date: index % 3 === 2 ? "-" : "Bugün",
  }));

  return (
    <Panel title="Etkinlikler">
      <div className={`mb-5 rounded-[1.8rem] border p-5 ${
        isDark
          ? "border-yellow-500/20 bg-gradient-to-br from-yellow-500/12 via-white/[0.03] to-black text-white"
          : "border-yellow-200 bg-gradient-to-br from-yellow-50 via-white to-amber-50 text-slate-900 shadow-sm"
      }`}>
        <div className={`text-xs font-bold uppercase tracking-[0.35em] ${isDark ? "text-yellow-300" : "text-yellow-700"}`}>
          Yayıncı Etkinlik Merkezi
        </div>
        <div className={`mt-3 text-3xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
          Yayıncılarına özel etkinlik ve bildirim planla.
        </div>
        <p className={`mt-2 max-w-3xl text-sm leading-7 ${isDark ? "text-white/55" : "text-slate-600"}`}>
          Bu alanda yarışma, kampanya, çekiliş ve özel duyurular oluşturabilir; bildirimi kimlerin gördüğünü raporlayabilirsin.
        </p>
      </div>

      <div className="grid gap-4 xl:grid-cols-4">
        <InfoBox title="Aktif Etkinlik" value="3" note="Yayıncı panelinde görünen" />
        <InfoBox title="Planlı Bildirim" value="7" note="İleri tarihli gönderim" />
        <InfoBox title="Görülme Oranı" value="67%" note="Bildirim görüntüleme oranı" />
        <InfoBox title="Katılım" value={`2/${publishers.length || 0}`} note="Katılacak yayıncı sayısı" />
      </div>

      <div className="mt-6 grid gap-5 xl:grid-cols-[1fr_.75fr]">
        <div className={`rounded-[1.8rem] border p-5 transition-colors ${
          isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
        }`}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>Etkinlik Listesi</div>
              <div className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>Yarışma, kampanya ve duyuru planları</div>
            </div>
            <button className="rounded-2xl bg-yellow-500 px-5 py-3 font-bold text-black hover:bg-yellow-400 shadow-md">
              + Etkinlik Ekle
            </button>
          </div>
          <div className="space-y-3">
            {eventList.map((item) => (
              <div
                key={item.id}
                className={`rounded-2xl border p-4 transition ${
                  isDark ? "border-white/10 bg-white/[0.025]" : "border-slate-200 bg-slate-50 shadow-sm"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className={`text-base font-black ${isDark ? "text-white" : "text-slate-900"}`}>{item.title}</div>
                    <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/55" : "text-slate-600"}`}>{item.desc}</p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs font-bold ${
                    isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
                  }`}>
                    {item.status}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-3">
                  <MiniInfo label="Tür" value={item.type} />
                  <MiniInfo label="Hedef" value={item.target} />
                  <MiniInfo label="Tarih" value={item.date} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`rounded-[1.8rem] border p-5 transition-colors ${
          isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
        }`}>
          <div className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>Yeni Etkinlik / Bildirim</div>
          <div className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>Yayıncı panelinde gösterilecek etkinlik planı</div>
          <div className="mt-5 space-y-3">
            <input placeholder="Etkinlik başlığı" className="form-input" />
            <select className="form-input">
              <option>Yarışma</option>
              <option>Kampanya</option>
              <option>Çekiliş</option>
              <option>Duyuru</option>
            </select>
            <select className="form-input">
              <option>Tüm Yayıncılar</option>
              <option>Aktif Yayıncılar</option>
              <option>Kademe 5 ve üzeri</option>
              <option>Son 3 gün yayın açmayanlar</option>
            </select>
            <input type="date" className="form-input" />
            <textarea placeholder="Etkinlik açıklaması veya bildirim metni..." className="form-textarea h-32" />
            <button className="w-full rounded-2xl bg-yellow-500 px-5 py-3 font-bold text-black hover:bg-yellow-400 shadow-md">
              Etkinliği Yayınla
            </button>
          </div>
        </div>
      </div>

      <WatchReport title="Bildirim Görülme Raporu" rows={seenReport} mode="event" />
    </Panel>
  );
}

function ReportsSection({ publishers, performanceData, reportStartDate, reportEndDate }) {
  const { isDark } = useTheme();
  const summary = performanceData?.summary || {};

  const totalDiamonds = safeNumber(summary.totalDiamonds);
  const totalLiveCount = safeNumber(summary.totalLiveCount);
  const totalLiveSeconds = safeNumber(summary.totalLiveSeconds);

  const tierMap = publishers.reduce((acc, publisher) => {
    const tier = getTier(publisher.currentDiamonds);

    if (!acc[tier]) {
      acc[tier] = {
        tier,
        publisherCount: 0,
        diamonds: 0,
        liveHours: 0,
        liveCount: 0,
      };
    }

    acc[tier].publisherCount += 1;
    acc[tier].diamonds += safeNumber(publisher.currentDiamonds);
    acc[tier].liveHours += safeNumber(publisher.currentHours);
    acc[tier].liveCount += safeNumber(publisher.currentPublishCount);

    return acc;
  }, {});

  const tierRows = Object.values(tierMap).sort(
    (a, b) => safeNumber(b.diamonds) - safeNumber(a.diamonds)
  );

  return (
    <div className="space-y-6">
      <Panel title="Raporlar">
        <div className="mb-5 grid gap-4 xl:grid-cols-4">
          <InfoBox
            title="Toplam Yayıncı"
            value={publishers.length}
            note="Yöneticiye bağlı yayıncı"
          />

          <InfoBox
            title="Toplam Elmas"
            value={formatNumber(totalDiamonds)}
            note={`${formatDateForDisplay(reportStartDate)} - ${formatDateForDisplay(reportEndDate)}`}
          />

          <InfoBox
            title="Toplam Yayın"
            value={formatNumber(totalLiveCount)}
            note="Seçili dönem yayın adedi"
          />

          <InfoBox
            title="Toplam Süre"
            value={formatDurationFromSeconds(totalLiveSeconds)}
            note="Seçili dönem yayın süresi"
          />
        </div>

        <div className={`rounded-[1.8rem] border p-5 transition-colors ${
          isDark
            ? "border-yellow-500/20 bg-black/40 shadow-[0_20px_80px_rgba(0,0,0,.45)]"
            : "border-slate-200/90 bg-white shadow-sm"
        }`}>
          <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>Rapor Merkezi</div>
              <div className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>
                Bu alan performans verilerinden beslenir.
              </div>
            </div>

            <div className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] ${
              isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
            }`}>
              MySQL Live
            </div>
          </div>

          <div className={`overflow-hidden rounded-[1.5rem] border ${
            isDark ? "border-white/10 bg-black/30" : "border-slate-200 bg-slate-50"
          }`}>
            <div className={`grid grid-cols-[1.2fr_.8fr_.8fr_.8fr_.8fr] border-b px-5 py-4 text-xs font-bold uppercase tracking-[0.18em] ${
              isDark ? "border-white/10 bg-white/[0.03] text-white/40" : "border-slate-200 bg-slate-100 text-slate-600"
            }`}>
              <div>Yayıncı</div>
              <div>Kademe</div>
              <div>Yayın</div>
              <div>Süre</div>
              <div>Elmas</div>
            </div>

            {publishers.map((publisher) => (
              <div
                key={publisher.id}
                className={`grid grid-cols-[1.2fr_.8fr_.8fr_.8fr_.8fr] items-center border-b px-5 py-4 text-sm last:border-b-0 transition ${
                  isDark ? "border-white/5 hover:bg-yellow-500/[0.03]" : "border-slate-100 hover:bg-yellow-50/40 text-slate-800"
                }`}
              >
                <div>
                  <div className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{publisher.fullName}</div>
                  <div className={`text-xs ${isDark ? "text-white/40" : "text-slate-500"}`}>Hidden ID: {publisher.hiddenId}</div>
                </div>
                <div className={`font-bold ${isDark ? "text-yellow-300" : "text-yellow-700"}`}>{getTier(publisher.currentDiamonds)}</div>
                <div className={isDark ? "text-white/70" : "text-slate-700 font-semibold"}>{formatNumber(publisher.currentPublishCount)}</div>
                <div className={isDark ? "text-white/70" : "text-slate-700 font-semibold"}>{formatNumber(publisher.currentHours)} saat</div>
                <div className={`font-black ${isDark ? "text-yellow-300" : "text-yellow-700"}`}>
                  {formatNumber(publisher.currentDiamonds)}
                </div>
              </div>
            ))}

            {publishers.length === 0 && (
              <div className={`px-5 py-5 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>
                Yayıncı verisi bulunmuyor.
              </div>
            )}
          </div>
        </div>
      </Panel>

      <Panel title="Kademe Kazanım Sıralaması">
        <div className={`mb-4 rounded-2xl border p-4 text-sm leading-6 ${
          isDark
            ? "border-yellow-500/20 bg-yellow-500/10 text-yellow-100"
            : "border-yellow-200 bg-yellow-50/80 text-yellow-900 font-semibold shadow-sm"
        }`}>
          Bu tablo performans verilerine göre kademe bazlı yayıncı sayısı, yayın süresi ve toplam elmas dağılımını gösterir.
        </div>

        <div className={`overflow-hidden rounded-[1.6rem] border ${
          isDark ? "border-white/10 bg-black/25" : "border-slate-200 bg-white shadow-sm"
        }`}>
          <div className={`grid grid-cols-[1fr_.7fr_.8fr_.8fr_1fr] border-b px-5 py-4 text-xs font-bold uppercase tracking-[0.18em] ${
            isDark ? "border-white/10 bg-white/[0.035] text-white/45" : "border-slate-200 bg-slate-100 text-slate-600"
          }`}>
            <div>Kademe</div>
            <div>Yayıncı</div>
            <div>Yayın</div>
            <div>Süre</div>
            <div>Elmas</div>
          </div>

          {tierRows.map((row, index) => (
            <div
              key={row.tier}
              className={`grid grid-cols-[1fr_.7fr_.8fr_.8fr_1fr] items-center border-b px-5 py-4 text-sm last:border-b-0 transition ${
                isDark ? "border-white/5 hover:bg-yellow-500/[0.035]" : "border-slate-100 hover:bg-yellow-50/40 text-slate-800"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold ${
                  isDark ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-yellow-400 bg-yellow-100 text-yellow-900"
                }`}>
                  {index + 1}
                </span>
                <span className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{row.tier}</span>
              </div>

              <div className={`font-bold ${isDark ? "text-white/75" : "text-slate-700"}`}>{row.publisherCount}</div>
              <div className={`font-semibold ${isDark ? "text-white/70" : "text-slate-700"}`}>{formatNumber(row.liveCount)}</div>
              <div className={`font-semibold ${isDark ? "text-white/70" : "text-slate-700"}`}>{formatNumber(row.liveHours)} saat</div>
              <div className={`font-black ${isDark ? "text-yellow-300" : "text-yellow-700"}`}>{formatNumber(row.diamonds)}</div>
            </div>
          ))}

          {tierRows.length === 0 && (
            <div className={`px-5 py-5 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>
              Kademe verisi bulunmuyor.
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

function WatchReport({ title, rows, mode = "academy" }) {
  const { isDark } = useTheme();
  return (
    <div className={`mt-6 rounded-[1.8rem] border p-5 ${
      isDark
        ? "border-yellow-500/20 bg-gradient-to-br from-yellow-500/10 via-white/[0.02] to-black"
        : "border-slate-200/90 bg-white shadow-sm"
    }`}>
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className={`text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>{title}</div>
          <div className={`mt-1 text-sm ${isDark ? "text-white/45" : "text-slate-500"}`}>
            Kim gördü / izledi, kim görmedi ve son işlem zamanı takip edilir.
          </div>
        </div>
        <button className={`rounded-2xl border px-5 py-3 text-sm font-bold transition ${
          isDark
            ? "border-yellow-500/30 bg-yellow-500/10 text-yellow-300 hover:bg-yellow-500 hover:text-black"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 shadow-sm"
        }`}>
          Raporu Dışa Aktar
        </button>
      </div>
      <div className={`overflow-hidden rounded-[1.5rem] border ${
        isDark ? "border-white/10 bg-black/30" : "border-slate-200 bg-slate-50"
      }`}>
        <div className={`grid grid-cols-[1.2fr_.8fr_.8fr_.8fr] border-b px-5 py-4 text-xs font-bold uppercase tracking-[0.18em] ${
          isDark ? "border-white/10 bg-white/[0.03] text-white/40" : "border-slate-200 bg-slate-100 text-slate-600"
        }`}>
          <div>Yayıncı</div>
          <div>Durum</div>
          <div>{mode === "academy" ? "İlerleme" : "Katılım"}</div>
          <div>Son İşlem</div>
        </div>
        {rows.map((user, index) => (
          <div
            key={index}
            className={`grid grid-cols-[1.2fr_.8fr_.8fr_.8fr] items-center border-b px-5 py-4 text-sm last:border-b-0 transition ${
              isDark ? "border-white/5 hover:bg-yellow-500/[0.03]" : "border-slate-100 hover:bg-yellow-50/40 text-slate-800"
            }`}
          >
            <div className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{user.name}</div>
            <div><StatusBadge status={user.status} /></div>
            <div>{mode === "academy" ? <Progress value={user.progress || 0} /> : <span className={isDark ? "text-white/65" : "text-slate-600"}>{user.action}</span>}</div>
            <div className={isDark ? "text-white/55" : "text-slate-500"}>{user.date}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Progress({ value }) {
  return (
    <div>
      <div className="h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-yellow-400" style={{ width: `${value}%` }} /></div>
      <div className="mt-2 text-xs text-white/45">%{value} tamamlandı</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const color = status === "Tamamlandı" || status === "Gördü" ? "border-green-500/25 bg-green-500/10 text-green-300" : status === "İzliyor" ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300" : "border-red-500/25 bg-red-500/10 text-red-300";
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${color}`}>{status}</span>;
}

function MenuPlaceholder({ activeMenu }) {
  const titles = {
    gifts: "Şifre Talepleri",
  };

  const title = titles[activeMenu] || "Panel Alanı";

  if (activeMenu === "gifts") {
    return (
      <Panel title={title}>
        <div className="min-h-[620px] rounded-[1.8rem] border border-yellow-500/10 bg-black/20" />
      </Panel>
    );
  }

  return (
    <Panel title={title}>
      <div className="grid gap-4 lg:grid-cols-3">
        <InfoBox title="Durum" value="Taslak" note="Bu alan detaylandırılacak." />
        <InfoBox title="Veri Kaynağı" value="API" note="Canlı veriye bağlanacak." />
        <InfoBox title="İşlem" value="Yönetilebilir" note="Ekle, düzenle, sil altyapısı hazırlanacak." />
      </div>

      <div className="mt-5 rounded-[1.2rem] border border-white/10 bg-black/20 p-4 text-sm leading-6 text-white/60">
        Bu menü için önerilen içerik: listeleme alanı, filtreleme, durum etiketi, ekleme/düzenleme butonları ve işlem geçmişi.
      </div>
    </Panel>
  );
}

function InactivePopup({ users }) {
  return (
    <div className="absolute right-0 top-[105%] z-50 w-[430px] rounded-2xl border border-yellow-500/20 bg-[#0b0b0b] p-4 shadow-[0_20px_60px_rgba(0,0,0,.6)]">
      <div className="mb-3 text-sm font-semibold text-white">
        Son 7 Gün Yayın Detayı
      </div>

      <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
        {users.map((user) => (
          <div
            key={user.id}
            className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <PublisherNameWithTier
                  publisher={user}
                  label={user.fullName}
                  nameClassName="text-sm font-bold"
                />
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-white/45">
                  <span>@{user.username} • Hidden ID: {user.hiddenId}</span>
                  <PublisherTierBadge publisher={user} compact />
                </div>
              </div>

              <div className="rounded-full border border-yellow-500/25 bg-yellow-500/10 px-3 py-1 text-xs font-semibold text-yellow-300">
                {safeNumber(user.lastSevenActiveDays)}/{safeNumber(user.lastSevenTotalDays || 7)}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {(user.lastSevenDays || []).map((day) => (
                <span
                  key={`${user.id}-${day.report_date}`}
                  className={`rounded-full border px-2 py-1 text-[10px] font-semibold ${
                    day.opened
                      ? "border-green-500/25 bg-green-500/10 text-green-300"
                      : "border-white/10 bg-black/30 text-white/35"
                  }`}
                  title={`${formatDateForDisplay(day.report_date)} • ${safeNumber(day.liveCount)} yayın`}
                >
                  {formatDateForDisplay(day.report_date).slice(0, 5)}
                </span>
              ))}

              {(user.lastSevenDays || []).length === 0 && (
                <span className="text-xs text-white/35">Son 7 gün verisi yok.</span>
              )}
            </div>
          </div>
        ))}

        {users.length === 0 && (
          <div className="text-sm text-white/45">Yayıncı bulunmuyor.</div>
        )}
      </div>
    </div>
  );
}

function SidebarItem({ item, active, open, onClick }) {
  const { isDark } = useTheme();
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex w-full items-center justify-between rounded-[1.2rem] px-3.5 py-3 text-left transition-all duration-200 ${
        active
          ? isDark
            ? "border border-yellow-500/30 bg-gradient-to-r from-yellow-500/25 via-yellow-500/10 to-transparent font-bold text-white shadow-sm"
            : "bg-yellow-500 text-black font-black shadow-md border-transparent"
          : isDark
          ? "border border-transparent text-white/78 hover:border-white/10 hover:bg-white/[0.035] hover:text-white"
          : "border border-slate-200/80 bg-white text-slate-800 hover:bg-slate-50 hover:text-slate-900 shadow-sm"
      }`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="min-w-0 flex-1 pr-2">
          <div className="flex items-center gap-2.5">
            <span className={`truncate text-[14px] font-bold tracking-tight ${
              active ? (isDark ? "text-white" : "text-black") : (isDark ? "text-white/85" : "text-slate-800")
            }`}>
              {item.label}
            </span>
            {item.count > 0 && (
              <span className={`rounded-full border px-2.5 py-[2px] text-[12px] font-bold ${
                active
                  ? isDark ? "border-white/20 bg-white/10 text-white" : "bg-black/15 border-transparent text-black"
                  : isDark ? "border-white/15 bg-white/[0.06] text-white/80" : "border-slate-200 bg-slate-100 text-slate-700"
              }`}>
                {item.count}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {item.badge && (
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
            active
              ? isDark ? "bg-yellow-500 text-black" : "bg-black text-white"
              : isDark
              ? "border border-yellow-500/25 bg-yellow-500/10 text-yellow-300"
              : "border border-amber-300 bg-amber-50 text-amber-800"
          }`}>
            {item.badge}
          </span>
        )}
        {item.children?.length && (
          <span className={`text-xl ${active && !isDark ? "text-black" : isDark ? "text-white/65" : "text-slate-400"}`}>
            {open ? "⌃" : "⌄"}
          </span>
        )}
      </div>
    </button>
  );
}

function Badge({ text }) {
  const { isDark } = useTheme();
  return (
    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
      isDark ? "border-white/10 bg-white/[0.03] text-white/60" : "border-slate-200 bg-slate-100 text-slate-700"
    }`}>
      {text}
    </span>
  );
}

function Panel({ title, children }) {
  const { isDark } = useTheme();
  return (
    <div className={`rounded-[2rem] border p-6 transition-colors ${
      isDark
        ? "border-white/10 bg-white/[0.03] shadow-[0_10px_35px_rgba(0,0,0,.22)]"
        : "border-slate-200/90 bg-white text-slate-900 shadow-[0_4px_25px_rgba(0,0,0,0.03)]"
    }`}>
      <h2 className={`mb-4 text-2xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>{title}</h2>
      {children}
    </div>
  );
}

function MetricCard({ title, value, note, topValue, dropdown, onDropdownClick, children }) {
  const { isDark } = useTheme();
  return (
    <div className={`relative min-h-[145px] rounded-[1.6rem] border p-5 transition-colors ${
      isDark
        ? "border-white/10 bg-white/[0.035] shadow-[0_10px_35px_rgba(0,0,0,.22)]"
        : "border-slate-200/90 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.03)]"
    }`}>
      <div className="flex items-center justify-between gap-3">
        <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? "text-yellow-300" : "text-slate-500"}`}>
          {title}
        </span>
        {!isDark && (
          <span className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
            Aktif
          </span>
        )}
      </div>
      {topValue && (
        <div className={`mt-4 text-lg font-bold ${isDark ? "text-white/70" : "text-slate-600"}`}>
          {topValue}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <div className={`text-3xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
          {value}
        </div>
        {dropdown && (
          <button
            type="button"
            onClick={onDropdownClick}
            className={`text-xl transition ${isDark ? "text-white/60 hover:text-yellow-300" : "text-slate-400 hover:text-yellow-600"}`}
          >
            ▾
          </button>
        )}
      </div>
      {note && (
        <div className={`mt-2.5 text-xs font-medium ${isDark ? "text-white/42" : "text-slate-500"}`}>
          {note}
        </div>
      )}
      {children}
    </div>
  );
}

function PasswordRequestsSection({
  currentEmployee,
  publishers,
  requests,
  loading,
  reloadRequests,
  reloadMessages,
}) {
  const { isDark } = useTheme();
  const [passwordValues, setPasswordValues] = useState({});
  const [processingId, setProcessingId] = useState(null);

  function findPublisherForRequest(request) {
    const requestUsername = String(request.username || "").toLowerCase().trim();
    const requestPhone = String(request.phone || "").replace(/\D/g, "");

    return (
      publishers.find((publisher) => {
        const publisherUsername = String(publisher.username || "").toLowerCase().trim();
        const publisherPhone = String(publisher.phone || "").replace(/\D/g, "");

        return (
          requestUsername === publisherUsername ||
          (requestPhone && publisherPhone && requestPhone === publisherPhone)
        );
      }) || null
    );
  }

  function formatRequestDate(value) {
    if (!value) return "-";

    try {
      return new Date(String(value).replace(" ", "T")).toLocaleString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  }

  async function handleCompleteRequest(request) {
    const publisher = findPublisherForRequest(request);
    const newPassword = String(passwordValues[request.id] || "").trim();

    if (!publisher?.id) {
      alert("Bu talep bağlı yayıncılar arasında eşleşmedi.");
      return;
    }

    if (!newPassword) {
      alert("Yeni şifre alanı zorunlu.");
      return;
    }

    try {
      setProcessingId(request.id);

      const passwordResponse = await fetch(API_PASSWORD_UPDATE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: publisher.id,
          password: newPassword,
        }),
      });

      const passwordData = await passwordResponse.json();

      if (!passwordData.success) {
        alert(passwordData.message || "Şifre güncellenemedi.");
        return;
      }

      const messageResponse = await fetch(API_MESSAGE_SEND, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sender_id: currentEmployee.id,
          sender_role: "employee",
          receiver_id: publisher.id,
          subject: "Şifre Güncelleme",
          message: `Merhaba ${publisher.fullName}, güncel şifreniz: ${newPassword}

Şifreniz yöneticiniz tarafından güncellenmiştir. SMS bilgilendirmesi ayrıca özel süreç üzerinden iletilecektir.`,
        }),
      });

      const messageData = await messageResponse.json();

      if (!messageData.success) {
        alert(messageData.message || "Şifre güncellendi ancak mesaj gönderilemedi.");
        return;
      }

      await fetch(API_PASSWORD_RESET_STATUS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: request.id,
          status: "Tamamlandı",
          note: "Şifre güncellendi ve yayıncı mesaj alanına iletildi.",
        }),
      });

      alert("Şifre güncellendi ve yayıncı mesaj alanına iletildi.");

      setPasswordValues((prev) => ({
        ...prev,
        [request.id]: "",
      }));

      await reloadRequests();
      await reloadMessages();
    } catch (err) {
      console.error("Şifre talebi tamamlanamadı:", err);
      alert("İşlem sırasında bağlantı hatası oluştu.");
    } finally {
      setProcessingId(null);
    }
  }

  return (
    <Panel title="Şifre Talepleri">
      <div className={`mb-5 rounded-2xl border p-4 text-sm leading-6 ${
        isDark
          ? "border-yellow-500/20 bg-yellow-500/10 text-yellow-100"
          : "border-yellow-200 bg-yellow-50/80 text-yellow-900 font-semibold shadow-sm"
      }`}>
        Yayıncıların şifre yenileme talepleri bu alanda görüntülenir. Yeni şifre kaydedildiğinde yayıncının mesaj alanına otomatik bilgilendirme gider.
      </div>

      {loading && (
        <div className={`rounded-2xl border p-5 text-sm ${
          isDark ? "border-white/10 bg-black/25 text-white/45" : "border-slate-200 bg-white text-slate-500"
        }`}>
          Şifre talepleri yükleniyor...
        </div>
      )}

      {!loading && requests.length === 0 && (
        <div className={`rounded-2xl border p-5 text-sm ${
          isDark ? "border-white/10 bg-black/25 text-white/45" : "border-slate-200 bg-white text-slate-500"
        }`}>
          Bağlı yayıncılarınıza ait bekleyen şifre talebi bulunmuyor.
        </div>
      )}

      <div className="space-y-4">
        {requests.map((request) => {
          const publisher = findPublisherForRequest(request);
          const isDone = request.status === "Tamamlandı";

          return (
            <div
              key={request.id}
              className={`rounded-[1.4rem] border p-5 transition ${
                isDark ? "border-white/10 bg-black/25" : "border-slate-200/90 bg-white shadow-sm"
              }`}
            >
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div>
                  <div className={`text-xs font-bold uppercase tracking-[0.24em] ${
                    isDark ? "text-yellow-300" : "text-yellow-600"
                  }`}>
                    Şifre Talebi
                  </div>

                  <div className={`mt-2 text-xl font-black ${isDark ? "text-white" : "text-slate-900"}`}>
                    {publisher?.fullName || request.username}
                  </div>

                  <div className={`mt-2 text-sm leading-6 ${isDark ? "text-white/50" : "text-slate-600"}`}>
                    Kullanıcı adı: <span className={`font-bold ${isDark ? "text-white/80" : "text-slate-900"}`}>{request.username}</span>
                    <br />
                    Telefon: <span className={`font-bold ${isDark ? "text-white/80" : "text-slate-900"}`}>{request.phone}</span>
                    <br />
                    Talep tarihi: <span className={`font-bold ${isDark ? "text-white/80" : "text-slate-900"}`}>{formatRequestDate(request.created_at)}</span>
                  </div>
                </div>

                <span
                  className={`w-fit rounded-full border px-4 py-2 text-xs font-bold ${
                    isDone
                      ? "border-green-500/25 bg-green-500/10 text-green-300"
                      : isDark
                      ? "border-yellow-500/25 bg-yellow-500/10 text-yellow-300"
                      : "border-yellow-400 bg-yellow-100 text-yellow-900"
                  }`}
                >
                  {request.status}
                </span>
              </div>

              {!isDone && (
                <div className="mt-5 grid gap-3 xl:grid-cols-[1fr_auto]">
                  <input
                    type="text"
                    value={passwordValues[request.id] || ""}
                    onChange={(event) =>
                      setPasswordValues((prev) => ({
                        ...prev,
                        [request.id]: event.target.value,
                      }))
                    }
                    placeholder="Yayıncı için yeni şifre gir"
                    className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none ${
                      isDark
                        ? "border-yellow-500/20 bg-black/45 text-white placeholder:text-white/35 focus:border-yellow-400"
                        : "border-slate-300 bg-slate-50 text-slate-900 placeholder:text-slate-400 focus:border-yellow-500"
                    }`}
                  />

                  <button
                    type="button"
                    disabled={processingId === request.id}
                    onClick={() => handleCompleteRequest(request)}
                    className="rounded-2xl bg-yellow-500 px-6 py-3 text-sm font-bold text-black transition hover:bg-yellow-400 disabled:opacity-60 shadow-md"
                  >
                    {processingId === request.id
                      ? "İşleniyor..."
                      : "Şifreyi Güncelle ve Mesaj Gönder"}
                  </button>
                </div>
              )}

              {isDone && (
                <div className="mt-4 rounded-2xl border border-green-500/20 bg-green-500/10 p-4 text-sm font-semibold text-green-700">
                  Bu talep tamamlandı. Güncel şifre yayıncı mesaj alanına iletilmiştir.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}